<template>
  <div>
    <!-- Enhanced Page Header -->
    <PageHeader
      show-back-button
      use-history-back
      :title="t('reports.title')"
      :description="t('reports.description')"
      :helper-text="t('reports.helperText')"
      :icon-path="getIconPath('report')"
    >
      <template #actions>
        <div class="flex items-center space-x-2">
          <Button
            @click="loadAllData"
            variant="outline"
            size="sm"
            :aria-label="t('common.refresh')"
            :disabled="loading"
          >
            <Icon 
              name="refresh"
              size="sm"
              class="mr-2"
              :class="{ 'animate-spin': loading }"
              aria-hidden="true"
            />
            {{ t('common.refresh') }}
          </Button>
          <Button
            @click="exportAllData"
            variant="primary"
            size="sm"
            :aria-label="t('reports.exportAll')"
          >
            <Icon 
              name="download"
              size="sm"
              class="mr-2"
              aria-hidden="true"
            />
            {{ t('reports.exportAll') }}
          </Button>
        </div>
      </template>
    </PageHeader>

    <!-- Loading State -->
    <Loader 
      v-if="loading" 
      variant="spinner" 
      size="lg" 
      :text="t('reports.chart.loading')" 
      center 
      class="py-12"
    />

    <!-- Content -->
    <div v-else class="space-y-6 p-6">
      <!-- Tabs Navigation -->
      <div 
        ref="tabsContainer"
        :class="[
          'transition-all duration-200',
          isTabsStuck ? 'fixed top-0 left-0 right-0 bg-white dark:bg-gray-900 shadow-md pb-0 px-5 z-50 !mt-0' : ''
        ]"
      >
        <HLTabs 
          :value="activeTab" 
          @update:value="activeTab = $event"
          :animated="false" 
          size="lg"
          :aria-label="t('reports.tabs.ariaLabel')"
          :maxVisibleTabs="reportTabs.length"
        >
          <HLTab v-for="tabItem in reportTabs" :key="tabItem.id" :name="tabItem.id">
            <HLIcon size="18" class="mr-1.5">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" shape-rendering="geometricPrecision" :style="{ color: tabItem.iconColorHex }" aria-hidden="true">
                <path :d="tabItem.svgPath" />
              </svg>
            </HLIcon>
            {{ tabItem.label }}
          </HLTab>
        </HLTabs>
    </div>

      <!-- Spacer when tabs are stuck (height of tabs + padding) -->
      <div v-if="isTabsStuck" :style="{ height: tabsContainer?.offsetHeight + 'px' }"></div>
      
      <!-- Tab Content -->
                  <div>
        <!-- Overview Tab Content -->
        <div v-show="activeTab === REPORT_TABS.OVERVIEW" class="space-y-6">
          <div>
            <!-- Stats Grid -->
            <StatsGrid :stats="overviewStats" :columns="{ base: 1, md: 2, lg: 3, xl: 6 }" />
            
            <!-- Quality Metrics -->
        <div>
                <div class="mb-0">
                  <div class="flex items-center gap-2">
                  <h6 class="text-base font-semibold text-gray-900 dark:text-gray-100">
                    {{ t('reports.overview.qualityMetrics') }}
                  </h6>
                  
                  <!-- Info Icon with Popover -->
                  <HLPopover
                    trigger="hover"
                    placement="bottom"
                    :show-arrow="true"
                    raw
                  >
                    <template #trigger>
                    <Icon 
                      name="info"
                        size="sm"
                        class="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors flex-shrink-0"
                      :aria-label="t('reports.overview.metricsInfo.ariaLabel')"
                    />
                    </template>
                    <template #default>
                      <div class="text-sm max-w-2xl p-4 whitespace-pre-line leading-relaxed bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
                        {{ t('reports.overview.metricsInfo.tooltipContent') }}
                      </div>
                    </template>
                  </HLPopover>
                    </div>
                  </div>
              <StatsGrid :stats="qualityMetricsStats" :columns="{ base: 1, md: 2, lg: 4 }" />
              </div>

            <!-- Activity Timeline -->
            <Section class="mt-8">
              <StackedAreaChart
                :data="systemActivityTimelineData"
                :height="350"
                :title="t('reports.overview.activityTimeline')"
              />
            </Section>
                    </div>
          </div>

        <!-- Teams & Projects Tab Content -->
        <div v-show="activeTab === REPORT_TABS.TEAMS" class="space-y-6">
          <div class="space-y-4">
            <StatsGrid :stats="teamsAndProjectsStats" :columns="{ base: 1, sm: 2, md: 2, lg: 4 }" />
            
            <!-- Teams & Projects Filter -->
            <SectionFilter
              :title="t('reports.sections.teamsAndProjects')"
              :expanded="sectionFilters.teamsAndProjects.expanded"
              :search="sectionFilters.teamsAndProjects.search"
              :filtered-count="filteredTeamsAndProjects.teams.length + filteredTeamsAndProjects.projects.length"
              :total-count="filteredData.teams.length + filteredData.projects.length"
              :active-filter-count="teamsAndProjectsActiveFilters"
              :has-active-filters="teamsAndProjectsActiveFilters > 0"
              :show-search="false"
              show-deleted-toggle
              :include-deleted="sectionFilters.teamsAndProjects.includeDeleted"
              :custom-result-message="teamsAndProjectsResultMessage"
              @toggle="toggleSectionFilter('teamsAndProjects')"
              @clear="clearSectionFilter('teamsAndProjects')"
              @update:search="sectionFilters.teamsAndProjects.search = $event"
              @update:include-deleted="sectionFilters.teamsAndProjects.includeDeleted = $event"
              @exportSectionReport="exportSectionReport('teams')"
            >
              <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.labels.selectTeams') }}</Text>
                <HLSelect
                  :id="`filter-teams-${uniqueFilterId}`"
                  v-model:value="sectionFilters.teamsAndProjects.selectedTeams"
                  :options="teamSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allTeams')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
                />
              </div>
              <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.labels.selectProjects') }}</Text>
                <HLSelect
                  :id="`filter-projects-${uniqueFilterId}`"
                  v-model:value="sectionFilters.teamsAndProjects.selectedProjects"
                  :options="teamsAndProjectsProjectSelectOptionsComputed"
                  :placeholder="t('reports.filter.placeholders.allProjects')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
          />
        </div>
            </SectionFilter>
        
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <!-- Teams Timeline Chart -->
              <Section>
                <LineChart
                  :data="teamsTimelineData"
                  :height="300"
                  dataKey="count"
                  :label="t('reports.chart.teamsLabel')"
                  :title="t('reports.chart.teamsAddedOverTime')"
                />
              </Section>

              <!-- Projects Timeline -->
              <Section>
                <LineChart
                  :data="projectsTimelineData"
                  :height="300"
                  dataKey="count"
                  :label="t('reports.chart.projectsLabel')"
                  :title="t('reports.chart.projectsCreatedOverTime')"
                />
              </Section>
        </div>

            <!-- Teams Breakdown Table -->
            <Section overflow>
              <h5 class="text-lg font-semibold text-gray-900 dark:text-white m-0">{{ t('reports.chart.teamsBreakdown') }}</h5>
              <div class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800">
                <div class="p-6">
                  <HLDataTableWrapper responsive-column-width max-width="100%">
                    <HLDataTable
                      id="teams-breakdown-table"
                      :columns="teamsBreakdownTableColumns"
                :data="paginatedTeamsTableData"
                      row-key="id"
                      :column-hover="false"
                      :horizontal-borders="true"
                      class="reports-data-table"
                    >
                      <!-- Empty State -->
                      <template #empty>
                        <div class="flex items-center justify-center w-full min-h-[300px]">
                          <div class="text-center max-w-md mx-auto px-4">
                            <Icon name="users" size="xxl" class="mx-auto text-gray-400 mb-4" />
                            <Heading level="h5" class="my-2 text-gray-900 dark:text-white">
                              {{ t('reports.noTeamsFound') }}
                            </Heading>
                          </div>
                        </div>
                      </template>
                    </HLDataTable>
                  </HLDataTableWrapper>
                </div>
                
                <!-- Pagination inside table border -->
                <div class="border-t border-gray-200 dark:border-gray-700 px-6 py-4" v-if="teamsTableData.length > pagination.teamsTable.pageSize">
                  <div class="flex justify-end">
                    <HLPagination
                      :current-page="pagination.teamsTable.currentPage"
                      :total-pages="Math.ceil(teamsTableData.length / pagination.teamsTable.pageSize)"
                      :show-per-page-drop-down="true"
                      :pages-to-display="7"
                      @update:page="pagination.teamsTable.currentPage = $event"
                      size="md"
                      show-current-range
                      :show-total-pages="true"
                    >
                      <template #prev> Previous </template>
                      <template #next> Next </template>
                    </HLPagination>
                  </div>
                </div>
              </div>
            </Section>
            
            <!-- Projects Breakdown Table -->
            <Section overflow>
              <h5 class="text-lg font-semibold text-gray-900 dark:text-white m-0">{{ t('reports.chart.projectsBreakdown') }}</h5>
              <div class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800">
                <div class="p-6">
                  <HLDataTableWrapper responsive-column-width max-width="100%">
                    <HLDataTable
                      id="projects-breakdown-table"
                      :columns="projectsBreakdownTableColumns"
                :data="paginatedProjectsTableData"
                      row-key="id"
                      :column-hover="false"
                      :horizontal-borders="true"
                      class="reports-data-table"
                    >
                      <!-- Empty State -->
                      <template #empty>
                        <div class="flex items-center justify-center w-full min-h-[300px]">
                          <div class="text-center max-w-md mx-auto px-4">
                            <Icon name="folder" size="xxl" class="mx-auto text-gray-400 mb-4" />
                            <Heading level="h5" class="my-2 text-gray-900 dark:text-white">
                              {{ t('reports.noProjectsFound') }}
                            </Heading>
                          </div>
                        </div>
                </template>
                    </HLDataTable>
                  </HLDataTableWrapper>
                </div>
                
                <!-- Pagination inside table border -->
                <div class="border-t border-gray-200 dark:border-gray-700 px-6 py-4" v-if="projectsTableData.length > pagination.projectsTable.pageSize">
                  <div class="flex justify-end">
                    <HLPagination
                      :current-page="pagination.projectsTable.currentPage"
                      :total-pages="Math.ceil(projectsTableData.length / pagination.projectsTable.pageSize)"
                      :show-per-page-drop-down="true"
                      :pages-to-display="7"
                      @update:page="pagination.projectsTable.currentPage = $event"
                      size="md"
                      show-current-range
                      :show-total-pages="true"
                    >
                      <template #prev> Previous </template>
                      <template #next> Next </template>
                    </HLPagination>
                  </div>
                </div>
              </div>
            </Section>
                </div>
              </div>
              
        <!-- Features Tab Content -->
        <div v-show="activeTab === REPORT_TABS.FEATURES" class="space-y-6">
          <div class="space-y-4">
            <StatsGrid :stats="featuresStats" :columns="{ base: 1, sm: 2, md: 3, lg: 6 }" />

            <SectionFilter
              :title="t('reports.sections.features')"
          :expanded="sectionFilters.features.expanded"
              :show-search="false"
          :filtered-count="filteredFeatures.length"
          :total-count="mainFeaturesCount"
          :active-filter-count="featuresActiveFilters"
          :has-active-filters="featuresActiveFilters > 0"
          show-deleted-toggle
          :include-deleted="sectionFilters.features.includeDeleted"
          @toggle="toggleSectionFilter('features')"
          @clear="clearSectionFilter('features')"
          @update:include-deleted="sectionFilters.features.includeDeleted = $event"
          @export="exportSectionReport('features')"
        >
        <!-- Search Bar -->
        <div class="col-span-full mb-4">
          <HLInput
            v-model="sectionFilters.features.search"
            :placeholder="t('reports.filter.searchPlaceholder', { section: t('reports.sections.features').toLowerCase() })"
            clearable
            class="w-full"
          >
            <template #prefix>
              <Icon name="search" size="sm" class="text-gray-400" />
            </template>
          </HLInput>
        </div>

        <div>
            <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.teams') }}</Text>
            <HLSelect
              :id="`filter-feature-teams-${uniqueFilterId}`"
              v-model:value="sectionFilters.features.teamIds"
              :options="featureTeamSelectOptions"
              :placeholder="t('reports.filter.placeholders.allTeams')"
              multiple
              filterable
              :option-renderer="optionRenderer"
          />
        </div>
        <div>
            <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.projects') }}</Text>
            <HLSelect
              :id="`filter-feature-projects-${uniqueFilterId}`"
              v-model:value="sectionFilters.features.projectIds"
              :options="featureProjectSelectOptions"
              :placeholder="t('reports.filter.placeholders.allProjects')"
              multiple
              filterable
              :option-renderer="optionRenderer"
          />
        </div>
              <div>
            <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.status') }}</Text>
            <HLSelect
              :id="`filter-feature-status-${uniqueFilterId}`"
              v-model:value="sectionFilters.features.status"
              :options="featureStatusSelectOptions"
              :placeholder="t('reports.filter.placeholders.allStatuses')"
              multiple
              filterable
              :option-renderer="optionRenderer"
            />
      </div>
              <div>
            <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.priority') }}</Text>
            <HLSelect
              :id="`filter-feature-priority-${uniqueFilterId}`"
              v-model:value="sectionFilters.features.priority"
              :options="featurePrioritySelectOptions"
              :placeholder="t('reports.filter.placeholders.allPriorities')"
              multiple
              filterable
              :option-renderer="optionRenderer"
            />
    </div>
              <div>
            <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.labels.developer') }}</Text>
            <HLSelect
              :id="`filter-feature-dev-${uniqueFilterId}`"
              v-model:value="sectionFilters.features.devAssignees"
              :options="featureDevSelectOptions"
              :placeholder="t('reports.filter.placeholders.allDevelopers')"
              multiple
              filterable
              :option-renderer="optionRenderer"
            />
            </div>
          <div>
            <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.labels.qaEngineer') }}</Text>
            <HLSelect
              :id="`filter-feature-qa-${uniqueFilterId}`"
              v-model:value="sectionFilters.features.qaAssignees"
              :options="featureQASelectOptions"
              :placeholder="t('reports.filter.placeholders.allQAEngineers')"
              multiple
              filterable
              :option-renderer="optionRenderer"
            />
            </div>
          <div>
            <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.labels.startDateRange') }}</Text>
            <HLDatePicker
              :id="`filter-features-start-date-${uniqueFilterId}`"
              type="daterange"
              v-model:value="featuresStartDateRangeValue"
              clearable
              :placeholder="t('reports.filter.labels.startDateRange')"
            />
          </div>
          <div>
            <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.labels.dueDateRange') }}</Text>
            <HLDatePicker
              :id="`filter-features-due-date-${uniqueFilterId}`"
              type="daterange"
              v-model:value="featuresDueDateRangeValue"
              clearable
              :placeholder="t('reports.filter.labels.dueDateRange')"
            />
          </div>
        </SectionFilter>
        
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- Features by Status -->
          <Section>
            <BarChart
              :data="featuresStatusData"
              :height="250"
              :title="t('reports.chart.featuresByStatus')"
              @segment-click="handleFeatureStatusClick"
            />
          </Section>

          <!-- Features by Priority -->
          <Section>
            <BarChart
              :data="featuresPriorityData"
              :height="250"
              :title="t('reports.chart.featuresByPriority')"
            />
          </Section>

          <!-- Features Timeline -->
          <Section>
            <AreaChart
              :data="featuresTimelineData"
              :height="250"
              dataKey="count"
              :label="t('reports.chart.featuresLabel')"
              :title="t('reports.chart.timeline')"
            />
          </Section>
        </div>

        <!-- Additional Features Breakdown Charts -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <!-- Features by Team -->
          <Section>
            <DoughnutChart
              :data="featuresTeamData"
              :height="300"
              :title="t('reports.chart.featuresByTeam')"
            />
          </Section>

          <!-- Features by Project -->
          <Section>
            <DoughnutChart
              :data="featuresProjectData"
              :height="300"
              :title="t('reports.chart.featuresByProject')"
            />
          </Section>

          <!-- Features by Developer -->
          <Section>
            <DoughnutChart
              :data="featuresDeveloperData"
              :height="300"
              :title="t('reports.chart.featuresByDeveloper')"
            />
          </Section>

          <!-- Features by QA -->
          <Section>
            <DoughnutChart
              :data="featuresQAData"
              :height="300"
              :title="t('reports.chart.featuresByQA')"
            />
          </Section>
              </div>
              
        <!-- Filtered Results Table -->
        <Section v-if="filteredFeatures.length > 0" overflow class="mt-6">
          <h5 class="text-lg font-semibold text-gray-900 dark:text-white m-0">{{ t('reports.table.filtered', { entity: t('reports.tabs.features'), count: filteredFeatures.length }) }}</h5>
          
          <div class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800">
            <div class="p-6">
              <HLDataTableWrapper responsive-column-width max-width="100%">
                <HLDataTable
                  id="features-filtered-table"
                  :columns="featuresFilteredTableColumns"
                  :data="paginatedFilteredFeatures"
                  row-key="id"
                  :column-hover="false"
                  :horizontal-borders="true"
                  class="reports-data-table"
                />
              </HLDataTableWrapper>
                </div>
            
            <!-- Pagination inside table border -->
            <div class="border-t border-gray-200 dark:border-gray-700 px-6 py-4" v-if="filteredFeatures.length > pagination.features.pageSize">
              <div class="flex justify-end">
                <HLPagination 
                  :current-page="pagination.features.currentPage"
                  :total-pages="Math.ceil(filteredFeatures.length / pagination.features.pageSize)"
                  :show-per-page-drop-down="true"
                  :pages-to-display="7"
                  @update:page="pagination.features.currentPage = $event"
                  size="md"
                  show-current-range
                  :show-total-pages="true"
                >
                  <template #prev>{{ t('common.previous') }}</template>
                  <template #next>{{ t('common.next') }}</template>
                </HLPagination>
              </div>
            </div>
          </div>
        </Section>
              </div>
            </div>

        <!-- Test Cases Tab Content -->
        <div v-show="activeTab === REPORT_TABS.TEST_CASES" class="space-y-6">
          <div class="space-y-4">
            <StatsGrid :stats="testCasesStats" :columns="{ base: 1, sm: 2, md: 2, lg: 4 }" />
            
            <SectionFilter
              :title="t('reports.sections.testCases')"
              :expanded="sectionFilters.testCases.expanded"
              :show-search="false"
              :filtered-count="filteredTestCases.length"
              :total-count="totalTestCasesCount"
              :active-filter-count="testCasesActiveFilters"
              :has-active-filters="testCasesActiveFilters > 0"
              show-deleted-toggle
              :include-deleted="sectionFilters.testCases.includeDeleted"
              @toggle="toggleSectionFilter('testCases')"
              @clear="clearSectionFilter('testCases')"
              @update:include-deleted="sectionFilters.testCases.includeDeleted = $event"
              @export="exportSectionReport('testCases')"
            >
              <!-- Search Bar -->
              <div class="col-span-full mb-4">
                <HLInput
                  v-model="sectionFilters.testCases.search"
                  :placeholder="t('reports.filter.searchPlaceholder', { section: t('reports.sections.testCases').toLowerCase() })"
                  clearable
                  class="w-full"
                >
                  <template #prefix>
                    <Icon name="search" size="sm" class="text-gray-400" />
                  </template>
                </HLInput>
              </div>

              <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.teams') }}</Text>
                <HLSelect
                  :id="`filter-testcase-teams-${uniqueFilterId}`"
                  v-model:value="sectionFilters.testCases.teamIds"
                  :options="testCaseTeamSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allTeams')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
                />
            </div>
              <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.projects') }}</Text>
                <HLSelect
                  :id="`filter-testcase-projects-${uniqueFilterId}`"
                  v-model:value="sectionFilters.testCases.projectIds"
                  :options="testCaseProjectSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allProjects')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
                />
      </div>
              <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.labels.testType') }}</Text>
                <HLSelect
                  :id="`filter-testcase-type-${uniqueFilterId}`"
                  v-model:value="sectionFilters.testCases.testType"
                  :options="testCaseTypeSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allTestTypes')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
                />
    </div>
              <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.labels.caseType') }}</Text>
                <HLSelect
                  :id="`filter-testcase-casetype-${uniqueFilterId}`"
                  v-model:value="sectionFilters.testCases.caseType"
                  :options="testCaseCaseTypeSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allCaseTypes')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
                />
              </div>
              <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.priority') }}</Text>
                <HLSelect
                  :id="`filter-testcase-priority-${uniqueFilterId}`"
                  v-model:value="sectionFilters.testCases.priority"
                  :options="testCasePrioritySelectOptions"
                  :placeholder="t('reports.filter.placeholders.allPriorities')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
                />
              </div>
              <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.status') }}</Text>
                <HLSelect
                  :id="`filter-testcase-status-${uniqueFilterId}`"
                  v-model:value="sectionFilters.testCases.status"
                  :options="testCaseStatusSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allStatuses')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
                />
              </div>
              <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.labels.automationStatus') }}</Text>
                <HLSelect
                  :id="`filter-testcase-automation-${uniqueFilterId}`"
                  v-model:value="sectionFilters.testCases.automationStatus"
                  :options="testCaseAutomationSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allAutomationStatuses')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
                />
              </div>
              <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.labels.assignee') }}</Text>
                <HLSelect
                  :id="`filter-testcase-assignee-${uniqueFilterId}`"
                  v-model:value="sectionFilters.testCases.assignees"
                  :options="testCaseAssigneeSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allAssignees')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
                />
              </div>
              <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.labels.estimationRange') }}</Text>
                <div class="flex gap-2">
                  <div class="flex-1">
                    <HLInputNumber
                      :id="`filter-testcase-estimation-min-${uniqueFilterId}`"
                      :value="sectionFilters.testCases.estimationRange.min"
                      @update:value="sectionFilters.testCases.estimationRange.min = $event"
                      placeholder="Min"
                      :min="0"
                      clearable
                      class="w-full"
                    />
                  </div>
                  <div class="flex-1">
                    <HLInputNumber
                      :id="`filter-testcase-estimation-max-${uniqueFilterId}`"
                      :value="sectionFilters.testCases.estimationRange.max"
                      @update:value="sectionFilters.testCases.estimationRange.max = $event"
                      placeholder="Max"
                      :min="0"
                      clearable
                      class="w-full"
                    />
                  </div>
                </div>
              </div>
            </SectionFilter>

        <!-- Row 1: Coverage, Case Type, Test Type -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <Section>
            <DoughnutChart
              :data="testCasesAutomationData"
              :height="250"
              :title="t('reports.chart.coverage')"
            />
          </Section>

          <Section>
            <DoughnutChart
              :data="testCasesCaseTypeData"
              :height="250"
              :title="t('reports.chart.testCasesByCaseType')"
            />
          </Section>

          <Section>
            <DoughnutChart
              :data="testCasesTypeData"
              :height="250"
              :title="t('reports.chart.testCasesByType')"
            />
          </Section>
        </div>
        
        <!-- Row 2: Priority, Status, Assignee -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <Section>
            <BarChart
              :data="testCasesPriorityData"
              :height="250"
              :title="t('reports.chart.testCasesByPriority')"
            />
          </Section>
          
          <Section>
            <DoughnutChart
              :data="testCasesStatusData"
              :height="250"
              :title="t('reports.chart.testCasesByStatus')"
            />
          </Section>

          <Section>
            <BarChart
              :data="testCasesAssigneeData"
              :height="250"
              :title="t('reports.chart.testCasesByAssignee')"
            />
          </Section>
    </div>

        <!-- Row 3: Team, Project, Estimation Range -->
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <Section>
            <BarChart
              :data="testCasesTeamData"
              :height="250"
              :title="t('reports.chart.testCasesByTeam')"
            />
          </Section>
          
          <Section>
            <BarChart
              :data="testCasesProjectData"
              :height="250"
              :title="t('reports.chart.testCasesByProject')"
            />
          </Section>

          <Section>
            <BarChart
              :data="testCasesEstimationData"
              :height="250"
              :title="t('reports.chart.testCasesByEstimation')"
            />
          </Section>
    </div>

        <!-- Row 4: Timeline -->
        <Section class="mt-6">
          <AreaChart
            :data="testCasesTimelineData"
            :height="300"
            dataKey="count"
            :label="t('reports.chart.testCasesLabel')"
            :title="t('reports.chart.testCasesCreatedOverTime')"
          />
        </Section>
        
        <!-- Filtered Test Cases Table -->
        <Section v-if="filteredTestCases.length > 0" overflow class="mt-6">
          <h5 class="text-lg font-semibold text-gray-900 dark:text-white m-0">{{ t('reports.table.filtered', { entity: t('reports.tabs.testCases'), count: filteredTestCases.length }) }}</h5>
          
          <div class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800">
            <div class="p-6">
              <HLDataTableWrapper responsive-column-width max-width="100%">
                <HLDataTable
                  id="testcases-filtered-table"
                  :columns="testCasesFilteredTableColumns"
                  :data="paginatedFilteredTestCases"
                  row-key="id"
                  :column-hover="false"
                  :horizontal-borders="true"
                  class="reports-data-table"
                />
              </HLDataTableWrapper>
                </div>
            
            <!-- Pagination inside table border -->
            <div class="border-t border-gray-200 dark:border-gray-700 px-6 py-4" v-if="filteredTestCases.length > pagination.testCases.pageSize">
              <div class="flex justify-end">
                <HLPagination 
                  :current-page="pagination.testCases.currentPage"
                  :total-pages="Math.ceil(filteredTestCases.length / pagination.testCases.pageSize)"
                  :show-per-page-drop-down="true"
                  :pages-to-display="7"
                  @update:page="pagination.testCases.currentPage = $event"
                  size="md"
                  show-current-range
                  :show-total-pages="true"
                >
                  <template #prev>{{ t('common.previous') }}</template>
                  <template #next>{{ t('common.next') }}</template>
                </HLPagination>
              </div>
            </div>
          </div>
        </Section>
                </div>
              </div>
              
        <!-- Test Runs Tab Content -->
        <div v-show="activeTab === REPORT_TABS.TEST_RUNS" class="space-y-6">
          <div class="space-y-4">
            <StatsGrid :stats="testRunsStats" :columns="{ base: 1, sm: 2, md: 3, lg: 6 }" />
            
            <SectionFilter
              :title="t('reports.sections.testRuns')"
              :expanded="sectionFilters.testRuns.expanded"
              :show-search="false"
              :filtered-count="filteredTestRuns.length"
              :total-count="allTestRuns.length"
              :active-filter-count="testRunsActiveFilters"
              :has-active-filters="testRunsActiveFilters > 0"
              show-deleted-toggle
              :include-deleted="sectionFilters.testRuns.includeDeleted"
              @toggle="toggleSectionFilter('testRuns')"
              @clear="clearSectionFilter('testRuns')"
              @update:include-deleted="sectionFilters.testRuns.includeDeleted = $event"
              @export="exportSectionReport('testRuns')"
            >
        <!-- Search Bar -->
        <div class="col-span-full mb-4">
          <HLInput
            v-model="sectionFilters.testRuns.search"
            :placeholder="t('reports.filter.searchPlaceholder', { section: t('reports.sections.testRuns').toLowerCase() })"
            clearable
            class="w-full"
          >
            <template #prefix>
              <Icon name="search" size="sm" class="text-gray-400" />
            </template>
          </HLInput>
        </div>

        <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.teams') }}</Text>
                <HLSelect
                  :id="`filter-testrun-teams-${uniqueFilterId}`"
                  v-model:value="sectionFilters.testRuns.teamIds"
                  :options="testRunTeamSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allTeams')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
          />
        </div>
        <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.projects') }}</Text>
                <HLSelect
                  :id="`filter-testrun-projects-${uniqueFilterId}`"
                  v-model:value="sectionFilters.testRuns.projectIds"
                  :options="testRunProjectSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allProjects')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
          />
        </div>
        <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.status') }}</Text>
                <HLSelect
                  :id="`filter-testrun-status-${uniqueFilterId}`"
                  v-model:value="sectionFilters.testRuns.status"
                  :options="testRunStatusSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allStatuses')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
          />
          </div>
          <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.environment') }}</Text>
                <HLSelect
                  :id="`filter-testrun-environment-${uniqueFilterId}`"
                  v-model:value="sectionFilters.testRuns.environment"
                  :options="testRunEnvironmentSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allEnvironments')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
            />
          </div>
          <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.labels.createdBy') }}</Text>
                <HLSelect
                  :id="`filter-testrun-createdby-${uniqueFilterId}`"
                  v-model:value="sectionFilters.testRuns.createdBy"
                  :options="testRunCreatedBySelectOptions"
                  :placeholder="t('reports.filter.placeholders.allCreators')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
            />
          </div>
          <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.labels.estimationRange') }}</Text>
                <div class="flex gap-2">
                  <div class="flex-1">
                    <HLInputNumber
                      :id="`filter-testrun-estimation-min-${uniqueFilterId}`"
                      :value="sectionFilters.testRuns.estimationRange.min"
                      @update:value="sectionFilters.testRuns.estimationRange.min = $event"
                      placeholder="Min"
                      :min="0"
                      clearable
                      class="w-full"
                    />
                  </div>
                  <div class="flex-1">
                    <HLInputNumber
                      :id="`filter-testrun-estimation-max-${uniqueFilterId}`"
                      :value="sectionFilters.testRuns.estimationRange.max"
                      @update:value="sectionFilters.testRuns.estimationRange.max = $event"
                      placeholder="Max"
                      :min="0"
                      clearable
                      class="w-full"
                    />
                  </div>
                </div>
          </div>
            </SectionFilter>
        
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <!-- By Status -->
          <Section>
            <DoughnutChart
              :data="testRunsStatusChartData"
              :height="250"
              :title="t('reports.chart.testRunByStatus')"
            />
          </Section>
        
          <!-- By Environment -->
          <Section>
            <BarChart
              :data="testRunsEnvironmentData"
              :height="250"
              :title="t('reports.chart.testRunByEnvironment')"
            />
          </Section>
        
          <!-- Completion Rate -->
          <Section>
            <DoughnutChart
              :data="testRunsCompletionData"
              :height="250"
              :title="t('reports.chart.testRunCompletionRate')"
            />
          </Section>
        </div>
        
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- By Created By -->
          <Section>
            <BarChart
              :data="testRunsCreatedByData"
              :height="300"
              :title="t('reports.chart.testRunByCreatedBy')"
            />
          </Section>
        
          <!-- Runs Timeline (spans 2 columns) -->
          <Section class="lg:col-span-2">
            <LineChart
              :data="testRunsTimelineData"
              :height="300"
              dataKey="count"
              :label="t('reports.chart.testRunsLabel')"
              :title="t('reports.chart.testRunsCreatedOverTime')"
            />
          </Section>
        </div>
        
        <!-- Filtered Results Table -->
        <Section v-if="filteredTestRuns.length > 0" overflow class="mt-6">
          <h5 class="text-lg font-semibold text-gray-900 dark:text-white m-0">{{ t('reports.table.filtered', { entity: t('reports.tabs.testRuns'), count: filteredTestRuns.length }) }}</h5>
          
          <div class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800">
            <div class="p-6">
              <HLDataTableWrapper responsive-column-width max-width="100%">
                <HLDataTable
                  id="testruns-filtered-table"
                  :columns="testRunsFilteredTableColumns"
                  :data="paginatedFilteredTestRuns"
                  row-key="id"
                  :column-hover="false"
                  :horizontal-borders="true"
                  class="reports-data-table"
                />
              </HLDataTableWrapper>

            </div>
            <!-- Pagination inside table border -->
            <div class="border-t border-gray-200 dark:border-gray-700 px-6 py-4" v-if="filteredTestRuns.length > pagination.testRuns.pageSize">
              <div class="flex justify-end">
                <HLPagination 
                  :current-page="pagination.testRuns.currentPage"
                  :total-pages="Math.ceil(filteredTestRuns.length / pagination.testRuns.pageSize)"
                  :show-per-page-drop-down="true"
                  :pages-to-display="7"
                  @update:page="pagination.testRuns.currentPage = $event"
                  size="md"
                  show-current-range
                  :show-total-pages="true"
                >
                  <template #prev>{{ t("common.previous") }}</template>
                  <template #next>{{ t("common.next") }}</template>
                </HLPagination>
              </div>
            </div>
          </div>
        </Section>
                </div>
              </div>
              
        <!-- Bugs Tab Content -->
        <div v-show="activeTab === REPORT_TABS.BUGS" class="space-y-6">
          <div class="space-y-4">
            <StatsGrid :stats="bugsStats" :columns="{ base: 7, sm: 7, md: 7, lg: 7, xl: 7 }" />
            
            <SectionFilter
              :title="t('reports.sections.bugs')"
              :expanded="sectionFilters.bugs.expanded"
              :show-search="false"
              :filtered-count="filteredBugs.length"
              :total-count="allBugs.length"
              :active-filter-count="bugsActiveFilters"
              :has-active-filters="bugsActiveFilters > 0"
              show-deleted-toggle
              :include-deleted="sectionFilters.bugs.includeDeleted"
              @toggle="toggleSectionFilter('bugs')"
              @clear="clearSectionFilter('bugs')"
              @update:include-deleted="sectionFilters.bugs.includeDeleted = $event"
              @export="exportSectionReport('bugs')"
            >
        <!-- Search Bar -->
        <div class="col-span-full mb-4">
          <HLInput
            v-model="sectionFilters.bugs.search"
            :placeholder="t('reports.filter.searchPlaceholder', { section: t('reports.sections.bugs').toLowerCase() })"
            clearable
            class="w-full"
          >
            <template #prefix>
              <Icon name="search" size="sm" class="text-gray-400" />
            </template>
          </HLInput>
        </div>

        <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.teams') }}</Text>
                <HLSelect
                  :id="`filter-bug-teams-${uniqueFilterId}`"
                  v-model:value="sectionFilters.bugs.teamIds"
                  :options="bugTeamSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allTeams')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
          />
        </div>
        <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.projects') }}</Text>
                <HLSelect
                  :id="`filter-bug-projects-${uniqueFilterId}`"
                  v-model:value="sectionFilters.bugs.projectIds"
                  :options="bugProjectSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allProjects')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
          />
        </div>
        <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.features') }}</Text>
                <HLSelect
                  :id="`filter-bug-features-${uniqueFilterId}`"
                  v-model:value="sectionFilters.bugs.featureIds"
                  :options="bugFeatureSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allFeatures')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
          />
        </div>
        <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.status') }}</Text>
                <HLSelect
                  :id="`filter-bug-status-${uniqueFilterId}`"
                  v-model:value="sectionFilters.bugs.status"
                  :options="bugStatusSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allStatuses')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
          />
          </div>
          <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.severity') }}</Text>
                <HLSelect
                  :id="`filter-bug-severity-${uniqueFilterId}`"
                  v-model:value="sectionFilters.bugs.severity"
                  :options="bugSeveritySelectOptions"
                  :placeholder="t('reports.filter.placeholders.allSeverities')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
            />
          </div>
          <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.priority') }}</Text>
                <HLSelect
                  :id="`filter-bug-priority-${uniqueFilterId}`"
                  v-model:value="sectionFilters.bugs.priority"
                  :options="bugPrioritySelectOptions"
                  :placeholder="t('reports.filter.placeholders.allPriorities')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
            />
          </div>
              <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.type') }}</Text>
                <HLSelect
                  :id="`filter-bug-type-${uniqueFilterId}`"
                  v-model:value="sectionFilters.bugs.type"
                  :options="bugTypeSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allTypes')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
                />
        </div>
        <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.fields.environment') }}</Text>
                <HLSelect
                  :id="`filter-bug-environment-${uniqueFilterId}`"
                  v-model:value="sectionFilters.bugs.environment"
                  :options="bugEnvironmentSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allEnvironments')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
          />
      </div>
      <div>
                <Text as="label" variant="label" class="block mb-1">{{ t('reports.filter.labels.assignedTo') }}</Text>
                <HLSelect
                  :id="`filter-bug-assignedto-${uniqueFilterId}`"
                  v-model:value="sectionFilters.bugs.assignedTo"
                  :options="bugAssignedToSelectOptions"
                  :placeholder="t('reports.filter.placeholders.allAssignees')"
                  multiple
                  filterable
                  :option-renderer="optionRenderer"
          />
      </div>
            </SectionFilter>
        
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <!-- Bugs by Status -->
          <Section>
            <DoughnutChart
              :data="bugsStatusData"
              :height="250"
              :title="t('reports.chart.bugsByStatus')"
              @segment-click="handleBugStatusClick"
            />
          </Section>
          
          <!-- Bugs by Severity -->
          <Section>
            <BarChart
              :data="bugsSeverityData"
              :height="250"
              :title="t('reports.chart.bugsBySeverity')"
            />
          </Section>
          
          <!-- Bugs by Priority -->
          <Section>
            <DoughnutChart
              :data="bugsPriorityData"
              :height="250"
              :title="t('reports.chart.bugsByPriority')"
            />
          </Section>
        </div>
        
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <!-- Bugs by Type -->
          <Section>
            <DoughnutChart
              :data="bugsTypeData"
              :height="250"
              :title="t('reports.chart.bugsByType')"
            />
          </Section>
          
          <!-- Bugs by Environment -->
          <Section>
            <BarChart
              :data="bugsEnvironmentData"
              :height="250"
              :title="t('reports.chart.bugsByEnvironment')"
            />
          </Section>
          
          <!-- Bugs by Team -->
          <Section>
            <BarChart
              :data="bugsTeamData"
              :height="250"
              :title="t('reports.chart.bugsByTeam')"
            />
          </Section>
        </div>
        
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <!-- Bugs by Project -->
          <Section>
            <BarChart
              :data="bugsProjectData"
              :height="300"
              :title="t('reports.chart.bugsByProject')"
            />
          </Section>
          
          <!-- Bugs by Assignee -->
          <Section>
            <BarChart
              :data="bugsAssigneeData"
              :height="300"
              :title="t('reports.chart.bugsByAssignee')"
            />
          </Section>
          
          <!-- Bug Timeline -->
          <Section>
            <LineChart
              :data="bugsTimelineData"
              :height="300"
              dataKey="count"
              :label="t('reports.chart.bugsLabel')"
              :title="t('reports.chart.bugsCreatedOverTime')"
            />
          </Section>
        </div>
        
        <!-- Filtered Results Table -->
        <Section v-if="filteredBugs.length > 0" overflow class="mt-6">
          <h5 class="text-lg font-semibold text-gray-900 dark:text-white m-0">{{ t('reports.table.filtered', { entity: t('reports.tabs.bugs'), count: filteredBugs.length }) }}</h5>
          
          <div class="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden bg-white dark:bg-gray-800">
            <div class="p-6">
              <HLDataTableWrapper responsive-column-width max-width="100%">
                <HLDataTable
                  id="bugs-filtered-table"
                  :columns="bugsFilteredTableColumns"
                  :data="paginatedFilteredBugs"
                  row-key="id"
                  :column-hover="false"
                  :horizontal-borders="true"
                  class="reports-data-table"
                />
              </HLDataTableWrapper>
                </div>
            
            <!-- Pagination inside table border -->
            <div class="border-t border-gray-200 dark:border-gray-700 px-6 py-4" v-if="filteredBugs.length > pagination.bugs.pageSize">
              <div class="flex justify-end">
                <HLPagination 
                  :current-page="pagination.bugs.currentPage"
                  :total-pages="Math.ceil(filteredBugs.length / pagination.bugs.pageSize)"
                  :show-per-page-drop-down="true"
                  :pages-to-display="7"
                  @update:page="pagination.bugs.currentPage = $event"
                  size="md"
                  show-current-range
                  :show-total-pages="true"
                >
                  <template #prev>{{ t('common.previous') }}</template>
                  <template #next>{{ t('common.next') }}</template>
                </HLPagination>
              </div>
            </div>
          </div>
        </Section>
    </div>
          </div>
        </div>
        
        <!-- Accessibility Tab Content -->
        <div v-show="activeTab === REPORT_TABS.ACCESSIBILITY" class="space-y-6">
          <!-- Sub-tab Navigation using HLTabs -->
          <HLTabs 
            :value="accessibilityView" 
            @update:value="accessibilityView = $event"
            :animated="false" 
            size="md"
            aria-label="Accessibility sub-tabs"
            :maxVisibleTabs="accessibilitySubTabs.length"
          >
            <HLTab v-for="tabItem in accessibilitySubTabs" :key="tabItem.id" :name="tabItem.id">
              <HLIcon size="16" class="mr-1.5">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" shape-rendering="geometricPrecision" aria-hidden="true">
                  <path :d="tabItem.svgPath" />
                </svg>
              </HLIcon>
              {{ tabItem.label }}
            </HLTab>
          </HLTabs>

          <!-- Loading State -->
          <div v-if="accessibilityLoading" class="flex justify-center py-12">
            <Loader variant="spinner" size="lg" text="Loading accessibility reports..." />
          </div>

          <!-- Empty State (when no reports) - only show when NOT on Settings tab -->
          <Section v-else-if="accessibilityReports.length === 0 && accessibilityView !== ACCESSIBILITY_TABS.SETTINGS">
            <div class="text-center py-12">
              <Icon name="shield" size="xxl" class="mx-auto text-teal-500 mb-4" />
              <Heading level="h3" class="mb-2 text-gray-900 dark:text-white">
                No Accessibility Reports Yet
              </Heading>
              <HLText class="text-gray-500 dark:text-gray-400 mb-4 block">
                {{ isSuperAdmin 
                  ? 'Get started by importing your first accessibility test report.' 
                  : 'Accessibility reports will appear here once imported by an administrator.' 
                }}
              </HLText>
              <HLButton
                v-if="isSuperAdmin"
                @click="accessibilityView = ACCESSIBILITY_TABS.SETTINGS"
                variant="primary"
                color="blue"
                size="md"
                class="mt-4"
              >
                <template #iconLeft>
                  <Icon name="upload" size="sm" />
                </template>
                Go to Settings to Import
              </HLButton>
            </div>
          </Section>

          <!-- Settings Tab (always accessible even without reports) -->
          <div v-else-if="accessibilityView === ACCESSIBILITY_TABS.SETTINGS">
            <!-- All Reports Management Table -->
            <Section v-if="accessibilityReports.length > 0">
              <div class="flex items-center justify-between px-6 pt-4 mb-4">
                <Heading level="h5" class="text-gray-900 dark:text-white m-0">
                  Uploaded Accessibility Reports ({{ accessibilityReports.length }})
                </Heading>
                <HLButton
                  v-if="isSuperAdmin"
                  @click="showAccessibilityImportModal = true"
                  variant="primary"
                  color="blue"
                  size="md"
                >
                  <template #iconLeft>
                    <Icon name="upload" size="sm" />
                  </template>
                  Import Report
                </HLButton>
              </div>
              
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead class="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Report Date
                      </th>
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Total
                      </th>
                      <!-- Impact columns -->
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wider">
                        Critical
                      </th>
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-orange-600 dark:text-orange-400 uppercase tracking-wider">
                        Serious
                      </th>
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-yellow-600 dark:text-yellow-400 uppercase tracking-wider">
                        Moderate
                      </th>
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                        Minor
                      </th>
                      <!-- Status columns -->
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-blue-600 dark:text-blue-400 uppercase tracking-wider">
                        Open
                      </th>
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-orange-600 dark:text-orange-400 uppercase tracking-wider">
                        Reopened
                      </th>
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-yellow-600 dark:text-yellow-400 uppercase tracking-wider">
                        In Progress
                      </th>
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-red-600 dark:text-red-400 uppercase tracking-wider">
                        Blocked
                      </th>
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-green-600 dark:text-green-400 uppercase tracking-wider">
                        Closed
                      </th>
                      <!-- Comparison columns -->
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-red-500 dark:text-red-400 uppercase tracking-wider" title="New issues detected in this report">
                        New
                      </th>
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider" title="Issues that existed in previous report and still exist">
                        Recurring
                      </th>
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-green-500 dark:text-green-400 uppercase tracking-wider" title="Issues from previous report that are no longer present (potentially fixed)">
                        Fixed
                      </th>
                      <th scope="col" class="px-3 py-3 text-center text-xs font-medium text-purple-600 dark:text-purple-400 uppercase tracking-wider" title="Net change (New - Fixed)">
                        Net
                      </th>
                      <th scope="col" class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Uploaded By
                      </th>
                      <th v-if="isSuperAdmin" scope="col" class="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                    <tr 
                      v-for="report in accessibilityReports" 
                      :key="report.id"
                      class="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                    >
                      <td class="px-4 py-3 whitespace-nowrap">
                        <div class="text-sm font-medium text-gray-900 dark:text-white">
                          {{ new Date(report.report_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) }}
                        </div>
                        <div class="text-xs text-gray-500 dark:text-gray-400">
                          {{ report.file_info?.row_count || report.summary?.total_issues || 0 }} rows
                        </div>
                      </td>
                      <td class="px-3 py-3 whitespace-nowrap text-center">
                        <span class="text-sm font-semibold text-gray-900 dark:text-white">
                          {{ report.summary.total_issues }}
                        </span>
                      </td>
                      <!-- Impact columns -->
                      <td class="px-3 py-3 whitespace-nowrap text-center">
                        <span class="text-sm font-medium text-red-600 dark:text-red-400">
                          {{ report.summary.by_impact.critical }}
                        </span>
                      </td>
                      <td class="px-3 py-3 whitespace-nowrap text-center">
                        <span class="text-sm font-medium text-orange-600 dark:text-orange-400">
                          {{ report.summary.by_impact.serious }}
                        </span>
                      </td>
                      <td class="px-3 py-3 whitespace-nowrap text-center">
                        <span class="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                          {{ report.summary.by_impact.moderate }}
                        </span>
                      </td>
                      <td class="px-3 py-3 whitespace-nowrap text-center">
                        <span class="text-sm font-medium text-blue-600 dark:text-blue-400">
                          {{ report.summary.by_impact.minor }}
                        </span>
                      </td>
                      <!-- Status columns -->
                      <td class="px-3 py-3 whitespace-nowrap text-center">
                        <span class="text-sm font-medium text-blue-600 dark:text-blue-400">
                          {{ report.summary.by_status.open || 0 }}
                        </span>
                      </td>
                      <td class="px-3 py-3 whitespace-nowrap text-center">
                        <span class="text-sm font-medium text-orange-600 dark:text-orange-400">
                          {{ report.summary.by_status.reopened || 0 }}
                        </span>
                      </td>
                      <td class="px-3 py-3 whitespace-nowrap text-center">
                        <span class="text-sm font-medium text-yellow-600 dark:text-yellow-400">
                          {{ report.summary.by_status.in_progress || 0 }}
                        </span>
                      </td>
                      <td class="px-3 py-3 whitespace-nowrap text-center">
                        <span class="text-sm font-medium text-red-600 dark:text-red-400">
                          {{ report.summary.by_status.blocked || 0 }}
                        </span>
                      </td>
                      <td class="px-3 py-3 whitespace-nowrap text-center">
                        <span class="text-sm font-medium text-green-600 dark:text-green-400">
                          {{ report.summary.by_status.closed || 0 }}
                        </span>
                      </td>
                      <!-- Comparison columns -->
                      <td class="px-3 py-3 whitespace-nowrap text-center">
                        <span v-if="report.summary.comparison" class="text-sm font-medium text-red-500 dark:text-red-400">
                          +{{ report.summary.comparison.new_issues || 0 }}
                        </span>
                        <span v-else class="text-sm text-gray-400">-</span>
                      </td>
                      <td class="px-3 py-3 whitespace-nowrap text-center">
                        <span v-if="report.summary.comparison" class="text-sm font-medium text-gray-600 dark:text-gray-300">
                          {{ report.summary.comparison.recurring_issues || 0 }}
                        </span>
                        <span v-else class="text-sm text-gray-400">-</span>
                      </td>
                      <td class="px-3 py-3 whitespace-nowrap text-center">
                        <span v-if="report.summary.comparison" class="text-sm font-medium text-green-500 dark:text-green-400">
                          -{{ report.summary.comparison.fixed_issues || 0 }}
                        </span>
                        <span v-else class="text-sm text-gray-400">-</span>
                      </td>
                      <td class="px-3 py-3 whitespace-nowrap text-center">
                        <span 
                          v-if="report.summary.comparison" 
                          class="text-sm font-medium"
                          :class="{
                            'text-red-500 dark:text-red-400': report.summary.comparison.net_change > 0,
                            'text-green-500 dark:text-green-400': report.summary.comparison.net_change < 0,
                            'text-gray-500 dark:text-gray-400': report.summary.comparison.net_change === 0
                          }"
                        >
                          {{ report.summary.comparison.net_change > 0 ? '+' : '' }}{{ report.summary.comparison.net_change || 0 }}
                        </span>
                        <span v-else class="text-sm text-gray-400">-</span>
                      </td>
                      <td class="px-4 py-3 whitespace-nowrap">
                        <span class="text-sm text-gray-900 dark:text-white">
                          {{ report.uploaded_by_name }}
                        </span>
                      </td>
                      <td v-if="isSuperAdmin" class="px-4 py-3 whitespace-nowrap text-center">
                        <HLButton
                          variant="ghost"
                          size="sm"
                          @click="openDeleteReportModal(report)"
                          class="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                        >
                          <template #iconLeft>
                            <Icon name="trash" size="sm" />
                          </template>
                          Delete
                        </HLButton>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Section>

            <!-- Empty state for reports table -->
            <Section v-else>
              <div class="text-center py-8 px-6">
                <Icon name="file" size="xl" class="mx-auto text-gray-400 mb-3" />
                <HLText class="text-gray-500 dark:text-gray-400 mb-4 block">
                  {{ isSuperAdmin 
                    ? 'No reports have been imported yet. Click "Import Report" to get started.'
                    : 'No reports have been imported yet. Please contact an administrator.'
                  }}
                </HLText>
                <HLButton
                  v-if="isSuperAdmin"
                  @click="showAccessibilityImportModal = true"
                  variant="primary"
                  color="blue"
                  size="md"
                  class="mt-4"
                >
                  <template #iconLeft>
                    <Icon name="upload" size="sm" />
                  </template>
                  Import Report
                </HLButton>
              </div>
            </Section>
          </div>

          <!-- Main content (Overview, Summary, Detailed) - only when reports exist -->
          <div v-else>
            <!-- ==================== OVERVIEW TAB ==================== -->
            <div v-if="accessibilityView === ACCESSIBILITY_TABS.OVERVIEW">
              <!-- Stats Grid -->
              <div class="grid grid-cols-1 lg:grid-cols-7 gap-6">
                <StatCard
                  v-for="(stat, index) in accessibilityStats"
                  :key="index"
                  :label="stat.label"
                  :value="stat.value"
                  :subtext="stat.subtext"
                  :icon-path="stat.iconPath"
                  :colors="stat.colors"
                />
              </div>

              <!-- Overview Filters using SectionFilter -->
              <SectionFilter
                class="mt-6"
                title="Accessibility Overview"
                :expanded="accessibilityOverviewFilters.expanded"
                :show-search="false"
                :show-export="true"
                export-text="Export Statistics"
                :active-filter-count="accessibilityOverviewActiveFilters"
                :has-active-filters="accessibilityOverviewActiveFilters > 0"
                :grid-columns="{ base: 1, md: 3, lg: 6 }"
                @toggle="accessibilityOverviewFilters.expanded = !accessibilityOverviewFilters.expanded"
                @clear="clearSummaryFilters"
                @export="handleOverviewExport"
              >
                <!-- Impact Filter -->
                <div>
                  <Text as="label" variant="label" class="block mb-1">Impact</Text>
                  <HLSelect
                    v-model:value="accessibilitySummaryFilters.impact"
                    :options="[
                      { label: 'Critical', value: 'critical' },
                      { label: 'Serious', value: 'serious' },
                      { label: 'Moderate', value: 'moderate' },
                      { label: 'Minor', value: 'minor' }
                    ]"
                    placeholder="All Impacts"
                    multiple
                    filterable
                    clearable
                  />
                </div>

                <!-- Status Filter -->
                <div>
                  <Text as="label" variant="label" class="block mb-1">Status</Text>
                  <HLSelect
                    v-model:value="accessibilitySummaryFilters.status"
                    :options="ACCESSIBILITY_STATUS_OPTIONS"
                    placeholder="All Statuses"
                    multiple
                    filterable
                    clearable
                  />
                </div>

                <!-- Assignee Filter -->
                <div>
                  <Text as="label" variant="label" class="block mb-1">Assigned To</Text>
                  <HLSelect
                    v-model:value="accessibilitySummaryFilters.assignedTo"
                    :options="accessibilityAssigneeOptions"
                    placeholder="All Assignees"
                    multiple
                    filterable
                    clearable
                  />
                </div>

                <!-- Issue Type Filter -->
                <div>
                  <Text as="label" variant="label" class="block mb-1">Issue Type</Text>
                  <HLSelect
                    v-model:value="accessibilitySummaryFilters.issueId"
                    :options="accessibilityIssueIdOptions"
                    placeholder="All Issue Types"
                    multiple
                    filterable
                    clearable
                  />
                </div>

                <!-- Test Name Filter -->
                <div>
                  <Text as="label" variant="label" class="block mb-1">Test Name</Text>
                  <HLSelect
                    v-model:value="accessibilitySummaryFilters.testName"
                    :options="accessibilityTestNameOptions"
                    placeholder="All Tests"
                    multiple
                    filterable
                    clearable
                  />
                </div>

                <!-- Date Range Filter -->
                <div>
                  <Text as="label" variant="label" class="block mb-1">Date Range</Text>
                  <HLSelect
                    v-model:value="accessibilitySummaryFilters.dateRange"
                    :options="[
                      { label: 'All Reports', value: 'all' },
                      { label: 'Latest Report', value: 'latest' },
                      { label: 'Last 7 Days', value: 'last_7_days' },
                      { label: 'Last 30 Days', value: 'last_30_days' },
                      { label: 'Custom Range', value: 'custom' }
                    ]"
                  />
                </div>

                <!-- Custom Date Range (if selected) -->
                <div v-if="accessibilitySummaryFilters.dateRange === 'custom'">
                  <Text as="label" variant="label" class="block mb-1">Start Date</Text>
                  <HLDatePicker
                    v-model:value="accessibilitySummaryFilters.customStartDate"
                    type="date"
                    placeholder="Start Date"
                    format="MMM dd, yyyy"
                    aria-label="Custom start date"
                  />
                </div>
                <div v-if="accessibilitySummaryFilters.dateRange === 'custom'">
                  <Text as="label" variant="label" class="block mb-1">End Date</Text>
                  <HLDatePicker
                    v-model:value="accessibilitySummaryFilters.customEndDate"
                    type="date"
                    placeholder="End Date"
                    format="MMM dd, yyyy"
                    aria-label="Custom end date"
                  />
                </div>
              </SectionFilter>

              <!-- Charts Section -->
              <div v-if="accessibilityReports.length > 0" class="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
              <!-- Row 1: Impact Distribution + Issues by Status + Issues by Assignee -->
              <Section>
                <DoughnutChart
                  :data="accessibilityImpactChartData"
                  :height="300"
                  title="Impact Distribution"
                />
              </Section>

              <Section>
                <HorizontalBarChart
                  :data="accessibilityStatusChartData"
                  :height="300"
                  title="Issues by Status"
                />
              </Section>

              <Section>
                <DoughnutChart
                  :data="accessibilityAssigneeChartData"
                  :height="300"
                  title="Issues by Assignee"
                  :showLegend="true"
                  :maxLegendItems="5"
                />
              </Section>
              </div>

              <!-- Row 2: All Issue Types + Top 10 Pages -->
              <div v-if="accessibilityReports.length > 0" class="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              <Section>
                <DoughnutChart
                  :data="accessibilityIssueIdChartData"
                  :height="300"
                  title="All Issue Types"
                  :showLegend="true"
                  :maxLegendItems="5"
                />
              </Section>

              <Section>
                <BarChart
                  :data="accessibilityTopPagesData"
                  :height="300"
                  title="Top 10 Pages by Issues"
                />
              </Section>
              </div>

              <!-- Row 3: Top 10 Tests + Top 10 Nodes -->
              <div v-if="accessibilityReports.length > 0" class="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
              <Section>
                <BarChart
                  :data="accessibilityTopTestsData"
                  :height="300"
                  title="Top 10 Tests by Issues"
                />
              </Section>

              <Section>
                <BarChart
                  :data="accessibilityTopNodesData"
                  :height="300"
                  title="Top 10 Nodes by Issues"
                />
              </Section>
              </div>

              <!-- Trend Charts Section -->
              <Section v-if="accessibilityReports.length > 1" class="mt-6">
                <div class="flex items-center justify-between mb-4">
                  <Heading level="h5" class="text-gray-900 dark:text-white m-0">
                    Trends Over Time
                  </Heading>
                  <div class="flex items-center gap-3">
                    <HLSelect
                      v-model:value="accessibilityTrendDateRange"
                      :options="[
                        { label: 'Last 7 Days', value: '7_days' },
                        { label: 'Last 30 Days', value: '30_days' },
                        { label: 'Last 12 Weeks', value: '12_weeks' },
                        { label: 'Custom Range', value: 'custom' }
                      ]"
                      size="sm"
                      style="width: 150px"
                    />
                    <HLDatePicker
                      v-if="accessibilityTrendDateRange === 'custom'"
                      v-model:value="accessibilityTrendCustomStartDate"
                      type="date"
                      placeholder="Start Date"
                      size="sm"
                      format="MMM dd, yyyy"
                      style="width: 140px"
                    />
                    <HLDatePicker
                      v-if="accessibilityTrendDateRange === 'custom'"
                      v-model:value="accessibilityTrendCustomEndDate"
                      type="date"
                      placeholder="End Date"
                      size="sm"
                      format="MMM dd, yyyy"
                      style="width: 140px"
                    />
                  </div>
                </div>

                <!-- Custom Date Range Warning -->
                <div v-if="accessibilityTrendDateRange === 'custom' && !isCustomDateRangeValid" class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 flex items-start gap-2">
                  <Icon name="warning" size="sm" class="text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
                  <HLText class="text-sm text-yellow-800 dark:text-yellow-300">
                    Please select both start and end dates. Start date must be before or equal to end date.
                  </HLText>
                </div>

                <div v-if="accessibilityTrendData && isCustomDateRangeValid" class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <!-- Total Issues Trend -->
                  <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <LineChart
                      :data="accessibilityTotalIssuesTrendData"
                      :height="300"
                      dataKey="value"
                      label="Total Issues"
                      title="Total Issues Over Time"
                    />
                  </div>

                  <!-- Impact Trends (Multi-line) -->
                  <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <Heading level="h6" class="mb-3 !mt-0">Issues by Impact Over Time</Heading>
                    <div :style="{ height: '300px' }">
                      <canvas ref="impactTrendCanvas"></canvas>
                    </div>
                  </div>

                  <!-- New vs Fixed Trend (spans 2 columns) -->
                  <div class="lg:col-span-2 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
                    <div class="flex items-center gap-2 mb-3">
                      <Heading level="h6" class="!mt-0 !mb-0">New vs Fixed Issues Over Time</Heading>
                      <HLTooltip placement="top" max-width="400px">
                        <template #trigger>
                          <Icon name="info" size="sm" class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help" />
                        </template>
                        <div class="text-sm space-y-2">
                          <p><strong>How it's calculated:</strong></p>
                          <p>• <strong>New Issues:</strong> Issues that appear in the current report but were not in the previous report (identified by fingerprint)</p>
                          <p>• <strong>Fixed Issues:</strong> Issues that were in the previous report but are no longer in the current report</p>
                          <p>• <strong>Net Change:</strong> New Issues - Fixed Issues (positive means more issues, negative means fewer issues)</p>
                        </div>
                      </HLTooltip>
                    </div>
                    <div :style="{ height: '300px' }">
                      <canvas ref="newVsFixedTrendCanvas"></canvas>
                    </div>
                  </div>
                </div>

                <div v-else class="text-center py-8 text-gray-500 dark:text-gray-400">
                  <Icon name="chart" size="xl" class="mb-2 opacity-50" />
                  <div>Upload multiple reports to see trend analysis</div>
                </div>
              </Section>

            </div>
            <!-- End of Overview tab -->

            <!-- ==================== SUMMARY TAB ==================== -->
            <div v-else-if="accessibilityView === ACCESSIBILITY_TABS.SUMMARY">
              <!-- Summary Filters using SectionFilter -->
              <SectionFilter
                title="Accessibility Test Pages"
                :expanded="accessibilitySummaryTableFilters.expanded"
                :show-search="false"
                :show-export="true"
                export-text="Export Summary"
                :filtered-count="accessibilityTestSummary?.length || 0"
                :total-count="accessibilityTestSummaryTotal"
                :active-filter-count="accessibilitySummaryTableActiveFilters"
                :has-active-filters="accessibilitySummaryTableActiveFilters > 0"
                :grid-columns="{ base: 1, md: 4, lg: 4 }"
                @toggle="accessibilitySummaryTableFilters.expanded = !accessibilitySummaryTableFilters.expanded"
                @clear="clearSummaryTableFilters"
                @export="handleSummaryExport"
              >
                <!-- Search Filter -->
                <div class="md:col-span-2 lg:col-span-2">
                  <Text as="label" variant="label" class="block mb-1">Search</Text>
                  <HLInput
                    v-model="accessibilitySummarySearch"
                    placeholder="Search test names..."
                    clearable
                  >
                    <template #prefix>
                      <Icon name="search" size="sm" class="text-gray-400" />
                    </template>
                  </HLInput>
                </div>

                <!-- Impact Filter -->
                <div>
                  <Text as="label" variant="label" class="block mb-1">Impact</Text>
                  <HLSelect
                    v-model:value="accessibilitySummaryTableFilters.impact"
                    :options="[
                      { label: 'Critical', value: 'critical' },
                      { label: 'Serious', value: 'serious' },
                      { label: 'Moderate', value: 'moderate' },
                      { label: 'Minor', value: 'minor' }
                    ]"
                    placeholder="All Impacts"
                    multiple
                    filterable
                    clearable
                  />
                </div>
              </SectionFilter>

              <!-- Test Summary Table -->
              <Section v-if="accessibilityTestSummary && accessibilityTestSummary.length > 0" class="mt-4">
                <div v-if="accessibilityLoading" class="px-6 py-6">
                  <HLText class="text-gray-500">Loading test summary...</HLText>
                </div>
                <div v-else-if="!accessibilityTestSummary || accessibilityTestSummary.length === 0" class="px-6 py-6">
                  <HLText class="text-gray-500">No test data available</HLText>
                </div>
                <div v-else>
                  <HLDataTableWrapper responsive-column-width max-width="100%">
                    <HLDataTable
                      :columns="accessibilitySummaryColumns"
                      :data="accessibilitySummaryPaginatedData"
                      :loading="accessibilityLoading"
                      :pagination="false"
                    />
                  </HLDataTableWrapper>
                  
                  <!-- Pagination -->
                  <div class="border-t border-gray-200 dark:border-gray-700 px-6 py-4" v-if="accessibilityTestSummary.length > accessibilitySummaryPagination.pageSize">
                    <div class="flex justify-end">
                      <HLPagination
                        :current-page="accessibilitySummaryPagination.currentPage"
                        :item-count="accessibilityTestSummary.length"
                        :per-page="String(accessibilitySummaryPagination.pageSize)"
                        :pages-to-display="7"
                        per-page-text="Rows per page"
                        :per-page-dropdown-options="accessibilitySummaryPerPageOptions"
                        @update:page="handleAccessibilitySummaryPageChange"
                        @update:perPage="handleAccessibilitySummaryPerPageChange"
                        size="md"
                      >
                        <template #prev>Previous</template>
                        <template #next>Next</template>
                      </HLPagination>
                    </div>
                  </div>
                </div>
              </Section>

              <!-- Empty State -->
              <Section v-else class="mt-4">
                <div class="text-center py-12">
                  <Icon name="table" size="xxl" class="mx-auto text-gray-400 mb-4" />
                  <HLText class="text-gray-500 dark:text-gray-400 block">
                    No test summary data available.
                  </HLText>
                </div>
              </Section>
            </div>
            <!-- End of Summary tab -->

            <!-- ==================== DETAILED VIEW TAB ==================== -->
            <div v-else-if="accessibilityView === ACCESSIBILITY_TABS.DETAILED">

              <!-- Detailed View Filters using SectionFilter -->
              <SectionFilter
                title="Accessibility Issues"
                :expanded="accessibilityIssuesFilters.expanded"
                :show-search="false"
                :show-export="true"
                :filtered-count="accessibilityIssues?.length || 0"
                :total-count="accessibilityIssuesPagination?.total || 0"
                :active-filter-count="accessibilityIssuesActiveFilters"
                :has-active-filters="accessibilityIssuesActiveFilters > 0"
                :grid-columns="{ base: 1, md: 2, lg: 4 }"
                @toggle="accessibilityIssuesFilters.expanded = !accessibilityIssuesFilters.expanded"
                @export="handleAccessibilityExport"
                @clear="clearAllIssuesFilters"
              >
                <!-- Impact Filter -->
                <div>
                  <Text as="label" variant="label" class="block mb-1">Impact</Text>
                  <HLSelect
                    v-model:value="accessibilityIssuesFilters.impact"
                    :options="[
                      { label: 'Critical', value: 'critical' },
                      { label: 'Serious', value: 'serious' },
                      { label: 'Moderate', value: 'moderate' },
                      { label: 'Minor', value: 'minor' }
                    ]"
                    placeholder="All Impacts"
                    multiple
                    filterable
                    clearable
                    @update:value="() => {
                      accessibilityIssuesPagination.currentPage = 1
                      loadAccessibilityIssues()
                    }"
                  />
                </div>

                <!-- Status Filter -->
                <div>
                  <Text as="label" variant="label" class="block mb-1">Status</Text>
                  <HLSelect
                    v-model:value="accessibilityIssuesFilters.status"
                    :options="ACCESSIBILITY_STATUS_OPTIONS"
                    placeholder="All Statuses"
                    multiple
                    filterable
                    clearable
                    @update:value="() => {
                      accessibilityIssuesPagination.currentPage = 1
                      loadAccessibilityIssues()
                    }"
                  />
                </div>

                <!-- Assignee Filter -->
                <div>
                  <Text as="label" variant="label" class="block mb-1">Assigned To</Text>
                  <HLSelect
                    v-model:value="accessibilityIssuesFilters.assignedTo"
                    :options="accessibilityAssigneeOptions"
                    placeholder="All Assignees"
                    multiple
                    filterable
                    clearable
                    @update:value="() => {
                      accessibilityIssuesPagination.currentPage = 1
                      loadAccessibilityIssues()
                    }"
                  />
                </div>

                <!-- Page URL Filter -->
                <div>
                  <Text as="label" variant="label" class="block mb-1">Page URL</Text>
                  <HLSelect
                    v-model:value="accessibilityIssuesFilters.pageUrl"
                    :options="accessibilityPageUrlOptions"
                    placeholder="All Pages"
                    multiple
                    filterable
                    clearable
                    @update:value="() => {
                      accessibilityIssuesPagination.currentPage = 1
                      loadAccessibilityIssues()
                    }"
                  />
                </div>

                <!-- Test Name Filter -->
                <div>
                  <Text as="label" variant="label" class="block mb-1">Test Name</Text>
                  <HLSelect
                    v-model:value="accessibilityIssuesFilters.testName"
                    :options="accessibilityTestNameOptions"
                    placeholder="All Tests"
                    multiple
                    filterable
                    clearable
                    @update:value="() => {
                      accessibilityIssuesPagination.currentPage = 1
                      loadAccessibilityIssues()
                    }"
                  />
                </div>

                <!-- Issue ID Filter -->
                <div>
                  <Text as="label" variant="label" class="block mb-1">Issue ID</Text>
                  <HLSelect
                    v-model:value="accessibilityIssuesFilters.issueId"
                    :options="accessibilityIssueIdOptions"
                    placeholder="All Issue IDs"
                    multiple
                    filterable
                    clearable
                    @update:value="() => {
                      accessibilityIssuesPagination.currentPage = 1
                      loadAccessibilityIssues()
                    }"
                  />
                </div>

                <!-- Node Filter -->
                <div>
                  <Text as="label" variant="label" class="block mb-1">Node</Text>
                  <HLSelect
                    v-model:value="accessibilityIssuesFilters.node"
                    :options="accessibilityNodeOptions"
                    placeholder="All Nodes"
                    multiple
                    filterable
                    clearable
                    @update:value="() => {
                      accessibilityIssuesPagination.currentPage = 1
                      loadAccessibilityIssues()
                    }"
                  />
                </div>
              </SectionFilter>

            <!-- Issues Table Section -->
            <Section v-if="accessibilityReports.length > 0" class="mt-6" overflow>
              <!-- Bulk Actions -->
              <div v-if="accessibilitySelectedIssues.length > 0" class="flex items-center gap-3 mb-4 px-6 pt-4">
                <span class="text-sm text-gray-700 dark:text-gray-300">
                  {{ accessibilitySelectedIssues.length }} issue(s) selected
                </span>
                <HLButton
                  variant="secondary"
                  size="sm"
                  @click="openBulkAssignModal"
                >
                  <template #iconLeft>
                    <Icon name="user" size="xs" />
                  </template>
                  Assign
                </HLButton>
                <HLButton
                  variant="secondary"
                  size="sm"
                  @click="openBulkStatusModal"
                >
                  <template #iconLeft>
                    <Icon name="edit" size="xs" />
                  </template>
                  Update Status
                </HLButton>
              </div>

              <!-- Table Header -->
              <div class="flex items-center justify-between mb-4 px-6" :class="{ 'pt-4': accessibilitySelectedIssues.length === 0 }">
                <!-- Left: Report Details -->
                <div class="flex flex-col gap-1">
                  <Heading level="h5" class="text-gray-900 dark:text-white m-0">
                    Accessibility Issue Details
                  </Heading>
                  <div class="text-sm text-gray-600 dark:text-gray-400">
                    Showing {{ accessibilityIssues?.length || 0 }} of {{ accessibilityIssuesPagination?.total || 0 }} issues
                  </div>
                </div>
              </div>

              <!-- Loading State -->
              <div v-if="accessibilityIssuesLoading" class="flex justify-center py-12">
                <Loader variant="spinner" size="md" text="Loading issues..." />
              </div>

              <!-- Table -->
              <div v-else-if="accessibilityIssues && accessibilityIssues.length > 0">
                <HLDataTableWrapper responsive-column-width max-width="100%">
                  <HLDataTable
                    id="accessibility-issues-table"
                    :columns="accessibilityIssuesColumns"
                    :data="accessibilityIssues"
                    row-key="id"
                    :column-hover="false"
                    :horizontal-borders="true"
                    :pagination="false"
                    class="reports-data-table accessibility-issues-table"
                  />
                </HLDataTableWrapper>

                <!-- Pagination -->
                <div v-if="accessibilityIssues.length > 0" class="mt-4 flex justify-end">
                  <HLPagination
                    :current-page="accessibilityIssuesPagination.currentPage"
                    :item-count="accessibilityIssuesPagination.total"
                    :per-page="String(accessibilityIssuesPagination.pageSize)"
                    :pages-to-display="7"
                    per-page-text="Rows per page"
                    :per-page-dropdown-options="accessibilityPerPageOptions"
                    @update:page="handleAccessibilityPageChange"
                    @update:perPage="handleAccessibilityPerPageChange"
                    size="md"
                  >
                    <template #prev>{{ t("common.previous") }}</template>
                    <template #next>{{ t("common.next") }}</template>
                  </HLPagination>
                </div>
              </div>

              <!-- Empty State -->
              <div v-else class="text-center py-12">
                <Icon name="clipboardList" size="xxl" class="mx-auto text-gray-400 mb-4" />
                <HLText class="text-gray-500 dark:text-gray-400 block">
                  No issues found matching your filters.
                </HLText>
              </div>
            </Section>
          </div>
          <!-- End of all-issues view -->
        </div>
        <!-- End of accessibility content -->
      </div>
    </div>
  </div>

  <!-- Accessibility Import Modal -->
  <HLModal
    :show="showAccessibilityImportModal"
    :title="showValidationStep ? 'Confirm Import' : 'Import Accessibility Report'"
    size="lg"
    @update:show="closeAccessibilityModal"
  >
    <div class="space-y-6 p-6">
      <!-- Step 1: File Selection -->
      <div v-if="!showValidationStep">
        <!-- Instructions -->
        <div>
          <HLText class="text-gray-600 dark:text-gray-400 block mb-2">
            Import your accessibility test results in CSV or Excel format.
          </HLText>
          <HLText class="text-sm text-gray-500 dark:text-gray-500 block">
            Supported formats: CSV (.csv), Excel (.xls, .xlsx) • Max file size: 50MB
          </HLText>
        </div>
        
        <!-- Drag & Drop Zone -->
        <div
          @drop.prevent="handleDrop"
          @dragover.prevent="handleDragOver"
          @dragleave="handleDragLeave"
          :class="[
            'border-2 border-dashed rounded-lg p-8 text-center transition-all cursor-pointer',
            isDragging 
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/10' 
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          ]"
          @click="$refs.fileInput?.click()"
        >
          <input
            ref="fileInput"
            type="file"
            multiple
            accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            class="hidden"
            aria-label="Upload accessibility report files"
            @change="handleFileSelect"
          />
          
          <Icon 
            name="upload" 
            size="xxl" 
            :class="[
              'mx-auto mb-3',
              isDragging ? 'text-blue-500' : 'text-gray-400'
            ]" 
          />
          
          <HLText 
            :class="[
              'font-medium mb-1 block',
              isDragging ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'
            ]"
          >
            {{ isDragging ? 'Drop files here' : 'Click to upload or drag and drop' }}
          </HLText>
          
          <HLText class="text-sm text-gray-500 dark:text-gray-400 block">
            CSV or Excel files (up to 50MB each)
          </HLText>
        </div>

        <!-- Selected Files List -->
        <div v-if="selectedFiles.length > 0" class="space-y-2">
          <HLText class="text-sm font-medium text-gray-700 dark:text-gray-300 block">
            Selected Files ({{ selectedFiles.length }})
          </HLText>
          
          <div class="space-y-2 max-h-48 overflow-y-auto">
            <div
              v-for="(file, index) in selectedFiles"
              :key="index"
              class="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <div class="flex items-center space-x-3 flex-1 min-w-0">
                <Icon name="document" size="md" class="text-gray-400 flex-shrink-0" />
                <div class="flex-1 min-w-0">
                  <HLText class="text-sm font-medium text-gray-900 dark:text-white block truncate">
                    {{ file.name }}
                  </HLText>
                  <HLText class="text-xs text-gray-500 dark:text-gray-400 block">
                    {{ formatFileSize(file.size) }}
                  </HLText>
                </div>
              </div>
              <HLButton
                variant="ghost"
                size="sm"
                @click="removeFile(index)"
                class="flex-shrink-0"
              >
                <Icon name="close" size="sm" class="text-gray-400 hover:text-red-500" />
              </HLButton>
            </div>
          </div>
        </div>

        <!-- Help Text -->
        <div class="bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div class="flex space-x-3">
            <Icon name="info" size="md" class="text-blue-500 flex-shrink-0 mt-0.5" />
            <div class="flex-1">
              <HLText class="text-sm text-blue-800 dark:text-blue-300 block font-medium mb-1">
                Expected CSV/Excel Format
              </HLText>
              <HLText class="text-xs text-blue-700 dark:text-blue-400 block">
                Required columns: Test Name, Page URL, Issue ID, Impact, Description, Help URL, Node
              </HLText>
            </div>
          </div>
        </div>
      </div>

      <!-- Step 2: Validation Preview -->
      <div v-else>
        <!-- Report Date Picker -->
        <div class="mb-6">
          <div class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Report Date
          </div>
          <HLDatePicker
            v-model:value="reportDate"
            :placeholder="'Select report date'"
            aria-label="Report Date"
            format="MMM dd, yyyy"
            class="w-full"
          />
        </div>

        <!-- Validation Statistics -->
        <div class="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-lg p-6 border border-blue-200 dark:border-blue-800">
          <HLText class="text-lg font-semibold text-gray-900 dark:text-white block mb-4">
            📊 Data Preview
          </HLText>
          
          <!-- Stats Grid -->
          <div class="grid grid-cols-2 gap-4 mb-4">
            <div class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <HLText class="text-sm text-gray-600 dark:text-gray-400 block mb-1">Total Rows</HLText>
              <HLText class="text-2xl font-bold text-gray-900 dark:text-white block">
                {{ validationResult?.total_rows?.toLocaleString() }}
              </HLText>
            </div>
            
            <div class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-green-200 dark:border-green-700">
              <HLText class="text-sm text-green-600 dark:text-green-400 block mb-1">✓ Valid Rows</HLText>
              <HLText class="text-2xl font-bold text-green-700 dark:text-green-400 block">
                {{ validationResult?.valid_rows?.toLocaleString() }}
              </HLText>
            </div>
            
            <div class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
              <HLText class="text-sm text-blue-600 dark:text-blue-400 block mb-1">🔹 Unique Issues</HLText>
              <HLText class="text-2xl font-bold text-blue-700 dark:text-blue-400 block">
                {{ validationResult?.unique_issues?.toLocaleString() }}
              </HLText>
            </div>
            
            <div class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
              <HLText class="text-sm text-gray-600 dark:text-gray-400 block mb-1">Duplicates</HLText>
              <HLText class="text-2xl font-bold text-gray-900 dark:text-white block">
                {{ validationResult?.duplicate_rows?.toLocaleString() }}
              </HLText>
            </div>
          </div>

          <!-- Impact Distribution -->
          <div class="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mb-4">
            <HLText class="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-3">Impact Distribution</HLText>
            <div class="grid grid-cols-4 gap-3">
              <div class="text-center">
                <HLText class="text-xs text-gray-600 dark:text-gray-400 block mb-1">Critical</HLText>
                <HLText class="text-lg font-bold text-red-600 dark:text-red-400 block">
                  {{ validationResult?.by_impact?.critical?.toLocaleString() }}
                </HLText>
              </div>
              <div class="text-center">
                <HLText class="text-xs text-gray-600 dark:text-gray-400 block mb-1">Serious</HLText>
                <HLText class="text-lg font-bold text-orange-600 dark:text-orange-400 block">
                  {{ validationResult?.by_impact?.serious?.toLocaleString() }}
                </HLText>
              </div>
              <div class="text-center">
                <HLText class="text-xs text-gray-600 dark:text-gray-400 block mb-1">Moderate</HLText>
                <HLText class="text-lg font-bold text-yellow-600 dark:text-yellow-400 block">
                  {{ validationResult?.by_impact?.moderate?.toLocaleString() }}
                </HLText>
              </div>
              <div class="text-center">
                <HLText class="text-xs text-gray-600 dark:text-gray-400 block mb-1">Minor</HLText>
                <HLText class="text-lg font-bold text-blue-600 dark:text-blue-400 block">
                  {{ validationResult?.by_impact?.minor?.toLocaleString() }}
                </HLText>
              </div>
            </div>
          </div>

          <!-- Invalid Rows Warning -->
          <div v-if="validationResult?.invalid_rows > 0" class="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div class="flex space-x-3">
              <Icon name="warning" size="md" class="text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div class="flex-1">
                <HLText class="text-sm text-yellow-800 dark:text-yellow-300 block font-medium mb-1">
                  {{ validationResult.invalid_rows }} rows will be skipped
                </HLText>
                <HLText class="text-xs text-yellow-700 dark:text-yellow-400 block">
                  These rows are missing required fields (Test Name, Page URL, Issue ID, Node) or have invalid Impact values.
                </HLText>
                <div v-if="validationResult?.sample_invalid_reasons?.length > 0" class="mt-2">
                  <HLText class="text-xs text-yellow-700 dark:text-yellow-400 block font-medium mb-1">Sample reasons:</HLText>
                  <ul class="text-xs text-yellow-700 dark:text-yellow-400 list-disc list-inside space-y-1">
                    <li v-for="(reason, idx) in validationResult.sample_invalid_reasons" :key="idx">{{ reason }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3 pt-2 pb-4 pr-4">
        <HLButton
          v-if="showValidationStep"
          variant="secondary"
          size="md"
          @click="showValidationStep = false; validationResult = null"
          :disabled="isUploading"
        >
          Back
        </HLButton>
        <HLButton
          variant="secondary"
          size="md"
          @click="closeAccessibilityModal"
          :disabled="isUploading || isValidating"
        >
          Cancel
        </HLButton>
        <HLButton
          v-if="!showValidationStep"
          variant="primary"
          color="blue"
          size="md"
          @click="handleValidateReport"
          :disabled="selectedFiles.length === 0 || isValidating"
          :loading="isValidating"
        >
          {{ isValidating ? 'Validating...' : 'Next' }}
        </HLButton>
        <HLButton
          v-else
          variant="primary"
          color="blue"
          size="md"
          @click="handleImportReport"
          :disabled="!reportDate || isUploading"
          :loading="isUploading"
        >
          {{ isUploading ? 'Importing...' : 'Confirm Import' }}
        </HLButton>
      </div>
    </template>
  </HLModal>

  <!-- Bulk Assignment Modal -->
  <HLModal
    :show="showBulkAssignModal"
    title="Assign Issues"
    size="md"
    @update:show="showBulkAssignModal = false"
  >
    <div class="space-y-4 p-6">
      <HLText class="text-gray-600 dark:text-gray-400 block">
        Assign {{ accessibilitySelectedIssues.length }} selected issue(s) to:
      </HLText>
      
      <div>
        <div class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Assignee
        </div>
        <HLSelect
          v-model:value="bulkAssignUser"
          :options="accessibilityAssigneeOptions"
          aria-label="Select assignee"
          placeholder="Select assignee"
          filterable
          show-search
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3 pt-2 pb-4 pr-4">
        <HLButton
          variant="secondary"
          size="md"
          @click="showBulkAssignModal = false"
        >
          Cancel
        </HLButton>
        <HLButton
          variant="primary"
          color="blue"
          size="md"
          @click="confirmBulkAssignment"
          :disabled="!bulkAssignUser && bulkAssignUser !== null"
        >
          Assign
        </HLButton>
      </div>
    </template>
  </HLModal>

  <!-- Bulk Status Update Modal -->
  <HLModal
    :show="showBulkStatusModal"
    title="Update Status"
    size="md"
    @update:show="showBulkStatusModal = false"
  >
    <div class="space-y-4 p-6">
      <HLText class="text-gray-600 dark:text-gray-400 block">
        Update status for {{ accessibilitySelectedIssues.length }} selected issue(s) to:
      </HLText>
      
      <div>
        <div class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Status
        </div>
        <HLSelect
          v-model:value="bulkStatus"
          :options="ACCESSIBILITY_STATUS_OPTIONS"
          aria-label="Select status"
          placeholder="Select status"
        />
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3 pt-2 pb-4 pr-4">
        <HLButton
          variant="secondary"
          size="md"
          @click="showBulkStatusModal = false"
        >
          Cancel
        </HLButton>
        <HLButton
          variant="primary"
          color="blue"
          size="md"
          @click="confirmBulkStatusUpdate"
        >
          Update
        </HLButton>
      </div>
    </template>
  </HLModal>

  <!-- Delete Report Confirmation Modal -->
  <HLModal
    :show="showDeleteReportModal"
    title="Delete Report"
    size="md"
    @update:show="showDeleteReportModal = false"
  >
    <div class="space-y-4 p-6">
      <div class="flex items-start gap-3">
        <div class="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
          <Icon name="warning" size="lg" class="text-red-600 dark:text-red-400" />
        </div>
        <div class="flex-1">
          <HLText class="font-semibold text-gray-900 dark:text-white text-lg block mb-2">
            Are you sure you want to delete this report?
          </HLText>
          <HLText class="text-gray-600 dark:text-gray-400 block mb-4">
            This action cannot be undone and will permanently remove:
          </HLText>
          
          <div v-if="reportToDelete" class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700 mb-4">
            <div class="font-medium text-gray-900 dark:text-white mb-2">
              📅 {{ new Date(reportToDelete.report_date).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) }}
            </div>
            <div class="text-sm text-gray-600 dark:text-gray-400">
              {{ reportToDelete.summary.total_issues }} total issues
              <template v-if="reportToDelete.file_info.row_count && reportToDelete.file_info.row_count > 0">
                • {{ reportToDelete.file_info.row_count }} rows imported
              </template>
            </div>
          </div>

          <div class="space-y-2 text-sm">
            <div class="flex items-start gap-2">
              <Icon name="check" size="sm" class="text-gray-400 mt-0.5 flex-shrink-0" />
              <HLText class="text-gray-600 dark:text-gray-400">
                The report record will be marked as deleted
              </HLText>
            </div>
            <div class="flex items-start gap-2">
              <Icon name="check" size="sm" class="text-gray-400 mt-0.5 flex-shrink-0" />
              <HLText class="text-gray-600 dark:text-gray-400">
                Issues that <strong>only existed</strong> in this report will be deleted
              </HLText>
            </div>
            <div class="flex items-start gap-2">
              <Icon name="check" size="sm" class="text-gray-400 mt-0.5 flex-shrink-0" />
              <HLText class="text-gray-600 dark:text-gray-400">
                Issues appearing in other reports will be updated (report date removed)
              </HLText>
            </div>
            <div class="flex items-start gap-2">
              <Icon name="check" size="sm" class="text-gray-400 mt-0.5 flex-shrink-0" />
              <HLText class="text-gray-600 dark:text-gray-400">
                Trend data will be recalculated
              </HLText>
            </div>
          </div>

          <div class="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <HLText class="text-sm text-yellow-800 dark:text-yellow-300 font-medium">
              ⚠️ This operation cannot be reversed
            </HLText>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-3 pt-2 pb-4 pr-4">
        <HLButton
          variant="secondary"
          size="md"
          @click="showDeleteReportModal = false"
          :disabled="isDeletingReport"
        >
          Cancel
        </HLButton>
        <HLButton
          variant="primary"
          color="red"
          size="md"
          @click="confirmDeleteReport"
          :loading="isDeletingReport"
          :disabled="isDeletingReport"
        >
          {{ isDeletingReport ? 'Deleting...' : 'Delete Report' }}
        </HLButton>
      </div>
    </template>
  </HLModal>

  <!-- Issue History Drawer -->
  <HLDrawer
    :show="showAssignmentHistoryDrawer"
    title="Issue History"
    placement="right"
    size="md"
    @update:show="showAssignmentHistoryDrawer = false"
  >
    <div v-if="currentAssignmentHistory" class="p-6 space-y-6">
      <!-- Detection History Section -->
      <div v-if="currentAssignmentHistory.detection_info" class="bg-gradient-to-br from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20 rounded-lg p-4 border border-teal-200 dark:border-teal-800">
        <div class="flex items-center gap-2 mb-3">
          <Icon name="calendar" size="sm" class="text-teal-600 dark:text-teal-400" />
          <HLText class="text-sm font-semibold text-gray-900 dark:text-white">
            Detection History
          </HLText>
        </div>
        
        <div class="space-y-2">
          <div class="flex items-start gap-2">
            <Icon name="arrowRight" size="xs" class="text-teal-600 dark:text-teal-400 mt-1 flex-shrink-0" />
            <div class="flex-1">
              <HLText class="text-xs text-gray-600 dark:text-gray-400 block">First Detected</HLText>
              <HLText class="text-sm font-medium text-gray-900 dark:text-white block">
                {{ new Date(currentAssignmentHistory.detection_info.first_detected).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) }}
              </HLText>
            </div>
          </div>
          
          <div class="flex items-start gap-2">
            <Icon name="arrowRight" size="xs" class="text-teal-600 dark:text-teal-400 mt-1 flex-shrink-0" />
            <div class="flex-1">
              <HLText class="text-xs text-gray-600 dark:text-gray-400 block">Last Detected</HLText>
              <HLText class="text-sm font-medium text-gray-900 dark:text-white block">
                {{ new Date(currentAssignmentHistory.detection_info.last_detected).toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' }) }}
              </HLText>
            </div>
          </div>
          
          <div class="flex items-start gap-2">
            <Icon name="arrowRight" size="xs" class="text-teal-600 dark:text-teal-400 mt-1 flex-shrink-0" />
            <div class="flex-1">
              <HLText class="text-xs text-gray-600 dark:text-gray-400 block">Total Reports</HLText>
              <HLText class="text-sm font-medium text-gray-900 dark:text-white block">
                Appeared in {{ currentAssignmentHistory.detection_info.total_reports }} report{{ currentAssignmentHistory.detection_info.total_reports !== 1 ? 's' : '' }}
              </HLText>
            </div>
          </div>
        </div>
        
        <!-- Report Dates List -->
        <div v-if="currentAssignmentHistory.detection_info.report_dates && currentAssignmentHistory.detection_info.report_dates.length > 0" class="mt-4 pt-3 border-t border-teal-200 dark:border-teal-700">
          <HLText class="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-2">
            Report Dates ({{ currentAssignmentHistory.detection_info.report_dates.length }}):
          </HLText>
          <div class="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
            <span 
              v-for="(date, idx) in currentAssignmentHistory.detection_info.report_dates"
              :key="idx"
              class="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300"
            >
              {{ new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) }}
            </span>
          </div>
        </div>
      </div>

      <HLDivider />

      <!-- Assignment & Status History -->
      <div v-if="mergedHistory.length > 0">
        <div class="flex items-center gap-2 mb-3">
          <Icon name="clockRotate" size="sm" class="text-gray-600 dark:text-gray-400" />
          <HLText class="text-sm font-semibold text-gray-900 dark:text-white">
            Activity History ({{ mergedHistory.length }})
          </HLText>
        </div>
        <div class="space-y-3">
          <div 
            v-for="(entry, index) in mergedHistory" 
            :key="index"
            class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 border-l-4"
            :class="{
              'border-blue-400 dark:border-blue-500': entry.type === 'assignment',
              'border-green-400 dark:border-green-500': entry.type === 'status'
            }"
          >
            <!-- Assignment Entry -->
            <template v-if="entry.type === 'assignment'">
              <HLText class="font-medium text-gray-900 dark:text-white block">
                Assigned to: {{ entry.assigned_to_name || 'Unassigned' }}
              </HLText>
              <HLText class="text-sm text-gray-500 dark:text-gray-400 block mt-1">
                {{ new Date(entry.dateString).toLocaleString() }}
              </HLText>
              <HLText class="text-xs text-gray-500 dark:text-gray-400 block mt-1">
                By {{ entry.assigned_by_name }}
              </HLText>
            </template>

            <!-- Status Entry -->
            <template v-else-if="entry.type === 'status'">
              <div class="flex items-center gap-2">
                <HLText class="font-medium text-gray-900 dark:text-white">
                  Status changed to:
                </HLText>
                <span :class="['inline-flex items-center justify-center px-2 py-0.5 rounded-md text-xs font-medium', 
                  entry.status === 'open' ? 'bg-blue-100 text-blue-800' :
                  entry.status === 'in_progress' ? 'bg-yellow-100 text-yellow-800' :
                  entry.status === 'blocked' ? 'bg-red-100 text-red-800' :
                  entry.status === 'closed' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800']">
                  {{ entry.statusDisplay }}
                </span>
              </div>
              <HLText class="text-sm text-gray-500 dark:text-gray-400 block mt-1">
                {{ new Date(entry.dateString).toLocaleString() }}
              </HLText>
              <HLText class="text-xs text-gray-500 dark:text-gray-400 block mt-1">
                By {{ entry.updated_by_name }}
              </HLText>
            </template>
          </div>
        </div>
      </div>

      <!-- No Activity History -->
      <div v-else>
        <HLText class="text-sm text-gray-500 dark:text-gray-400 block text-center py-8">
          No activity history available
        </HLText>
      </div>
    </div>
    <div v-else class="p-6 text-center text-gray-500 dark:text-gray-400">
      Loading history...
    </div>
  </HLDrawer>
</template>

<script setup>
import { ref, computed, onMounted, watch, nextTick, h } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSticky } from '@/composables/useSticky'
import Button from '@/components/ui/Button.vue'
import Text from '@/components/ui/Text.vue'
import Heading from '@/components/ui/Heading.vue'
import Modal from '@/components/ui/Modal.vue'
import { HLTabs, HLTab, HLIcon, HLDataTable, HLPagination, HLBadge, HLDataTableWrapper, HLTag, HLTooltip, HLSelect, HLInput, HLDatePicker, HLInputNumber, HLButton, HLModal, HLText, HLCheckbox, HLDrawer, HLDivider } from '@platform-ui/highrise'
import PageHeader from '@/components/ui/PageHeader.vue'
import StatsGrid from '@/components/ui/StatsGrid.vue'
import StatCard from '@/components/ui/StatCard.vue'
import SectionFilter from '@/components/reports/SectionFilter.vue'
import DoughnutChart from '@/components/reports/DoughnutChart.vue'
import BarChart from '@/components/reports/BarChart.vue'
import HorizontalBarChart from '@/components/reports/HorizontalBarChart.vue'
import LineChart from '@/components/reports/LineChart.vue'
import AreaChart from '@/components/reports/AreaChart.vue'
import Loader from '@/components/ui/Loader.vue'
import { Chart, LineController, CategoryScale, LinearScale, PointElement, LineElement, Title as ChartTitle, Tooltip as ChartTooltip, Legend as ChartLegend } from 'chart.js'

// Register Chart.js components for multi-line charts
Chart.register(LineController, CategoryScale, LinearScale, PointElement, LineElement, ChartTitle, ChartTooltip, ChartLegend)
import Input from '@/components/ui/Input.vue'
import Section from '@/components/ui/Section.vue'
import Table from '@/components/ui/Table.vue'
import Badge from '@/components/ui/Badge.vue'
import Link from '@/components/ui/Link.vue'
import { HLPopover } from '@platform-ui/highrise'
import Pagination from '@/components/ui/Pagination.vue'
import StackedBarChart from '@/components/reports/StackedBarChart.vue'
import StackedAreaChart from '@/components/reports/StackedAreaChart.vue'
import {
  bucketByDate,
  generateSparklineData,
  groupAndCount,
  calculateCumulative
} from '@/utils/reportHelpers'
import { useToast } from '@/composables/useToast'
import { STAT_CARD_COLORS } from '@/constants/dashboard'
import { STATUS_COLORS } from '@/constants/statusColors'
import { getIconPath } from '@/constants/icons'
import { TAB_ICON_COLORS } from '@/constants/projects'
import { 
  ACCESSIBILITY_IMPACT_COLORS, 
  ACCESSIBILITY_IMPACT_LABELS, 
  ACCESSIBILITY_STATUS_COLORS, 
  ACCESSIBILITY_STATUS_LABELS,
  ACCESSIBILITY_STATUS_OPTIONS,
  getIssueIdColor 
} from '@/constants/accessibility'
import Icon from '@/components/ui/Icon.vue'
import { t } from '@/i18n/en-us'
import { FEATURE_STATUS_OPTIONS, FEATURE_PRIORITY_OPTIONS } from '@/constants/featureConstants'
import { 
  TEST_CASE_TYPES, 
  CASE_TYPES, 
  AUTOMATION_STATUS, 
  AUTOMATION_STATUS_VALUES,
  TEST_CASE_STATUS, 
  PRIORITY_LEVELS 
} from '@/constants/testCaseConstants'
import { TEST_RUN_ENVIRONMENTS, TEST_RUN_STATUS, TEST_RUN_STATUS_VALUES } from '@/constants/testRunConstants'
import { BUG_STATUS_OPTIONS, BUG_SEVERITY_OPTIONS, BUG_PRIORITY_OPTIONS, BUG_TYPE_OPTIONS, ENVIRONMENT_OPTIONS } from '@/constants/bugs'
import { 
  REPORT_PAGINATION,
  REPORT_TABS,
  EXPORT_SHEET_NAMES,
  EXPORT_HEADERS,
  TABLE_COLUMN_CONFIGS,
  getPriorityColor,
  getFeatureStatusColor,
  getTestCaseStatusColor,
  getTestCaseTypeColor,
  getAutomationStatusColor,
  getTestRunStatusColor,
  getEnvironmentColor,
  getBugStatusColor,
  getBugTypeColor,
  getSeverityColor,
  formatDisplayText,
  formatAutomationStatus,
  FILTER_VALUES,
  ESTIMATION_RANGE_LABELS
} from '@/constants/reportConstants'

const { success: showSuccess, error: showError } = useToast()
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()

// Check if current user is Super Admin
const isSuperAdmin = computed(() => {
  return authStore.userProfile?.role?.toLowerCase() === 'super_admin'
})

// Sticky tabs setup - sticks to browser top (covers TopNav when scrolling)
const { 
  elementRef: tabsContainer, 
  isStuck: isTabsStuck,
  disable: disableSticky,
  enable: enableSticky
} = useSticky({ offset: 0 })

// State
const loading = ref(true)

// Initialize activeTab from URL query parameter, fallback to OVERVIEW
const getInitialTab = () => {
  const tabParam = route.query.tab
  // Validate that the tab parameter is a valid tab
  const validTabs = Object.values(REPORT_TABS)
  if (tabParam && validTabs.includes(tabParam)) {
    return tabParam
  }
  return REPORT_TABS.OVERVIEW
}

const activeTab = ref(getInitialTab())

// Accessibility sub-tab options
const ACCESSIBILITY_TABS = {
  OVERVIEW: 'overview',
  SUMMARY: 'summary',
  DETAILED: 'detailed',
  SETTINGS: 'settings'
}

// Accessibility sub-tabs for HLTabs component
const accessibilitySubTabs = computed(() => {
  const tabs = [
    {
      id: ACCESSIBILITY_TABS.OVERVIEW,
      label: 'Overview',
      svgPath: 'M3 3v18h18 M9 9h6 M9 13h4 M9 17h2' // Chart icon
    },
    {
      id: ACCESSIBILITY_TABS.SUMMARY,
      label: 'Summary',
      svgPath: 'M3 3h18v18H3zM3 9h18M3 15h18M9 3v18' // Table icon
    },
    {
      id: ACCESSIBILITY_TABS.DETAILED,
      label: 'Detailed View',
      svgPath: 'M8 6h13 M8 12h13 M8 18h13 M3 6h.01 M3 12h.01 M3 18h.01' // List icon
    }
  ]
  
  // Add Settings tab for all users (actions restricted to Super Admin)
  tabs.push({
    id: ACCESSIBILITY_TABS.SETTINGS,
    label: 'Settings',
    svgPath: 'M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z' // Settings icon
  })
  
  return tabs
})

// Accessibility view state (overview, summary, detailed, settings)
const accessibilityView = computed({
  get: () => {
    const view = route.query.view || ''
    // Map old 'all-issues' to 'detailed' for backwards compatibility
    if (view === 'all-issues') return ACCESSIBILITY_TABS.DETAILED
    return view || ACCESSIBILITY_TABS.OVERVIEW
  },
  set: (value) => {
    router.push({ query: { ...route.query, view: value } })
  }
})

// Accessibility modal state
const showAccessibilityImportModal = ref(false)
const selectedFiles = ref([])
const reportDate = ref(new Date())
const isDragging = ref(false)
const uploadProgress = ref(0)
const isUploading = ref(false)
const isValidating = ref(false)
const validationResult = ref(null)
const showValidationStep = ref(false)

// Accessibility data
const accessibilityReports = ref([])
const accessibilityLoading = ref(false)
const accessibilityIssues = ref([])
const accessibilityIssuesLoading = ref(false)
const accessibilityStatistics = ref(null)
const accessibilityTestSummaryData = ref([])
const accessibilityAllIssueIds = ref([])
const accessibilityAllNodes = ref([])
const accessibilityAllIssuesForCharts = ref([]) // All issues without pagination for charts
const accessibilityAllPageUrls = ref([])
const accessibilityAllTestNames = ref([])
const accessibilitySummarySearch = ref('')
const accessibilitySummaryPagination = ref({
  currentPage: 1,
  pageSize: 100
})

// Accessibility trend data
const accessibilityTrendData = ref(null)
const accessibilityTrendDateRange = ref('12_weeks') // '7_days', '30_days', '12_weeks', 'custom'
const accessibilityTrendCustomStartDate = ref(null)
const accessibilityTrendCustomEndDate = ref(null)
const impactTrendCanvas = ref(null)
const newVsFixedTrendCanvas = ref(null)
let impactTrendChartInstance = null
let newVsFixedTrendChartInstance = null

// Computed property to check if custom date range is valid
const isCustomDateRangeValid = computed(() => {
  if (accessibilityTrendDateRange.value !== 'custom') return true
  
  if (!accessibilityTrendCustomStartDate.value || !accessibilityTrendCustomEndDate.value) {
    return false
  }
  
  const startDate = accessibilityTrendCustomStartDate.value instanceof Date 
    ? accessibilityTrendCustomStartDate.value 
    : new Date(accessibilityTrendCustomStartDate.value)
  
  const endDate = accessibilityTrendCustomEndDate.value instanceof Date 
    ? accessibilityTrendCustomEndDate.value 
    : new Date(accessibilityTrendCustomEndDate.value)
  
  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return false
  }
  
  return startDate <= endDate
})
const accessibilityIssuesFilters = ref({
  search: '',
  impact: [],
  status: [],
  pageUrl: [],
  testName: [],
  issueId: [],
  node: [],
  assignedTo: [],
  expanded: false
})
const accessibilityIssuesPagination = ref({
  currentPage: 1,
  pageSize: 20,
  total: 0
})

// Overview filters state (for expandable SectionFilter)
const accessibilityOverviewFilters = ref({
  expanded: false
})

// Summary table filters state (for expandable SectionFilter)
const accessibilitySummaryTableFilters = ref({
  expanded: false,
  impact: []
})

// Summary view filters (used by Overview tab charts)
const accessibilitySummaryFilters = ref({
  impact: [],
  status: [],
  assignedTo: [],
  issueId: [],
  testName: [],
  dateRange: 'all', // 'all', 'latest', 'last_7_days', 'last_30_days', 'custom'
  customStartDate: null,
  customEndDate: null
})

// Per page options for accessibility issues pagination
const accessibilityPerPageOptions = [
  { key: '10', label: '10' },
  { key: '20', label: '20' },
  { key: '50', label: '50' },
  { key: '100', label: '100' }
]

// Per page options for accessibility summary pagination
const accessibilitySummaryPerPageOptions = [
  { key: '20', label: '20' },
  { key: '50', label: '50' },
  { key: '100', label: '100' },
  { key: '200', label: '200' },
  { key: '500', label: '500' }
]
// Bulk selection
const accessibilitySelectedIssues = ref([])
const accessibilityAllUsersForAssignment = ref([])
// Modals
const showBulkAssignModal = ref(false)
const showBulkStatusModal = ref(false)
const bulkAssignUser = ref(null)
const bulkStatus = ref('open')

// Delete Report Modal
const showDeleteReportModal = ref(false)
const reportToDelete = ref(null)
const isDeletingReport = ref(false)
// Assignment history drawer
const showAssignmentHistoryDrawer = ref(false)
const currentAssignmentHistory = ref(null)
const assignmentHistoryIssueId = ref(null)

// Helper function to format status for display
const formatStatusForDisplay = (status) => {
  if (!status) return 'Open'
  
  const statusMap = {
    'open': 'Open',
    'in_progress': 'In Progress',
    'blocked': 'Blocked',
    'closed': 'Closed'
  }
  
  return statusMap[status] || status
}

// Computed property to merge and sort assignment and status history
const mergedHistory = computed(() => {
  if (!currentAssignmentHistory.value) return []
  
  const merged = []
  
  // Add assignment history
  if (currentAssignmentHistory.value.assignment_history && currentAssignmentHistory.value.assignment_history.length > 0) {
    currentAssignmentHistory.value.assignment_history.forEach(entry => {
      merged.push({
        type: 'assignment',
        date: new Date(entry.assigned_date),
        assigned_to_name: entry.assigned_to_name,
        assigned_by_name: entry.assigned_by_name,
        dateString: entry.assigned_date
      })
    })
  }
  
  // Add status history
  if (currentAssignmentHistory.value.status_history && currentAssignmentHistory.value.status_history.length > 0) {
    currentAssignmentHistory.value.status_history.forEach(entry => {
      merged.push({
        type: 'status',
        date: new Date(entry.updated_date),
        status: entry.status,
        statusDisplay: formatStatusForDisplay(entry.status),
        updated_by_name: entry.updated_by_name,
        dateString: entry.updated_date
      })
    })
  }
  
  // Sort by date (most recent first)
  merged.sort((a, b) => b.date - a.date)
  
  return merged
})

// Accessibility Issues Active Filters Count
const accessibilityIssuesActiveFilters = computed(() => {
  let count = 0
  if (accessibilityIssuesFilters.value.search) count++
  if (accessibilityIssuesFilters.value.impact.length > 0) count++
  if (accessibilityIssuesFilters.value.status.length > 0) count++
  if (accessibilityIssuesFilters.value.pageUrl.length > 0) count++
  if (accessibilityIssuesFilters.value.testName.length > 0) count++
  if (accessibilityIssuesFilters.value.issueId.length > 0) count++
  if (accessibilityIssuesFilters.value.node.length > 0) count++
  if (accessibilityIssuesFilters.value.assignedTo.length > 0) count++
  return count
})

// All data from database
const allTeams = ref([])
const allProjects = ref([])
const allTeamsIncludingDeleted = ref([])
const allProjectsIncludingDeleted = ref([])
const allFeatures = ref([])
const allFeaturesIncludingDeleted = ref([])
const allTestCases = ref([])
const allTestCasesIncludingDeleted = ref([])
const allTestRuns = ref([])
const allTestRunsIncludingDeleted = ref([])
const allBugs = ref([])
const allBugsIncludingDeleted = ref([])
const allUsers = ref([])

// Section-specific filters
const sectionFilters = ref({
  teamsAndProjects: {
    expanded: false,
    search: '',
    selectedTeams: [],
    selectedProjects: [],
    includeDeleted: false
  },
  teams: {
    expanded: false,
    search: '',
    selectedTeams: [],
    includeDeleted: false
  },
  projects: {
    expanded: false,
    search: '',
    selectedProjects: [],
    teamIds: [],
    includeDeleted: false
  },
  features: {
    expanded: false,
    search: '',
    teamIds: [],
    projectIds: [],
    status: [],
    priority: [],
    devAssignees: [],
    qaAssignees: [],
    startDateRange: { start: null, end: null },
    dueDateRange: { start: null, end: null },
    includeDeleted: false
  },
  testCases: {
    expanded: false,
    search: '',
    teamIds: [],
    projectIds: [],
    testType: [],
    caseType: [],
    priority: [],
    status: [],
    automationStatus: [],
    assignees: [],
    estimationRange: { min: null, max: null },
    includeDeleted: false
  },
  testRuns: {
    expanded: false,
    search: '',
    teamIds: [],
    projectIds: [],
    status: [],
    environment: [],
    createdBy: [],
    estimationRange: { min: null, max: null },
    includeDeleted: false
  },
  bugs: {
    expanded: false,
    search: '',
    teamIds: [],
    projectIds: [],
    featureIds: [],
    status: [],
    severity: [],
    priority: [],
    type: [],
    environment: [],
    assignedTo: [],
    includeDeleted: false
  }
})

// Pagination state for filtered tables (using constants)
const pagination = ref({
  teamsTable: {
    currentPage: 1,
    pageSize: 10
  },
  projectsTable: {
    currentPage: 1,
    pageSize: 10
  },
  features: {
    currentPage: 1,
    pageSize: REPORT_PAGINATION.DEFAULT_PAGE_SIZE
  },
  testCases: {
    currentPage: 1,
    pageSize: REPORT_PAGINATION.DEFAULT_PAGE_SIZE
  },
  testRuns: {
    currentPage: 1,
    pageSize: REPORT_PAGINATION.DEFAULT_PAGE_SIZE
  },
  bugs: {
    currentPage: 1,
    pageSize: REPORT_PAGINATION.DEFAULT_PAGE_SIZE
  }
})

// Options for multi-select dropdowns
const teamOptions = computed(() => {
  const teams = sectionFilters.value.teamsAndProjects.includeDeleted 
    ? allTeamsIncludingDeleted.value 
    : allTeams.value
  return teams.map(team => ({
    value: team.id,
    label: team.name
  }))
})

const projectOptions = computed(() => {
  return allProjects.value.map(project => ({
    value: project.id,
    label: project.name
  }))
})

// Count only main features (features without parent_id)
const mainFeaturesCount = computed(() => {
  // Choose the correct data source based on includeDeleted toggle
  const sourceFeatures = sectionFilters.value.features.includeDeleted
    ? allFeaturesIncludingDeleted.value
    : allFeatures.value
  
  return sourceFeatures.filter(feature => 
    !feature.parent_id || feature.parent_id === null || feature.parent_id === ''
  ).length
})

// Total test cases count based on includeDeleted toggle
const totalTestCasesCount = computed(() => {
  return sectionFilters.value.testCases.includeDeleted
    ? allTestCasesIncludingDeleted.value.length
    : allTestCases.value.length
})

// No global filters applied - all data returned as-is
// Each section applies its own filters via section-specific computed properties
const filteredData = computed(() => {
  return {
    teams: allTeams.value,
    projects: allProjects.value,
    features: allFeatures.value,
    testCases: allTestCases.value,
    testRuns: allTestRuns.value,
    bugs: allBugs.value
  }
})

// Filtered teams and projects for Teams & Projects tab
const filteredTeamsAndProjects = computed(() => {
  // Start with teams and projects based on includeDeleted toggle
  let teams = sectionFilters.value.teamsAndProjects.includeDeleted 
    ? allTeamsIncludingDeleted.value 
    : allTeams.value
  let projects = sectionFilters.value.teamsAndProjects.includeDeleted 
    ? allProjectsIncludingDeleted.value 
    : allProjects.value
  
  // Filter by selected teams
  if (sectionFilters.value.teamsAndProjects.selectedTeams.length > 0) {
    teams = teams.filter(t => sectionFilters.value.teamsAndProjects.selectedTeams.includes(t.id))
    // Also filter projects by selected teams
    projects = projects.filter(p => sectionFilters.value.teamsAndProjects.selectedTeams.includes(p.teamId))
  }
  
  // Filter by selected projects
  if (sectionFilters.value.teamsAndProjects.selectedProjects.length > 0) {
    projects = projects.filter(p => sectionFilters.value.teamsAndProjects.selectedProjects.includes(p.id))
    // Also get teams that own the selected projects
    const selectedProjectTeamIds = projects.map(p => p.teamId)
    if (sectionFilters.value.teamsAndProjects.selectedTeams.length === 0) {
      teams = teams.filter(t => selectedProjectTeamIds.includes(t.id))
    }
  }
  
  return { teams, projects }
})

// Section-filtered data
const filteredTeams = computed(() => {
  let teams = filteredData.value.teams
  
  if (sectionFilters.value.teams.search) {
    const search = sectionFilters.value.teams.search.toLowerCase()
    teams = teams.filter(t => t.name.toLowerCase().includes(search))
  }
  
  if (sectionFilters.value.teams.selectedTeams.length > 0) {
    teams = teams.filter(t => sectionFilters.value.teams.selectedTeams.includes(t.id))
  }
  
  return teams
})

const filteredProjects = computed(() => {
  let projects = filteredData.value.projects
  
  if (sectionFilters.value.projects.search) {
    const search = sectionFilters.value.projects.search.toLowerCase()
    projects = projects.filter(p => p.name.toLowerCase().includes(search))
  }
  
  if (sectionFilters.value.projects.selectedProjects.length > 0) {
    projects = projects.filter(p => sectionFilters.value.projects.selectedProjects.includes(p.id))
  }
  
  if (sectionFilters.value.projects.teamIds.length > 0) {
    projects = projects.filter(p => sectionFilters.value.projects.teamIds.includes(p.teamId))
  }
  
  return projects
})

const filteredFeatures = computed(() => {
  // Choose the correct data source based on includeDeleted toggle
  const sourceFeatures = sectionFilters.value.features.includeDeleted
    ? allFeaturesIncludingDeleted.value
    : allFeatures.value
  
  // Only include main features (exclude subtasks/subfeatures)
  let features = sourceFeatures.filter(f => 
    !f.parent_id || f.parent_id === null || f.parent_id === ''
  )
  
  if (sectionFilters.value.features.search) {
    const search = sectionFilters.value.features.search.toLowerCase()
    features = features.filter(f => f.title?.toLowerCase().includes(search) || f.featureId?.toLowerCase().includes(search))
  }
  
  // Filter by teams (through projects)
  if (sectionFilters.value.features.teamIds.length > 0) {
    const teamProjectIds = filteredData.value.projects
      .filter(p => sectionFilters.value.features.teamIds.includes(p.teamId))
      .map(p => p.id)
    features = features.filter(f => teamProjectIds.includes(f.project_id))
  }
  
  // Filter by projects
  if (sectionFilters.value.features.projectIds.length > 0) {
    features = features.filter(f => sectionFilters.value.features.projectIds.includes(f.project_id))
  }
  
  if (sectionFilters.value.features.status.length > 0) {
    features = features.filter(f => sectionFilters.value.features.status.includes(f.status))
  }
  
  if (sectionFilters.value.features.priority.length > 0) {
    features = features.filter(f => sectionFilters.value.features.priority.includes(f.priority))
  }
  
  // Filter by developer assignees
  if (sectionFilters.value.features.devAssignees.length > 0) {
    features = features.filter(f => {
      const hasUnassigned = sectionFilters.value.features.devAssignees.includes(FILTER_VALUES.UNASSIGNED)
      const matchesAssigned = f.dev_assignee && sectionFilters.value.features.devAssignees.includes(f.dev_assignee)
      const matchesUnassigned = !f.dev_assignee && hasUnassigned
      return matchesAssigned || matchesUnassigned
    })
  }
  
  // Filter by QA assignees
  if (sectionFilters.value.features.qaAssignees.length > 0) {
    features = features.filter(f => {
      const hasUnassigned = sectionFilters.value.features.qaAssignees.includes(FILTER_VALUES.UNASSIGNED)
      const matchesAssigned = f.qa_assignee && sectionFilters.value.features.qaAssignees.includes(f.qa_assignee)
      const matchesUnassigned = !f.qa_assignee && hasUnassigned
      return matchesAssigned || matchesUnassigned
    })
  }
  
  // Filter by start date range
  if (sectionFilters.value.features.startDateRange.start) {
    features = features.filter(f => f.start_date && new Date(f.start_date) >= new Date(sectionFilters.value.features.startDateRange.start))
  }
  if (sectionFilters.value.features.startDateRange.end) {
    features = features.filter(f => f.start_date && new Date(f.start_date) <= new Date(sectionFilters.value.features.startDateRange.end))
  }
  
  // Filter by due date range
  if (sectionFilters.value.features.dueDateRange.start) {
    features = features.filter(f => f.due_date && new Date(f.due_date) >= new Date(sectionFilters.value.features.dueDateRange.start))
  }
  if (sectionFilters.value.features.dueDateRange.end) {
    features = features.filter(f => f.due_date && new Date(f.due_date) <= new Date(sectionFilters.value.features.dueDateRange.end))
  }
  
  return features
})

const filteredTestCases = computed(() => {
  // Choose the correct data source based on includeDeleted toggle
  const sourceTestCases = sectionFilters.value.testCases.includeDeleted
    ? allTestCasesIncludingDeleted.value
    : allTestCases.value
  
  let testCases = sourceTestCases
  
  if (sectionFilters.value.testCases.search) {
    const search = sectionFilters.value.testCases.search.toLowerCase()
    testCases = testCases.filter(tc => tc.name?.toLowerCase().includes(search) || tc.testCaseId?.toLowerCase().includes(search))
  }
  
  // Filter by teams (through projects)
  if (sectionFilters.value.testCases.teamIds.length > 0) {
    const teamProjectIds = filteredData.value.projects
      .filter(p => sectionFilters.value.testCases.teamIds.includes(p.teamId))
      .map(p => p.id)
    testCases = testCases.filter(tc => teamProjectIds.includes(tc.project_id))
  }
  
  // Filter by projects
  if (sectionFilters.value.testCases.projectIds.length > 0) {
    testCases = testCases.filter(tc => sectionFilters.value.testCases.projectIds.includes(tc.project_id))
  }
  
  if (sectionFilters.value.testCases.testType.length > 0) {
    testCases = testCases.filter(tc => sectionFilters.value.testCases.testType.includes(tc.type))
  }
  
  if (sectionFilters.value.testCases.caseType.length > 0) {
    testCases = testCases.filter(tc => sectionFilters.value.testCases.caseType.includes(tc.case_type))
  }
  
  if (sectionFilters.value.testCases.priority.length > 0) {
    testCases = testCases.filter(tc => sectionFilters.value.testCases.priority.includes(tc.priority))
  }
  
  if (sectionFilters.value.testCases.status.length > 0) {
    testCases = testCases.filter(tc => sectionFilters.value.testCases.status.includes(tc.status))
  }
  
  if (sectionFilters.value.testCases.automationStatus.length > 0) {
    testCases = testCases.filter(tc => sectionFilters.value.testCases.automationStatus.includes(tc.automation_status))
  }
  
  // Filter by assignees
  if (sectionFilters.value.testCases.assignees.length > 0) {
    testCases = testCases.filter(tc => {
      const hasUnassigned = sectionFilters.value.testCases.assignees.includes(FILTER_VALUES.UNASSIGNED)
      const assigneeId = tc.assignee || tc.assigned_to
      const matchesAssigned = assigneeId && sectionFilters.value.testCases.assignees.includes(assigneeId)
      const matchesUnassigned = !assigneeId && hasUnassigned
      return matchesAssigned || matchesUnassigned
    })
  }
  
  // Filter by estimation range (in minutes)
  if (sectionFilters.value.testCases.estimationRange.min !== null || sectionFilters.value.testCases.estimationRange.max !== null) {
    testCases = testCases.filter(tc => {
      const estimation = tc.estimated_time || 0
      const min = sectionFilters.value.testCases.estimationRange.min
      const max = sectionFilters.value.testCases.estimationRange.max
      
      if (min !== null && max !== null) {
        return estimation >= min && estimation <= max
      } else if (min !== null) {
        return estimation >= min
      } else if (max !== null) {
        return estimation <= max
      }
      return true
    })
  }
  
  return testCases
})

const filteredTestRuns = computed(() => {
  let testRuns = filteredData.value.testRuns
  
  // Filter deleted items unless explicitly included
  if (!sectionFilters.value.testRuns.includeDeleted) {
    testRuns = testRuns.filter(tr => {
      const isDeleted = tr.is_deleted === true || (tr.deleted_at !== null && tr.deleted_at !== undefined)
      return !isDeleted
    })
  }
  
  if (sectionFilters.value.testRuns.search) {
    const search = sectionFilters.value.testRuns.search.toLowerCase()
    testRuns = testRuns.filter(tr => tr.name?.toLowerCase().includes(search))
  }
  
  // Filter by teams (through projects)
  if (sectionFilters.value.testRuns.teamIds.length > 0) {
    const teamProjectIds = filteredData.value.projects
      .filter(p => sectionFilters.value.testRuns.teamIds.includes(p.teamId))
      .map(p => p.id)
    testRuns = testRuns.filter(tr => teamProjectIds.includes(tr.project_id))
  }
  
  // Filter by projects
  if (sectionFilters.value.testRuns.projectIds.length > 0) {
    testRuns = testRuns.filter(tr => sectionFilters.value.testRuns.projectIds.includes(tr.project_id))
  }
  
  if (sectionFilters.value.testRuns.status.length > 0) {
    testRuns = testRuns.filter(tr => sectionFilters.value.testRuns.status.includes(tr.status))
  }
  
  if (sectionFilters.value.testRuns.environment.length > 0) {
    testRuns = testRuns.filter(tr => sectionFilters.value.testRuns.environment.includes(tr.environment))
  }
  
  // Filter by created by
  if (sectionFilters.value.testRuns.createdBy.length > 0) {
    testRuns = testRuns.filter(tr => {
      const hasUnassigned = sectionFilters.value.testRuns.createdBy.includes(FILTER_VALUES.UNASSIGNED)
      const matchesAssigned = tr.created_by && sectionFilters.value.testRuns.createdBy.includes(tr.created_by)
      const matchesUnassigned = !tr.created_by && hasUnassigned
      return matchesAssigned || matchesUnassigned
    })
  }
  
  // Filter by estimation range (in minutes)
  if (sectionFilters.value.testRuns.estimationRange.min !== null || sectionFilters.value.testRuns.estimationRange.max !== null) {
    testRuns = testRuns.filter(tr => {
      const estimation = tr.estimated_time || 0
      const min = sectionFilters.value.testRuns.estimationRange.min
      const max = sectionFilters.value.testRuns.estimationRange.max
      
      if (min !== null && max !== null) {
        return estimation >= min && estimation <= max
      } else if (min !== null) {
        return estimation >= min
      } else if (max !== null) {
        return estimation <= max
      }
      return true
    })
  }
  
  return testRuns
})

const filteredBugs = computed(() => {
  let bugs = filteredData.value.bugs
  
  // Filter deleted items unless explicitly included
  if (!sectionFilters.value.bugs.includeDeleted) {
    bugs = bugs.filter(b => {
      const isDeleted = b.is_deleted === true || (b.deleted_at !== null && b.deleted_at !== undefined)
      return !isDeleted
    })
  }
  
  if (sectionFilters.value.bugs.search) {
    const search = sectionFilters.value.bugs.search.toLowerCase()
    bugs = bugs.filter(b => b.title?.toLowerCase().includes(search) || b.bugId?.toLowerCase().includes(search))
  }
  
  // Filter by teams (through projects)
  if (sectionFilters.value.bugs.teamIds.length > 0) {
    const teamProjectIds = filteredData.value.projects
      .filter(p => sectionFilters.value.bugs.teamIds.includes(p.teamId))
      .map(p => p.id)
    bugs = bugs.filter(b => teamProjectIds.includes(b.project_id))
  }
  
  // Filter by projects
  if (sectionFilters.value.bugs.projectIds.length > 0) {
    bugs = bugs.filter(b => sectionFilters.value.bugs.projectIds.includes(b.project_id))
  }
  
  if (sectionFilters.value.bugs.status.length > 0) {
    bugs = bugs.filter(b => sectionFilters.value.bugs.status.includes(b.status))
  }
  
  if (sectionFilters.value.bugs.severity.length > 0) {
    bugs = bugs.filter(b => sectionFilters.value.bugs.severity.includes(b.severity))
  }
  
  if (sectionFilters.value.bugs.priority.length > 0) {
    bugs = bugs.filter(b => sectionFilters.value.bugs.priority.includes(b.priority))
  }
  
  if (sectionFilters.value.bugs.type.length > 0) {
    bugs = bugs.filter(b => sectionFilters.value.bugs.type.includes(b.type))
  }
  
  if (sectionFilters.value.bugs.environment.length > 0) {
    bugs = bugs.filter(b => sectionFilters.value.bugs.environment.includes(b.environment))
  }
  
  // Filter by assigned to
  if (sectionFilters.value.bugs.assignedTo.length > 0) {
    bugs = bugs.filter(b => {
      const hasUnassigned = sectionFilters.value.bugs.assignedTo.includes(FILTER_VALUES.UNASSIGNED)
      const matchesAssigned = b.assigned_to && sectionFilters.value.bugs.assignedTo.includes(b.assigned_to)
      const matchesUnassigned = !b.assigned_to && hasUnassigned
      return matchesAssigned || matchesUnassigned
    })
  }
  
  return bugs
})

// Paginated data for external pagination (slice data based on current page)
const paginatedFeatures = computed(() => {
  const start = (pagination.value.features.currentPage - 1) * pagination.value.features.pageSize
  const end = start + pagination.value.features.pageSize
  return filteredFeatures.value.slice(start, end)
})

const paginatedTestCases = computed(() => {
  const start = (pagination.value.testCases.currentPage - 1) * pagination.value.testCases.pageSize
  const end = start + pagination.value.testCases.pageSize
  return filteredTestCases.value.slice(start, end)
})

const paginatedTestRuns = computed(() => {
  const start = (pagination.value.testRuns.currentPage - 1) * pagination.value.testRuns.pageSize
  const end = start + pagination.value.testRuns.pageSize
  return filteredTestRuns.value.slice(start, end)
})

const paginatedBugs = computed(() => {
  const start = (pagination.value.bugs.currentPage - 1) * pagination.value.bugs.pageSize
  const end = start + pagination.value.bugs.pageSize
  return filteredBugs.value.slice(start, end)
})

// Teams data
const teamsTimelineData = computed(() => {
  if (filteredTeams.value.length === 0) {
    return []
  }
  
  const buckets = bucketByDate(filteredTeams.value, 'created_at', 'week')
  const endDate = new Date()
  const startDate = new Date(endDate.getTime() - (12 * 7 * 24 * 60 * 60 * 1000)) // 12 weeks
  
  // Helper function to generate weekly sparkline data
  const generateWeeklySparklineData = (buckets, start, end) => {
    const data = []
    const currentWeek = new Date(start)
    
    // Set to Monday of the start week
    currentWeek.setDate(currentWeek.getDate() - currentWeek.getDay() + (currentWeek.getDay() === 0 ? -6 : 1))
    
    // Generate data for each week
    while (currentWeek <= end) {
      const weekKey = currentWeek.toISOString().split('T')[0]
      data.push({
        date: weekKey,
        count: buckets[weekKey] ? buckets[weekKey].length : 0
      })
      
      // Move to next week
      currentWeek.setDate(currentWeek.getDate() + 7)
    }
    
    return data
  }
  
  const weeklyData = calculateCumulative(generateWeeklySparklineData(buckets, startDate, endDate))
  
  // Format dates as "Week of MMM DD" for better readability
  return weeklyData.map(item => {
    const weekDate = new Date(item.date)
    const formattedDate = weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    
    return {
      date: `Week of ${formattedDate}`,
      count: item.count
    }
  })
})

const teamsTableData = computed(() => {
  return filteredTeamsAndProjects.value.teams.map(team => {
    const teamProjects = filteredTeamsAndProjects.value.projects.filter(p => p.teamId === team.id)
    const projectIds = teamProjects.map(p => p.id)
    
    return {
      id: team.id,
      name: team.name,
      projectsCount: teamProjects.length,
      featuresCount: filteredData.value.features.filter(f => 
        projectIds.includes(f.project_id) && 
        (!f.parent_id || f.parent_id === null || f.parent_id === '')
      ).length,
      testCasesCount: filteredData.value.testCases.filter(tc => projectIds.includes(tc.project_id)).length,
      testRunsCount: filteredData.value.testRuns.filter(tr => projectIds.includes(tr.project_id)).length,
      bugsCount: filteredData.value.bugs.filter(b => projectIds.includes(b.project_id)).length
    }
  })
})

// Paginated teams table data
const paginatedTeamsTableData = computed(() => {
  const start = (pagination.value.teamsTable.currentPage - 1) * pagination.value.teamsTable.pageSize
  const end = start + pagination.value.teamsTable.pageSize
  return teamsTableData.value.slice(start, end)
})

// Teams Breakdown Table Columns (HLDataTable format)
const teamsBreakdownTableColumns = computed(() => [
  {
    id: 'name',
    accessorKey: 'name',
    header: t('reports.table.team'),
    size: 300,
    enableResizing: true,
    enableSorting: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const team = props.row.original
      return h('a', {
        href: `/teams/${team.id}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer'
      }, team.name)
    }
  },
  {
    id: 'projectsCount',
    accessorKey: 'projectsCount',
    header: t('reports.table.projectsCount'),
    size: 150,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const count = props.row.original.projectsCount
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, { 
          id: `projects-tag-${props.row.original.id}`,
          color: 'blue-light',
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, 
          { default: () => `${count} Project${count !== 1 ? 's' : ''}` }
        )
      ])
    }
  },
  {
    id: 'featuresCount',
    accessorKey: 'featuresCount',
    header: t('reports.table.featuresCount'),
    size: 150,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const count = props.row.original.featuresCount
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, { 
          id: `features-tag-${props.row.original.id}`,
          color: 'purple',
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, 
          { default: () => `${count} Feature${count !== 1 ? 's' : ''}` }
        )
      ])
    }
  },
  {
    id: 'testCasesCount',
    accessorKey: 'testCasesCount',
    header: t('reports.table.testCasesCount'),
    size: 150,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const count = props.row.original.testCasesCount
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, { 
          id: `testcases-tag-${props.row.original.id}`,
          color: 'success',
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, 
          { default: () => `${count} Test Case${count !== 1 ? 's' : ''}` }
        )
      ])
    }
  },
  {
    id: 'testRunsCount',
    accessorKey: 'testRunsCount',
    header: t('reports.table.testRunsCount'),
    size: 150,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const count = props.row.original.testRunsCount
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, { 
          id: `testruns-tag-${props.row.original.id}`,
          color: 'warning',
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, 
          { default: () => `${count} Test Run${count !== 1 ? 's' : ''}` }
        )
      ])
    }
  },
  {
    id: 'bugsCount',
    accessorKey: 'bugsCount',
    header: t('reports.table.bugsCount'),
    size: 150,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const count = props.row.original.bugsCount
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, { 
          id: `bugs-tag-${props.row.original.id}`,
          color: 'error',
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, 
          { default: () => `${count} Bug${count !== 1 ? 's' : ''}` }
        )
      ])
    }
  }
])

// Teams table columns (old format - keep for backwards compatibility if needed)
const teamsTableColumns = computed(() => [
  {
    ...TABLE_COLUMN_CONFIGS.TEAMS.NAME,
    label: t('reports.table.team')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEAMS.PROJECTS_COUNT,
    label: t('reports.table.projectsCount')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEAMS.FEATURES_COUNT,
    label: t('reports.table.featuresCount')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEAMS.TEST_CASES_COUNT,
    label: t('reports.table.testCasesCount')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEAMS.TEST_RUNS_COUNT,
    label: t('reports.table.testRunsCount')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEAMS.BUGS_COUNT,
    label: t('reports.table.bugsCount')
  }
])

// Projects data
const projectsTableData = computed(() => {
  return filteredTeamsAndProjects.value.projects.map(project => {
    return {
      id: project.id,
      name: project.name,
      teamId: project.teamId,
      featuresCount: filteredData.value.features.filter(f => 
        f.project_id === project.id && 
        (!f.parent_id || f.parent_id === null || f.parent_id === '')
      ).length,
      testCasesCount: filteredData.value.testCases.filter(tc => tc.project_id === project.id).length,
      testRunsCount: filteredData.value.testRuns.filter(tr => tr.project_id === project.id).length,
      bugsCount: filteredData.value.bugs.filter(b => b.project_id === project.id).length
    }
  })
})

// Paginated projects table data
const paginatedProjectsTableData = computed(() => {
  const start = (pagination.value.projectsTable.currentPage - 1) * pagination.value.projectsTable.pageSize
  const end = start + pagination.value.projectsTable.pageSize
  return projectsTableData.value.slice(start, end)
})

// Projects Breakdown Table Columns (HLDataTable format)
const projectsBreakdownTableColumns = computed(() => [
  {
    id: 'name',
    accessorKey: 'name',
    header: t('reports.table.project'),
    size: 300,
    enableResizing: true,
    enableSorting: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const project = props.row.original
      const url = `/projects/${project.id}`
      return h('a', { 
        href: url,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer'
      }, project.name)
    }
  },
  {
    id: 'teamId',
    accessorKey: 'teamId',
    header: t('reports.table.team'),
    size: 200,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const teamId = props.row.original.teamId
      const teamName = getTeamName(teamId)
      return h('a', {
        href: `/teams/${teamId}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer'
      }, teamName)
    }
  },
  {
    id: 'featuresCount',
    accessorKey: 'featuresCount',
    header: t('reports.table.featuresCount'),
    size: 150,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const count = props.row.original.featuresCount
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, { 
          id: `project-features-tag-${props.row.original.id}`,
          color: 'purple',
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, 
          { default: () => `${count} Feature${count !== 1 ? 's' : ''}` }
        )
      ])
    }
  },
  {
    id: 'testCasesCount',
    accessorKey: 'testCasesCount',
    header: t('reports.table.testCasesCount'),
    size: 150,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const count = props.row.original.testCasesCount
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, { 
          id: `project-testcases-tag-${props.row.original.id}`,
          color: 'success',
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, 
          { default: () => `${count} Test Case${count !== 1 ? 's' : ''}` }
        )
      ])
    }
  },
  {
    id: 'testRunsCount',
    accessorKey: 'testRunsCount',
    header: t('reports.table.testRunsCount'),
    size: 150,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const count = props.row.original.testRunsCount
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, { 
          id: `project-testruns-tag-${props.row.original.id}`,
          color: 'warning',
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, 
          { default: () => `${count} Test Run${count !== 1 ? 's' : ''}` }
        )
      ])
    }
  },
  {
    id: 'bugsCount',
    accessorKey: 'bugsCount',
    header: t('reports.table.bugsCount'),
    size: 150,
    enableResizing: true,
    meta: {
      align: 'center',
      headerAlign: 'center'
    },
    cellFormatter: (props) => {
      const count = props.row.original.bugsCount
      return h('div', { class: 'flex justify-center' }, [
        h(HLTag, { 
          id: `project-bugs-tag-${props.row.original.id}`,
          color: 'error',
          size: 'sm',
          round: true,
          interactive: false,
          bordered: false
        }, 
          { default: () => `${count} Bug${count !== 1 ? 's' : ''}` }
        )
      ])
    }
  }
])

// Features table columns configuration
const featuresTableColumns = computed(() => [
  {
    ...TABLE_COLUMN_CONFIGS.FEATURES.ID,
    label: t('reports.table.id')
  },
  {
    ...TABLE_COLUMN_CONFIGS.FEATURES.TITLE,
    label: t('reports.table.title')
  },
  {
    ...TABLE_COLUMN_CONFIGS.FEATURES.PROJECT,
    label: t('reports.table.project')
  },
  {
    ...TABLE_COLUMN_CONFIGS.FEATURES.STATUS,
    label: t('reports.table.status')
  },
  {
    ...TABLE_COLUMN_CONFIGS.FEATURES.PRIORITY,
    label: t('reports.table.priority')
  },
  {
    ...TABLE_COLUMN_CONFIGS.FEATURES.DEVELOPER,
    label: t('reports.table.developer')
  },
  {
    ...TABLE_COLUMN_CONFIGS.FEATURES.QA,
    label: t('reports.table.qa')
  },
  {
    ...TABLE_COLUMN_CONFIGS.FEATURES.START_DATE,
    label: t('reports.table.startDate')
  },
  {
    ...TABLE_COLUMN_CONFIGS.FEATURES.DUE_DATE,
    label: t('reports.table.dueDate')
  }
])

// Test Cases table columns configuration
const testCasesTableColumns = computed(() => [
  {
    ...TABLE_COLUMN_CONFIGS.TEST_CASES.ID,
    label: t('reports.table.id')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEST_CASES.TITLE,
    label: t('reports.table.title')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEST_CASES.TYPE,
    label: t('reports.table.type')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEST_CASES.PRIORITY,
    label: t('reports.table.priority')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEST_CASES.STATUS,
    label: t('reports.table.status')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEST_CASES.AUTOMATION,
    label: t('reports.table.automation')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEST_CASES.PROJECT,
    label: t('reports.table.project')
  }
])

// Test Runs table columns configuration
const testRunsTableColumns = computed(() => [
  {
    ...TABLE_COLUMN_CONFIGS.TEST_RUNS.TITLE,
    label: t('reports.table.title')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEST_RUNS.STATUS,
    label: t('reports.table.status')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEST_RUNS.ENVIRONMENT,
    label: t('reports.table.environment')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEST_RUNS.PROGRESS,
    label: t('reports.table.progress')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEST_RUNS.CREATED_BY,
    label: t('reports.table.createdBy')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEST_RUNS.PROJECT,
    label: t('reports.table.project')
  },
  {
    ...TABLE_COLUMN_CONFIGS.TEST_RUNS.CREATED,
    label: t('reports.table.created'),
    formatter: (value) => formatDate(value)
  }
])

// Bugs table columns configuration
const bugsTableColumns = computed(() => [
  {
    ...TABLE_COLUMN_CONFIGS.BUGS.ID,
    label: t('reports.table.id')
  },
  {
    ...TABLE_COLUMN_CONFIGS.BUGS.TITLE,
    label: t('reports.table.title')
  },
  {
    ...TABLE_COLUMN_CONFIGS.BUGS.TYPE,
    label: t('reports.table.type')
  },
  {
    ...TABLE_COLUMN_CONFIGS.BUGS.STATUS,
    label: t('reports.table.status')
  },
  {
    ...TABLE_COLUMN_CONFIGS.BUGS.SEVERITY,
    label: t('reports.table.severity')
  },
  {
    ...TABLE_COLUMN_CONFIGS.BUGS.PRIORITY,
    label: t('reports.table.priority')
  },
  {
    ...TABLE_COLUMN_CONFIGS.BUGS.ENVIRONMENT,
    label: t('reports.table.environment')
  },
  {
    ...TABLE_COLUMN_CONFIGS.BUGS.PROJECT,
    label: t('reports.table.project')
  }
])

// Projects data
const projectsTimelineData = computed(() => {
  if (filteredProjects.value.length === 0) {
    return []
  }
  
  const buckets = bucketByDate(filteredProjects.value, 'created_at', 'week')
  const endDate = new Date()
  const startDate = new Date(endDate.getTime() - (12 * 7 * 24 * 60 * 60 * 1000)) // 12 weeks
  
  // Helper function to generate weekly sparkline data
  const generateWeeklySparklineData = (buckets, start, end) => {
    const data = []
    const currentWeek = new Date(start)
    
    // Set to Monday of the start week
    currentWeek.setDate(currentWeek.getDate() - currentWeek.getDay() + (currentWeek.getDay() === 0 ? -6 : 1))
    
    // Generate data for each week
    while (currentWeek <= end) {
      const weekKey = currentWeek.toISOString().split('T')[0]
      data.push({
        date: weekKey,
        count: buckets[weekKey] ? buckets[weekKey].length : 0
      })
      
      // Move to next week
      currentWeek.setDate(currentWeek.getDate() + 7)
    }
    
    return data
  }
  
  const weeklyData = calculateCumulative(generateWeeklySparklineData(buckets, startDate, endDate))
  
  // Format dates as "Week of MMM DD" for better readability
  return weeklyData.map(item => {
    const weekDate = new Date(item.date)
    const formattedDate = weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    
    return {
      date: `Week of ${formattedDate}`,
      count: item.count
    }
  })
})

const projectsStackedData = computed(() => {
  if (filteredProjects.value.length === 0) {
    return []
  }
  
  return filteredProjects.value.slice(0, 10).map(project => ({
    name: project.name.length > 20 ? project.name.substring(0, 20) + '...' : project.name,
    features: filteredData.value.features.filter(f => 
      f.project_id === project.id && 
      (!f.parent_id || f.parent_id === null || f.parent_id === '')
    ).length,
    testCases: filteredData.value.testCases.filter(tc => tc.project_id === project.id).length,
    testRuns: filteredData.value.testRuns.filter(tr => tr.project_id === project.id).length,
    bugs: filteredData.value.bugs.filter(b => b.project_id === project.id).length
  }))
})

// Features data
const featuresStatusData = computed(() => {
  const features = filteredFeatures.value
  const groups = groupAndCount(features, 'status')
  
  // Always show all possible status values, even if count is 0
  return FEATURE_STATUS_OPTIONS.map(option => ({
    label: option.label,
    value: groups[option.value] || 0
  }))
})

const featuresPriorityData = computed(() => {
  const features = filteredFeatures.value
  const groups = groupAndCount(features, 'priority')
  
  // Always show all possible priority values, even if count is 0
  return FEATURE_PRIORITY_OPTIONS.map(option => ({
    label: option.label,
    value: groups[option.value] || 0
  }))
})

const featuresTimelineData = computed(() => {
  if (filteredFeatures.value.length === 0) {
    return []
  }
  
  const buckets = bucketByDate(filteredFeatures.value, 'created_at', 'day')
  const start = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  const end = new Date().toISOString().split('T')[0]
  return generateSparklineData(buckets, start, end)
})

// Features by Team
const featuresTeamData = computed(() => {
  if (filteredFeatures.value.length === 0) {
    return []
  }
  
  // Group features by team (via project)
  const teamFeatureCount = {}
  filteredFeatures.value.forEach(feature => {
    const project = allProjects.value.find(p => p.id === feature.project_id)
    if (project && project.teamId) {
      const team = allTeams.value.find(t => t.id === project.teamId)
      if (team) {
        const teamName = team.name
        teamFeatureCount[teamName] = (teamFeatureCount[teamName] || 0) + 1
      }
    }
  })
  
  return Object.entries(teamFeatureCount)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10) // Top 10 teams
})

// Features by Project
const featuresProjectData = computed(() => {
  if (filteredFeatures.value.length === 0) {
    return []
  }
  
  const groups = groupAndCount(filteredFeatures.value, 'project_id')
  return Object.entries(groups)
    .map(([projectId, value]) => {
      const project = allProjects.value.find(p => p.id === projectId)
      const label = project ? project.name : t('common.unknownProject')
      return { label, value }
    })
    .sort((a, b) => b.value - a.value)
    .slice(0, 10) // Top 10 projects
})

// Features by Developer
const featuresDeveloperData = computed(() => {
  if (filteredFeatures.value.length === 0) {
    return []
  }
  
  // Group features by developer
  const devFeatureCount = {}
  filteredFeatures.value.forEach(feature => {
    if (feature.dev_assignee) {
      const user = allUsers.value.find(u => u.id === feature.dev_assignee)
      const devName = user ? user.name : feature.dev_assignee
      devFeatureCount[devName] = (devFeatureCount[devName] || 0) + 1
    }
  })
  
  return Object.entries(devFeatureCount)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10) // Top 10 developers
})

// Features by QA
const featuresQAData = computed(() => {
  if (filteredFeatures.value.length === 0) {
    return []
  }
  
  // Group features by QA
  const qaFeatureCount = {}
  filteredFeatures.value.forEach(feature => {
    if (feature.qa_assignee) {
      const user = allUsers.value.find(u => u.id === feature.qa_assignee)
      const qaName = user ? user.name : feature.qa_assignee
      qaFeatureCount[qaName] = (qaFeatureCount[qaName] || 0) + 1
    }
  })
  
  return Object.entries(qaFeatureCount)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 10) // Top 10 QAs
})

// Test Cases data
const testCasesAutomationData = computed(() => {
  if (filteredTestCases.value.length === 0) {
    return []
  }
  
  const automated = filteredTestCases.value.filter(tc => tc.automation_status === AUTOMATION_STATUS_VALUES.AUTOMATED).length
  const semiAutomated = filteredTestCases.value.filter(tc => tc.automation_status === AUTOMATION_STATUS_VALUES.SEMI_AUTOMATED).length
  const manual = filteredTestCases.value.filter(tc => tc.automation_status === AUTOMATION_STATUS_VALUES.MANUAL || !tc.automation_status).length
  
  return [
    { label: t('reports.stats.automated'), value: automated },
    { label: t('reports.stats.semiAutomated'), value: semiAutomated },
    { label: t('reports.stats.manual'), value: manual }
  ].filter(item => item.value > 0)
})

const testCasesTypeData = computed(() => {
  if (filteredTestCases.value.length === 0) {
    return []
  }
  
  const groups = groupAndCount(filteredTestCases.value, 'type')
  return Object.entries(groups).map(([label, value]) => ({
    label: label.charAt(0).toUpperCase() + label.slice(1),
    value
  }))
})

const testCasesPriorityData = computed(() => {
  if (filteredTestCases.value.length === 0) {
    return []
  }
  
  const groups = groupAndCount(filteredTestCases.value, 'priority')
  return Object.entries(groups).map(([label, value]) => ({
    label: label.charAt(0).toUpperCase() + label.slice(1),
    value
  }))
})

const testCasesStatusData = computed(() => {
  if (filteredTestCases.value.length === 0) {
    return []
  }
  
  const groups = groupAndCount(filteredTestCases.value, 'status')
  return Object.entries(groups).map(([label, value]) => ({
    label: label.charAt(0).toUpperCase() + label.slice(1).replace(/_/g, ' '),
    value
  }))
})

const testCasesTimelineData = computed(() => {
  if (filteredTestCases.value.length === 0) {
    return []
  }
  
  const buckets = bucketByDate(filteredTestCases.value, 'created_at', 'week')
  const endDate = new Date()
  const startDate = new Date(endDate.getTime() - (12 * 7 * 24 * 60 * 60 * 1000)) // 12 weeks
  
  // Helper function to generate weekly sparkline data
  const generateWeeklySparklineData = (buckets, start, end) => {
    const data = []
    const currentWeek = new Date(start)
    
    // Set to Monday of the start week
    currentWeek.setDate(currentWeek.getDate() - currentWeek.getDay() + (currentWeek.getDay() === 0 ? -6 : 1))
    
    // Generate data for each week
    while (currentWeek <= end) {
      const weekKey = currentWeek.toISOString().split('T')[0]
      data.push({
        date: weekKey,
        count: buckets[weekKey] ? buckets[weekKey].length : 0
      })
      
      // Move to next week
      currentWeek.setDate(currentWeek.getDate() + 7)
    }
    
    return data
  }
  
  const weeklyData = generateWeeklySparklineData(buckets, startDate, endDate)
  
  // Format dates as "Week of MMM DD" for better readability
  return weeklyData.map(item => {
    const weekDate = new Date(item.date)
    const formattedDate = weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    return {
      ...item,
      date: `Week of ${formattedDate}`
    }
  })
})

const testCasesAssigneeData = computed(() => {
  if (filteredTestCases.value.length === 0) {
    return []
  }
  
  const assigneeCounts = {}
  
  filteredTestCases.value.forEach(tc => {
    const assigneeId = tc.assignee || tc.assigned_to
    if (assigneeId) {
      const user = allUsers.value.find(u => u.id === assigneeId)
      const assigneeName = user ? user.name : assigneeId
      assigneeCounts[assigneeName] = (assigneeCounts[assigneeName] || 0) + 1
    } else {
      assigneeCounts[t('reports.filter.options.unassigned')] = (assigneeCounts[t('reports.filter.options.unassigned')] || 0) + 1
    }
  })
  
  return Object.entries(assigneeCounts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
})

const testCasesCaseTypeData = computed(() => {
  if (filteredTestCases.value.length === 0) {
    return []
  }
  
  const groups = groupAndCount(filteredTestCases.value, 'case_type')
  return Object.entries(groups).map(([label, value]) => ({
    label: label.charAt(0).toUpperCase() + label.slice(1).replace(/_/g, ' '),
    value
  }))
})

const testCasesTeamData = computed(() => {
  if (filteredTestCases.value.length === 0) {
    return []
  }
  
  const teamCounts = {}
  
  filteredTestCases.value.forEach(tc => {
    const project = allProjects.value.find(p => p.id === tc.project_id)
    if (project) {
      const team = allTeams.value.find(t => t.id === project.teamId)
      const teamName = team ? team.name : t('common.unknownTeam')
      teamCounts[teamName] = (teamCounts[teamName] || 0) + 1
    }
  })
  
  return Object.entries(teamCounts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
})

const testCasesProjectData = computed(() => {
  if (filteredTestCases.value.length === 0) {
    return []
  }
  
  const projectCounts = {}
  
  filteredTestCases.value.forEach(tc => {
    const project = allProjects.value.find(p => p.id === tc.project_id)
    const projectName = project ? project.name : t('common.unknownProject')
    projectCounts[projectName] = (projectCounts[projectName] || 0) + 1
  })
  
  return Object.entries(projectCounts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
})

const testCasesEstimationData = computed(() => {
  if (filteredTestCases.value.length === 0) {
    return []
  }
  
  // Define ranges with sort order
  const ranges = [
    { label: 'Not estimated', min: 0, max: 0, count: 0, order: 0 },
    { label: '< 2 mins', min: 0.01, max: 2, count: 0, order: 1 },
    { label: '2-5 mins', min: 2, max: 5, count: 0, order: 2 },
    { label: '5-10 mins', min: 5, max: 10, count: 0, order: 3 },
    { label: '10-20 mins', min: 10, max: 20, count: 0, order: 4 },
    { label: '> 20 mins', min: 20, max: Infinity, count: 0, order: 5 }
  ]
  
  filteredTestCases.value.forEach(tc => {
    const estimation = tc.estimated_time || tc.estimation || tc.estimated_hours || 0
    
    // Find the appropriate range
    for (const range of ranges) {
      if (estimation === 0 && range.min === 0 && range.max === 0) {
        range.count++
        break
      } else if (estimation > range.min && estimation <= range.max) {
        range.count++
        break
      }
    }
  })
  
  // Return ranges with counts > 0, sorted by order
  return ranges
    .filter(range => range.count > 0)
    .sort((a, b) => a.order - b.order)
    .map(range => ({ label: range.label, value: range.count }))
})

// Test Runs data
const testRunsStatusChartData = computed(() => {
  if (filteredTestRuns.value.length === 0) {
    return []
  }
  
  const groups = groupAndCount(filteredTestRuns.value, 'status')
  return Object.entries(groups).map(([label, value]) => ({
    label: label.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' '),
    value
  }))
})

const testRunsEnvironmentData = computed(() => {
  if (filteredTestRuns.value.length === 0) {
    return []
  }
  
  const groups = groupAndCount(filteredTestRuns.value, 'environment')
  return Object.entries(groups).map(([label, value]) => ({
    label: label.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' '),
    value
  }))
})

const testRunsCompletionData = computed(() => {
  if (filteredTestRuns.value.length === 0) {
    return []
  }
  
  const completed = filteredTestRuns.value.filter(tr => tr.status === TEST_RUN_STATUS_VALUES.COMPLETED).length
  const inProgress = filteredTestRuns.value.filter(tr => tr.status === TEST_RUN_STATUS_VALUES.IN_PROGRESS).length
  const notStarted = filteredTestRuns.value.filter(tr => tr.status === TEST_RUN_STATUS_VALUES.YET_TO_START).length
  const paused = filteredTestRuns.value.filter(tr => tr.status === TEST_RUN_STATUS_VALUES.PAUSED).length
  const cancelled = filteredTestRuns.value.filter(tr => tr.status === TEST_RUN_STATUS_VALUES.CANCELLED).length
  
  return [
    { label: t('reports.stats.completed'), value: completed },
    { label: t('reports.stats.inProgress'), value: inProgress },
    { label: t('reports.stats.notStarted'), value: notStarted },
    { label: t('reports.stats.paused'), value: paused },
    { label: t('reports.stats.cancelled'), value: cancelled }
  ].filter(item => item.value > 0)
})

const testRunsStatusData = computed(() => {
  if (filteredTestRuns.value.length === 0) {
    return []
  }
  
  // This would need status tracking over time - simplified version
  const buckets = bucketByDate(filteredTestRuns.value, 'created_at', 'day')
  const start = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  const end = new Date().toISOString().split('T')[0]
  
  return generateSparklineData(buckets, start, end).map(item => ({
    date: item.date,
    completed: Math.floor(item.count * 0.7),
    'in_progress': Math.floor(item.count * 0.2),
    pending: Math.floor(item.count * 0.1)
  }))
})

const testRunsTimelineData = computed(() => {
  if (filteredTestRuns.value.length === 0) {
    return []
  }
  
  const buckets = bucketByDate(filteredTestRuns.value, 'created_at', 'week')
  const endDate = new Date()
  const startDate = new Date(endDate.getTime() - (12 * 7 * 24 * 60 * 60 * 1000)) // 12 weeks
  
  // Helper function to generate weekly sparkline data
  const generateWeeklySparklineData = (buckets, start, end) => {
    const data = []
    const currentWeek = new Date(start)
    
    // Set to Monday of the start week
    currentWeek.setDate(currentWeek.getDate() - currentWeek.getDay() + (currentWeek.getDay() === 0 ? -6 : 1))
    
    // Generate data for each week
    while (currentWeek <= end) {
      const weekKey = currentWeek.toISOString().split('T')[0]
      data.push({
        date: weekKey,
        count: buckets[weekKey] ? buckets[weekKey].length : 0
      })
      
      // Move to next week
      currentWeek.setDate(currentWeek.getDate() + 7)
    }
    
    return data
  }
  
  const weeklyData = generateWeeklySparklineData(buckets, startDate, endDate)
  
  // Format dates as "Week of MMM DD" for better readability
  return weeklyData.map(item => {
    const weekDate = new Date(item.date)
    const formattedDate = weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    return {
      ...item,
      date: `Week of ${formattedDate}`
    }
  })
})

const testRunsAssigneeData = computed(() => {
  if (filteredTestRuns.value.length === 0) {
    return []
  }
  
  const assigneeCounts = {}
  
  filteredTestRuns.value.forEach(tr => {
    const assigneeId = tr.assigned_to
    if (assigneeId) {
      const user = allUsers.value.find(u => u.id === assigneeId)
      const assigneeName = user ? user.name : assigneeId
      assigneeCounts[assigneeName] = (assigneeCounts[assigneeName] || 0) + 1
    } else {
      assigneeCounts[t('reports.filter.options.unassigned')] = (assigneeCounts[t('reports.filter.options.unassigned')] || 0) + 1
    }
  })
  
  return Object.entries(assigneeCounts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
})

const testRunsCreatedByData = computed(() => {
  if (filteredTestRuns.value.length === 0) {
    return []
  }
  
  const createdByCounts = {}
  
  filteredTestRuns.value.forEach(tr => {
    const creatorId = tr.created_by
    if (creatorId) {
      const user = allUsers.value.find(u => u.id === creatorId)
      const creatorName = user ? user.name : creatorId
      createdByCounts[creatorName] = (createdByCounts[creatorName] || 0) + 1
    } else {
      createdByCounts[t('reports.filter.options.unassigned')] = (createdByCounts[t('reports.filter.options.unassigned')] || 0) + 1
    }
  })
  
  return Object.entries(createdByCounts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
})

const testRunsEstimationData = computed(() => {
  if (filteredTestRuns.value.length === 0) {
    return []
  }
  
  // Group test runs by estimation ranges (in minutes)
  const ranges = {
    [ESTIMATION_RANGE_LABELS.TEST_RUNS.RANGE_0_30]: 0,
    [ESTIMATION_RANGE_LABELS.TEST_RUNS.RANGE_31_60]: 0,
    [ESTIMATION_RANGE_LABELS.TEST_RUNS.RANGE_61_120]: 0,
    [ESTIMATION_RANGE_LABELS.TEST_RUNS.RANGE_121_240]: 0,
    [ESTIMATION_RANGE_LABELS.TEST_RUNS.RANGE_240_PLUS]: 0
  }
  
  filteredTestRuns.value.forEach(tr => {
    const estimation = tr.estimated_time || 0
    
    if (estimation === 0) {
      // Don't count unestimated test runs
    } else if (estimation <= 30) {
      ranges[ESTIMATION_RANGE_LABELS.TEST_RUNS.RANGE_0_30]++
    } else if (estimation <= 60) {
      ranges[ESTIMATION_RANGE_LABELS.TEST_RUNS.RANGE_31_60]++
    } else if (estimation <= 120) {
      ranges[ESTIMATION_RANGE_LABELS.TEST_RUNS.RANGE_61_120]++
    } else if (estimation <= 240) {
      ranges[ESTIMATION_RANGE_LABELS.TEST_RUNS.RANGE_121_240]++
    } else {
      ranges[ESTIMATION_RANGE_LABELS.TEST_RUNS.RANGE_240_PLUS]++
    }
  })
  
  return Object.entries(ranges)
    .map(([label, value]) => ({ label, value }))
    .filter(item => item.value > 0)
})

// Bugs data
const bugsStatusData = computed(() => {
  if (filteredBugs.value.length === 0) {
    return []
  }
  
  const groups = groupAndCount(filteredBugs.value, 'status')
  return Object.entries(groups).map(([label, value]) => ({
    label: label.charAt(0).toUpperCase() + label.slice(1).replace(/_/g, ' '),
    value
  }))
})

const bugsSeverityData = computed(() => {
  if (filteredBugs.value.length === 0) {
    return []
  }
  
  const groups = groupAndCount(filteredBugs.value, 'severity')
  return Object.entries(groups).map(([label, value]) => ({
    label: label.charAt(0).toUpperCase() + label.slice(1),
    value
  }))
})

const bugsPriorityData = computed(() => {
  if (filteredBugs.value.length === 0) {
    return []
  }
  
  const groups = groupAndCount(filteredBugs.value, 'priority')
  return Object.entries(groups).map(([label, value]) => ({
    label: label.charAt(0).toUpperCase() + label.slice(1),
    value
  }))
})

const bugsTypeData = computed(() => {
  if (filteredBugs.value.length === 0) {
    return []
  }
  
  const groups = groupAndCount(filteredBugs.value, 'type')
  return Object.entries(groups).map(([label, value]) => ({
    label: label === 'bug' ? t('reports.chart.bugTypeLabel') : label === 'feature' ? t('reports.chart.featureRequestLabel') : label.charAt(0).toUpperCase() + label.slice(1),
    value
  }))
})

const bugsEnvironmentData = computed(() => {
  if (filteredBugs.value.length === 0) {
    return []
  }
  
  const groups = groupAndCount(filteredBugs.value, 'environment')
  return Object.entries(groups).map(([label, value]) => ({
    label: label.replace(/_/g, ' ').split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' '),
    value
  }))
})

const bugsTeamData = computed(() => {
  if (filteredBugs.value.length === 0) {
    return []
  }
  
  const teamCounts = {}
  
  filteredBugs.value.forEach(bug => {
    const project = allProjects.value.find(p => p.id === bug.project_id)
    if (project) {
      const team = allTeams.value.find(t => t.id === project.teamId)
      const teamName = team ? team.name : t('common.unknownTeam')
      teamCounts[teamName] = (teamCounts[teamName] || 0) + 1
    }
  })
  
  return Object.entries(teamCounts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
})

const bugsProjectData = computed(() => {
  if (filteredBugs.value.length === 0) {
    return []
  }
  
  const projectCounts = {}
  
  filteredBugs.value.forEach(bug => {
    const project = allProjects.value.find(p => p.id === bug.project_id)
    const projectName = project ? project.name : t('common.unknownProject')
    projectCounts[projectName] = (projectCounts[projectName] || 0) + 1
  })
  
  return Object.entries(projectCounts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
})

const bugsAssigneeData = computed(() => {
  if (filteredBugs.value.length === 0) {
    return []
  }
  
  const assigneeCounts = {}
  
  filteredBugs.value.forEach(bug => {
    const assigneeId = bug.assigned_to
    if (assigneeId) {
      const user = allUsers.value.find(u => u.id === assigneeId)
      const assigneeName = user ? user.name : assigneeId
      assigneeCounts[assigneeName] = (assigneeCounts[assigneeName] || 0) + 1
    } else {
      assigneeCounts[t('reports.filter.options.unassigned')] = (assigneeCounts[t('reports.filter.options.unassigned')] || 0) + 1
    }
  })
  
  return Object.entries(assigneeCounts)
    .map(([label, value]) => ({ label, value }))
    .sort((a, b) => b.value - a.value)
})

const bugsTimelineData = computed(() => {
  if (filteredBugs.value.length === 0) {
    return []
  }
  
  const buckets = bucketByDate(filteredBugs.value, 'created_at', 'week')
  const endDate = new Date()
  const startDate = new Date(endDate.getTime() - (12 * 7 * 24 * 60 * 60 * 1000)) // 12 weeks
  
  // Helper function to generate weekly sparkline data
  const generateWeeklySparklineData = (buckets, start, end) => {
    const data = []
    const currentWeek = new Date(start)
    
    // Set to Monday of the start week
    currentWeek.setDate(currentWeek.getDate() - currentWeek.getDay() + (currentWeek.getDay() === 0 ? -6 : 1))
    
    // Generate data for each week
    while (currentWeek <= end) {
      const weekKey = currentWeek.toISOString().split('T')[0]
      data.push({
        date: weekKey,
        count: buckets[weekKey] ? buckets[weekKey].length : 0
      })
      
      // Move to next week
      currentWeek.setDate(currentWeek.getDate() + 7)
    }
    
    return data
  }
  
  const weeklyData = generateWeeklySparklineData(buckets, startDate, endDate)
  
  // Format dates as "Week of MMM DD" for better readability
  return weeklyData.map(item => {
    const weekDate = new Date(item.date)
    const formattedDate = weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    return {
      ...item,
      date: `Week of ${formattedDate}`
    }
  })
})

const bugsCriticalData = computed(() => {
  if (filteredBugs.value.length === 0) {
    return []
  }
  
  const critical = filteredBugs.value.filter(b => b.priority === 'critical' || b.severity === 'critical').length
  const high = filteredBugs.value.filter(b => (b.priority === 'high' || b.severity === 'high') && b.priority !== 'critical' && b.severity !== 'critical').length
  
  const data = []
  if (critical > 0) data.push({ label: t('reports.stats.critical'), value: critical })
  if (high > 0) data.push({ label: t('reports.stats.high'), value: high })
  
  return data.length > 0 ? data : []
})

// Stats Grid Data for each section
const overviewStats = computed(() => [
  {
    label: t('reports.stats.teams'),
    value: allTeams.value.length,
    subtext: t('reports.stats.active'),
    iconPath: getIconPath('team'),
    colors: STAT_CARD_COLORS.TEAMS
  },
  {
    label: t('reports.stats.projects'),
    value: allProjects.value.length,
    subtext: t('reports.stats.total'),
    iconPath: getIconPath('project'),
    colors: STAT_CARD_COLORS.PROJECTS
  },
  {
    label: t('reports.stats.features'),
    value: mainFeaturesCount.value,
    subtext: t('reports.stats.parent'),
    iconPath: getIconPath('feature'),
    colors: STAT_CARD_COLORS.FEATURES
  },
  {
    label: t('reports.stats.testCases'),
    value: allTestCases.value.length,
    subtext: t('reports.stats.active'),
    iconPath: getIconPath('testCase'),
    colors: STAT_CARD_COLORS.TEST_CASES
  },
  {
    label: t('reports.stats.testRuns'),
    value: allTestRuns.value.length,
    subtext: t('reports.stats.total'),
    iconPath: getIconPath('testRun'),
    colors: STAT_CARD_COLORS.TEST_RUNS
  },
  {
    label: t('reports.stats.bugs'),
    value: allBugs.value.length,
    subtext: t('reports.stats.reported'),
    iconPath: getIconPath('bug'),
    colors: STAT_CARD_COLORS.BUGS
  }
])

// Quality Metrics Stats - Key health indicators for StatsGrid
const qualityMetricsStats = computed(() => {
  // 1. Test Coverage: Test Cases / Features
  const testCoverageValue = mainFeaturesCount.value > 0 
    ? (allTestCases.value.length / mainFeaturesCount.value).toFixed(2)
    : '0.00'
  
  // 2. Automation Readiness: Automated Tests / Total Tests × 100
  const automatedTests = allTestCases.value.filter(tc => 
    tc.automation_status === AUTOMATION_STATUS_VALUES.AUTOMATED
  ).length
  const automationReadinessValue = allTestCases.value.length > 0 
    ? Math.round((automatedTests / allTestCases.value.length) * 100)
    : 0
  
  // 3. Bug-to-Feature Ratio: Bugs / Features
  const bugToFeatureRatioValue = mainFeaturesCount.value > 0 
    ? (allBugs.value.length / mainFeaturesCount.value).toFixed(2)
    : '0.00'
  
  // 4. Test Run Completion Rate: Completed Runs / Total Runs × 100
  const completedRuns = allTestRuns.value.filter(tr => 
    tr.status === TEST_RUN_STATUS_VALUES.COMPLETED
  ).length
  const testRunCompletionValue = allTestRuns.value.length > 0 
    ? Math.round((completedRuns / allTestRuns.value.length) * 100)
    : 0
  
  // Calculate 30-day trends
  const thirtyDaysAgo = new Date()
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30)
  
  // Filter old data (excluding last 30 days)
  const oldTestCases = allTestCases.value.filter(tc => new Date(tc.created_at) < thirtyDaysAgo)
  const oldFeatures = allFeatures.value.filter(f => 
    (!f.parent_id || f.parent_id === null || f.parent_id === '') &&
    new Date(f.created_at) < thirtyDaysAgo
  )
  const oldBugs = allBugs.value.filter(b => new Date(b.created_at) < thirtyDaysAgo)
  const oldTestRuns = allTestRuns.value.filter(tr => new Date(tr.created_at) < thirtyDaysAgo)
  
  // Calculate old metric values
  const oldTestCoverage = oldFeatures.length > 0 ? oldTestCases.length / oldFeatures.length : 0
  const oldAutomatedTests = oldTestCases.filter(tc => tc.automation_status === AUTOMATION_STATUS_VALUES.AUTOMATED).length
  const oldAutomationRate = oldTestCases.length > 0 ? (oldAutomatedTests / oldTestCases.length) * 100 : 0
  const oldBugRatio = oldFeatures.length > 0 ? oldBugs.length / oldFeatures.length : 0
  const oldCompletedRuns = oldTestRuns.filter(tr => tr.status === TEST_RUN_STATUS_VALUES.COMPLETED).length
  const oldCompletionRate = oldTestRuns.length > 0 ? (oldCompletedRuns / oldTestRuns.length) * 100 : 0
  
  // Calculate trends
  const currentTestCoverage = parseFloat(testCoverageValue)
  const currentAutomationRate = automationReadinessValue
  const currentBugRatio = parseFloat(bugToFeatureRatioValue)
  const currentCompletionRate = testRunCompletionValue
  
  const testCoverageTrend = oldTestCoverage > 0 ? Math.round(((currentTestCoverage - oldTestCoverage) / oldTestCoverage) * 100) : 0
  const automationTrend = oldAutomationRate > 0 ? Math.round(((currentAutomationRate - oldAutomationRate) / oldAutomationRate) * 100) : 0
  const bugRatioTrend = oldBugRatio > 0 ? Math.round(((currentBugRatio - oldBugRatio) / oldBugRatio) * 100) : 0
  const completionTrend = oldCompletionRate > 0 ? Math.round(((currentCompletionRate - oldCompletionRate) / oldCompletionRate) * 100) : 0
  
  // Helper to format trend text
  const formatTrend = (trend) => {
    if (trend === 0) return t('reports.overview.metrics.trendStable')
    const sign = trend > 0 ? '+' : ''
    return `${sign}${trend}% (${t('reports.overview.metrics.trend30Days')})`
  }
  
  // Helper to get colors based on metric type and value
  const getColors = (value, type) => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value
    
    if (numValue === 0 || !numValue) {
      return STATUS_COLORS.NOT_STARTED // Gray for zero/no data
    }
    
    switch (type) {
      case 'testCoverage':
        if (numValue >= 3) return STATUS_COLORS.COMPLETED // Green - Excellent coverage
        if (numValue >= 2) return STATUS_COLORS.SEMI_AUTOMATED // Cyan - Good coverage
        return STATUS_COLORS.PAUSED // Indigo - Needs improvement
        
      case 'automation':
        if (numValue >= 70) return STATUS_COLORS.AUTOMATED // Emerald - Highly automated
        if (numValue >= 40) return STATUS_COLORS.TESTING // Purple - Partial automation
        return STATUS_COLORS.MANUAL // Violet - Manual focus needed
        
      case 'bugRatio':
        if (numValue <= 0.5) return STATUS_COLORS.COMPLETED // Green - Excellent (lower is better)
        if (numValue <= 1.5) return STATUS_COLORS.REOPENED // Amber - Monitor closely
        return STATUS_COLORS.IN_REVIEW // Orange - Needs attention
        
      case 'completion':
        if (numValue >= 80) return STATUS_COLORS.COMPLETED // Green - High completion
        if (numValue >= 50) return STATUS_COLORS.IN_PROGRESS // Yellow - Moderate completion
        return STATUS_COLORS.BLOCKED // Fuchsia - Action required
        
      default:
        return STATUS_COLORS.TOTAL
    }
  }
  
  return [
    {
      label: t('reports.overview.metrics.testCoverage'),
      value: testCoverageValue,
      subtext: formatTrend(testCoverageTrend),
      iconPath: getIconPath('testCase'),
      colors: getColors(testCoverageValue, 'testCoverage')
    },
    {
      label: t('reports.overview.metrics.automationReadiness'),
      value: `${automationReadinessValue}%`,
      subtext: formatTrend(automationTrend),
      iconPath: STATUS_COLORS.AUTOMATED.icon,
      colors: getColors(automationReadinessValue, 'automation')
    },
    {
      label: t('reports.overview.metrics.bugToFeatureRatio'),
      value: bugToFeatureRatioValue,
      subtext: formatTrend(bugRatioTrend),
      iconPath: getIconPath('bug'),
      colors: getColors(bugToFeatureRatioValue, 'bugRatio')
    },
    {
      label: t('reports.overview.metrics.testRunCompletion'),
      value: `${testRunCompletionValue}%`,
      subtext: formatTrend(completionTrend),
      iconPath: getIconPath('testRun'),
      colors: getColors(testRunCompletionValue, 'completion')
    }
  ]
})

// System Activity Timeline - Stacked Area Chart showing all entity growth
// Shows weekly aggregated data for the last 12 weeks (approximately 3 months)
const systemActivityTimelineData = computed(() => {
  // Get date range for last 12 weeks (84 days)
  const endDate = new Date()
  const startDate = new Date(endDate.getTime() - 84 * 24 * 60 * 60 * 1000)
  
  // Bucket each entity type by week
  const teamsBuckets = bucketByDate(allTeams.value, 'created_at', 'week')
  const projectsBuckets = bucketByDate(allProjects.value, 'created_at', 'week')
  const featuresBuckets = bucketByDate(
    allFeatures.value.filter(f => !f.parent_id || f.parent_id === null || f.parent_id === ''), 
    'created_at', 
    'week'
  )
  const testCasesBuckets = bucketByDate(allTestCases.value, 'created_at', 'week')
  const testRunsBuckets = bucketByDate(allTestRuns.value, 'created_at', 'week')
  const bugsBuckets = bucketByDate(allBugs.value, 'created_at', 'week')
  
  // Helper function to generate weekly sparkline data
  const generateWeeklySparklineData = (buckets, start, end) => {
    const data = []
    const currentWeek = new Date(start)
    
    // Set to Monday of the start week
    currentWeek.setDate(currentWeek.getDate() - currentWeek.getDay() + (currentWeek.getDay() === 0 ? -6 : 1))
    
    // Generate data for each week
    while (currentWeek <= end) {
      const weekKey = currentWeek.toISOString().split('T')[0]
      data.push({
        date: weekKey,
        count: buckets[weekKey] ? buckets[weekKey].length : 0
      })
      
      // Move to next week
      currentWeek.setDate(currentWeek.getDate() + 7)
    }
    
    return data
  }
  
  // Generate weekly sparkline data for each entity
  const teamsData = calculateCumulative(generateWeeklySparklineData(teamsBuckets, startDate, endDate))
  const projectsData = calculateCumulative(generateWeeklySparklineData(projectsBuckets, startDate, endDate))
  const featuresData = calculateCumulative(generateWeeklySparklineData(featuresBuckets, startDate, endDate))
  const testCasesData = calculateCumulative(generateWeeklySparklineData(testCasesBuckets, startDate, endDate))
  const testRunsData = calculateCumulative(generateWeeklySparklineData(testRunsBuckets, startDate, endDate))
  const bugsData = calculateCumulative(generateWeeklySparklineData(bugsBuckets, startDate, endDate))
  
  // Combine all data into stacked format with formatted week labels
  return teamsData.map((item, index) => {
    // Format date as "Week of MMM DD" for better readability
    const weekDate = new Date(item.date)
    const formattedDate = weekDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    
    return {
      date: `Week of ${formattedDate}`,
      [t('reports.overview.activityLabels.teams')]: item.count,
      [t('reports.overview.activityLabels.projects')]: projectsData[index]?.count || 0,
      [t('reports.overview.activityLabels.features')]: featuresData[index]?.count || 0,
      [t('reports.overview.activityLabels.testCases')]: testCasesData[index]?.count || 0,
      [t('reports.overview.activityLabels.testRuns')]: testRunsData[index]?.count || 0,
      [t('reports.overview.activityLabels.bugs')]: bugsData[index]?.count || 0
    }
  })
})

const teamsAndProjectsStats = computed(() => [
  {
    label: t('reports.stats.teams'),
    value: filteredTeams.value.length,
    subtext: t('reports.stats.allTeams'),
    iconPath: getIconPath('team'),
    colors: STAT_CARD_COLORS.TEAMS
  },
  {
    label: t('reports.stats.projects'),
    value: filteredProjects.value.length,
    subtext: t('reports.stats.allProjects'),
    iconPath: getIconPath('project'),
    colors: STAT_CARD_COLORS.PROJECTS
  },
  {
    label: t('reports.stats.features'),
    value: mainFeaturesCount.value,
    subtext: t('reports.stats.combined'),
    iconPath: getIconPath('feature'),
    colors: STAT_CARD_COLORS.FEATURES
  },
  {
    label: t('reports.stats.testCases'),
    value: allTestCases.value.length,
    subtext: t('reports.stats.combined'),
    iconPath: getIconPath('testCase'),
    colors: STAT_CARD_COLORS.TEST_CASES
  }
])

const featuresStats = computed(() => [
  {
    label: t('reports.stats.total'),
    value: filteredFeatures.value.length,
    subtext: t('reports.stats.all'),
    iconPath: STATUS_COLORS.TOTAL.icon,
    colors: STATUS_COLORS.TOTAL
  },
  {
    label: t('reports.stats.open'),
    value: filteredFeatures.value.filter(f => f.status === 'open').length,
    subtext: t('reports.stats.pending'),
    iconPath: STATUS_COLORS.OPEN.icon,
    colors: STATUS_COLORS.OPEN
  },
  {
    label: t('reports.stats.inProgress'),
    value: filteredFeatures.value.filter(f => f.status === 'in-progress').length,
    subtext: t('reports.stats.working'),
    iconPath: STATUS_COLORS.IN_PROGRESS.icon,
    colors: STATUS_COLORS.IN_PROGRESS
  },
  {
    label: t('reports.stats.inReview'),
    value: filteredFeatures.value.filter(f => f.status === 'in-review').length,
    subtext: t('reports.stats.reviewing'),
    iconPath: STATUS_COLORS.IN_REVIEW.icon,
    colors: STATUS_COLORS.IN_REVIEW
  },
  {
    label: t('reports.stats.testing'),
    value: filteredFeatures.value.filter(f => f.status === 'testing').length,
    subtext: t('reports.stats.qa'),
    iconPath: STATUS_COLORS.TESTING.icon,
    colors: STATUS_COLORS.TESTING
  },
  {
    label: t('reports.stats.closed'),
    value: filteredFeatures.value.filter(f => f.status === 'closed').length,
    subtext: t('reports.stats.done'),
    iconPath: STATUS_COLORS.COMPLETED.icon,
    colors: STATUS_COLORS.COMPLETED
  }
])

const testCasesStats = computed(() => [
  {
    label: t('reports.stats.total'),
    value: filteredTestCases.value.length,
    subtext: t('reports.stats.all'),
    iconPath: STATUS_COLORS.TOTAL.icon,
    colors: STATUS_COLORS.TOTAL
  },
  {
    label: t('reports.stats.automated'),
    value: filteredTestCases.value.filter(tc => tc.automation_status === AUTOMATION_STATUS_VALUES.AUTOMATED).length,
    subtext: t('reports.stats.fullAuto'),
    iconPath: STATUS_COLORS.AUTOMATED.icon,
    colors: STATUS_COLORS.AUTOMATED
  },
  {
    label: t('reports.stats.semiAutomated'),
    value: filteredTestCases.value.filter(tc => tc.automation_status === AUTOMATION_STATUS_VALUES.SEMI_AUTOMATED).length,
    subtext: t('reports.stats.partial'),
    iconPath: STATUS_COLORS.SEMI_AUTOMATED.icon,
    colors: STATUS_COLORS.SEMI_AUTOMATED
  },
  {
    label: t('reports.stats.manual'),
    value: filteredTestCases.value.filter(tc => tc.automation_status === AUTOMATION_STATUS_VALUES.MANUAL).length,
    subtext: t('reports.stats.byHand'),
    iconPath: STATUS_COLORS.MANUAL.icon,
    colors: STATUS_COLORS.MANUAL
  }
])

const testRunsStats = computed(() => [
  {
    label: t('reports.stats.total'),
    value: filteredTestRuns.value.length,
    subtext: t('reports.stats.all'),
    iconPath: STATUS_COLORS.TOTAL.icon,
    colors: STATUS_COLORS.TOTAL
  },
  {
    label: t('reports.stats.yetToStart'),
    value: filteredTestRuns.value.filter(tr => tr.status === TEST_RUN_STATUS_VALUES.YET_TO_START).length,
    subtext: t('reports.stats.queued'),
    iconPath: STATUS_COLORS.DRAFT.icon,
    colors: STATUS_COLORS.DRAFT
  },
  {
    label: t('reports.stats.inProgress'),
    value: filteredTestRuns.value.filter(tr => tr.status === TEST_RUN_STATUS_VALUES.IN_PROGRESS).length,
    subtext: t('reports.stats.running'),
    iconPath: STATUS_COLORS.IN_PROGRESS.icon,
    colors: STATUS_COLORS.IN_PROGRESS
  },
  {
    label: t('reports.stats.paused'),
    value: filteredTestRuns.value.filter(tr => tr.status === TEST_RUN_STATUS_VALUES.PAUSED).length,
    subtext: t('reports.stats.onHold'),
    iconPath: STATUS_COLORS.PAUSED.icon,
    colors: STATUS_COLORS.PAUSED
  },
  {
    label: t('reports.stats.completed'),
    value: filteredTestRuns.value.filter(tr => tr.status === TEST_RUN_STATUS_VALUES.COMPLETED).length,
    subtext: t('reports.stats.finished'),
    iconPath: STATUS_COLORS.COMPLETED.icon,
    colors: STATUS_COLORS.COMPLETED
  },
  {
    label: t('reports.stats.cancelled'),
    value: filteredTestRuns.value.filter(tr => tr.status === TEST_RUN_STATUS_VALUES.CANCELLED).length,
    subtext: t('reports.stats.stopped'),
    iconPath: STATUS_COLORS.CANCELLED.icon,
    colors: STATUS_COLORS.CANCELLED
  }
])

const bugsStats = computed(() => [
  {
    label: t('reports.stats.total'),
    value: filteredBugs.value.length,
    subtext: t('reports.stats.all'),
    iconPath: STATUS_COLORS.TOTAL.icon,
    colors: STATUS_COLORS.TOTAL
  },
  {
    label: t('reports.stats.open'),
    value: filteredBugs.value.filter(b => b.status === 'open').length,
    subtext: t('reports.stats.active'),
    iconPath: STATUS_COLORS.OPEN.icon,
    colors: STATUS_COLORS.OPEN
  },
  {
    label: t('reports.stats.inProgress'),
    value: filteredBugs.value.filter(b => b.status === 'in_progress').length,
    subtext: t('reports.stats.fixing'),
    iconPath: STATUS_COLORS.IN_PROGRESS.icon,
    colors: STATUS_COLORS.IN_PROGRESS
  },
  {
    label: t('reports.stats.resolved'),
    value: filteredBugs.value.filter(b => b.status === 'resolved').length,
    subtext: t('reports.stats.fixed'),
    iconPath: STATUS_COLORS.RESOLVED.icon,
    colors: STATUS_COLORS.RESOLVED
  },
  {
    label: t('reports.stats.closed'),
    value: filteredBugs.value.filter(b => b.status === 'closed').length,
    subtext: t('reports.stats.done'),
    iconPath: STATUS_COLORS.COMPLETED.icon,
    colors: STATUS_COLORS.COMPLETED
  },
  {
    label: t('reports.stats.reopened'),
    value: filteredBugs.value.filter(b => b.status === 'reopened').length,
    subtext: t('reports.stats.recurred'),
    iconPath: STATUS_COLORS.REOPENED.icon,
    colors: STATUS_COLORS.REOPENED
  },
  {
    label: t('reports.stats.blocked'),
    value: filteredBugs.value.filter(b => b.status === 'blocked').length,
    subtext: t('reports.stats.stuck'),
    iconPath: STATUS_COLORS.BLOCKED.icon,
    colors: STATUS_COLORS.BLOCKED
  }
])

// Methods
const loadAllData = async () => {
  // Store current scroll position before loading
  const scrollPosition = window.scrollY
  
  try {
    // Disable sticky behavior during refresh to prevent unwanted sticky state
    disableSticky()
    
    loading.value = true

    // Import database service to check connection
    const { databaseService } = await import('@/database/index')
    const { getDatabaseConfig } = await import('@/database/config')
    const dbConfig = getDatabaseConfig()


    const [
      { teamService },
      { projectService },
      { featureService },
      { testCaseService },
      { testRunService },
      { bugService },
      { userService }
    ] = await Promise.all([
      import('@/database/services'),
      import('@/database/services'),
      import('@/database/services'),
      import('@/database/services'),
      import('@/database/services'),
      import('@/database/services'),
      import('@/database/services')
    ])

    const [teams, projects, features, testCases, testRuns, bugs, users] = await Promise.all([
      teamService.getAll(),
      projectService.getAll(),
      featureService.getAll(),
      testCaseService.getAll(),
      testRunService.getAll(),
      bugService.getAll(),
      userService.getAll()
    ])

    // Use the computed isSuperAdmin property
    const currentUserId = authStore.user?.uid

    // Filter teams based on visibility - only show teams the user has access to
    const visibleTeams = teams.filter(team => {
      // Skip deleted teams (check both deleted_at and is_deleted for backwards compatibility)
      if (team.is_deleted === true || team.deleted_at) return false
      
      // Super Admin can see all teams
      if (isSuperAdmin.value) return true
      
      // Private teams are visible only to Super Admin and team members
      if (team.visibility === 'private') {
        return team.members && team.members.includes(currentUserId)
      }
      
      // Public teams are visible to everyone
      return true
    })
    
    // Filter all teams (including deleted) based on visibility only
    const allVisibleTeams = teams.filter(team => {
      // Super Admin can see all teams (including deleted)
      if (isSuperAdmin.value) return true
      
      // Private teams are visible only to Super Admin and team members
      if (team.visibility === 'private') {
        return team.members && team.members.includes(currentUserId)
      }
      
      // Public teams are visible to everyone
      return true
    })
    
    // Filter projects based on visibility - only show projects the user has access to
    const visibleProjects = projects.filter(project => {
      // Skip deleted projects (check both deleted_at and is_deleted for backwards compatibility)
      if (project.is_deleted === true || project.deleted_at) return false
      
      // Super Admin can see all projects
      if (isSuperAdmin.value) return true
      
      // Private projects are visible only to Super Admin and project members
      if (project.visibility === 'private') {
        return project.members && project.members.includes(currentUserId)
      }
      
      // Public projects are visible to everyone
      return true
    })
    
    // Filter all projects (including deleted) based on visibility only
    const allVisibleProjects = projects.filter(project => {
      // Super Admin can see all projects (including deleted)
      if (isSuperAdmin.value) return true
      
      // Private projects are visible only to Super Admin and project members
      if (project.visibility === 'private') {
        return project.members && project.members.includes(currentUserId)
      }
      
      // Public projects are visible to everyone
      return true
    })

    allTeams.value = visibleTeams
    allProjects.value = visibleProjects
    allTeamsIncludingDeleted.value = allVisibleTeams
    allProjectsIncludingDeleted.value = allVisibleProjects
    
    // Get IDs of visible projects for filtering related data
    const visibleProjectIds = visibleProjects.map(p => p.id)
    
    
    // Get IDs of deleted projects to exclude their data
    const deletedProjectIds = projects
      .filter(p => p.is_deleted === true || p.deleted_at)
      .map(p => p.id)
    

    // Filter features, test cases, test runs, and bugs to only show NON-DELETED data
    // IMPORTANT: 
    // - Exclude items that are explicitly deleted (is_deleted === true or deleted_at exists)
    // - Exclude items from deleted projects
    // - INCLUDE items from non-existent projects (orphaned data) to show all data in reports
    
    // Store all features (including deleted) for toggle functionality
    allFeaturesIncludingDeleted.value = features.filter(f => {
      // Exclude items from deleted projects (even if includeDeleted is on)
      if (deletedProjectIds.includes(f.project_id)) return false
      return true
    })
    
    // Store only non-deleted features
    allFeatures.value = features.filter(f => {
      // Skip if explicitly deleted
      const isDeleted = f.is_deleted === true || (f.deleted_at !== null && f.deleted_at !== undefined)
      if (isDeleted) return false
      
      // Skip if belongs to a deleted project
      if (deletedProjectIds.includes(f.project_id)) return false
      
      return true
    })
    
    // Store all test cases (including deleted) for toggle functionality
    allTestCasesIncludingDeleted.value = testCases.filter(tc => {
      if (deletedProjectIds.includes(tc.project_id)) return false
      return true
    })
    
    allTestCases.value = testCases.filter(tc => {
      // Skip if explicitly deleted
      const isDeleted = tc.is_deleted === true || (tc.deleted_at !== null && tc.deleted_at !== undefined)
      if (isDeleted) return false
      
      // Skip if belongs to a deleted project
      if (deletedProjectIds.includes(tc.project_id)) return false
      
      return true
    })
    
    // Store all test runs (including deleted) for toggle functionality
    allTestRunsIncludingDeleted.value = testRuns.filter(tr => {
      if (deletedProjectIds.includes(tr.project_id)) return false
      return true
    })
    
    allTestRuns.value = testRuns.filter(tr => {
      // Skip if explicitly deleted
      const isDeleted = tr.is_deleted === true || (tr.deleted_at !== null && tr.deleted_at !== undefined)
      if (isDeleted) return false
      
      // Skip if belongs to a deleted project
      if (deletedProjectIds.includes(tr.project_id)) return false
      
      return true
    })
    
    // Store all bugs (including deleted) for toggle functionality
    allBugsIncludingDeleted.value = bugs.filter(b => {
      if (deletedProjectIds.includes(b.project_id)) return false
      return true
    })
    
    allBugs.value = bugs.filter(b => {
      // Skip if explicitly deleted
      const isDeleted = b.is_deleted === true || (b.deleted_at !== null && b.deleted_at !== undefined)
      if (isDeleted) return false
      
      // Skip if belongs to a deleted project
      if (deletedProjectIds.includes(b.project_id)) return false
      
      return true
    })
    
    allUsers.value = users || []

  } catch (error) {
    console.error('Failed to load reports data:', error)
    
    // Set empty arrays so the page still renders
    allTeams.value = []
    allProjects.value = []
    allFeatures.value = []
    allTestCases.value = []
    allTestRuns.value = []
    allBugs.value = []
    allUsers.value = []
    allTeamsIncludingDeleted.value = []
    allProjectsIncludingDeleted.value = []
    allFeaturesIncludingDeleted.value = []
    allTestCasesIncludingDeleted.value = []
    allTestRunsIncludingDeleted.value = []
    allBugsIncludingDeleted.value = []
    
    showError(t('reports.loadingFailed') + ': ' + (error.message || 'Unknown error'))
  } finally {
    loading.value = false
    
    // Restore scroll position after content loads
    // Use nextTick to ensure DOM has updated
    await nextTick()
    window.scrollTo({
      top: scrollPosition,
      behavior: 'instant' // Use 'instant' instead of 'smooth' to avoid visual jump
    })
    
    // Re-enable sticky behavior after DOM settles
    // The enable() function will automatically check and set correct sticky state
    setTimeout(() => {
      enableSticky()
    }, 100)
  }
}

// Accessibility file upload handlers
const handleFileSelect = (event) => {
  const files = Array.from(event.target.files || [])
  addFiles(files)
}

const handleDrop = (event) => {
  isDragging.value = false
  const files = Array.from(event.dataTransfer.files || [])
  addFiles(files)
}

const handleDragOver = (event) => {
  event.preventDefault()
  isDragging.value = true
}

const handleDragLeave = () => {
  isDragging.value = false
}

const addFiles = (files) => {
  const validFiles = files.filter(file => {
    // Validate file type
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ]
    const validExtensions = ['.csv', '.xls', '.xlsx']
    const hasValidType = validTypes.includes(file.type)
    const hasValidExtension = validExtensions.some(ext => file.name.toLowerCase().endsWith(ext))
    
    if (!hasValidType && !hasValidExtension) {
      showError(`Invalid file type: ${file.name}. Only CSV and Excel files are allowed.`)
      return false
    }
    
    // Validate file size (max 50MB)
    const maxSize = 50 * 1024 * 1024 // 50MB
    if (file.size > maxSize) {
      showError(`File too large: ${file.name}. Maximum size is 50MB.`)
      return false
    }
    
    return true
  })
  
  // Add unique files only
  validFiles.forEach(file => {
    const exists = selectedFiles.value.some(f => f.name === file.name && f.size === file.size)
    if (!exists) {
      selectedFiles.value.push(file)
    }
  })
}

const removeFile = (index) => {
  selectedFiles.value.splice(index, 1)
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
}

const closeAccessibilityModal = () => {
  showAccessibilityImportModal.value = false
  selectedFiles.value = []
  reportDate.value = new Date()
  uploadProgress.value = 0
  isUploading.value = false
  isValidating.value = false
  validationResult.value = null
  showValidationStep.value = false
}

const handleValidateReport = async () => {
  if (selectedFiles.value.length === 0) {
    showError('Please select at least one file to validate')
    return
  }
  
  try {
    isValidating.value = true
    
    // Import accessibility service
    const { accessibilityService } = await import('@/database/services')
    
    // Validate files
    const result = await accessibilityService.validateReport(selectedFiles.value)
    
    validationResult.value = result
    showValidationStep.value = true
    
  } catch (error) {
    console.error('Failed to validate report:', error)
    showError(error.message || 'Failed to validate accessibility report')
  } finally {
    isValidating.value = false
  }
}

const handleImportReport = async () => {
  if (selectedFiles.value.length === 0) {
    showError('Please select at least one file to import')
    return
  }
  
  if (!reportDate.value) {
    showError('Please select a report date')
    return
  }
  
  try {
    isUploading.value = true
    
    // Import accessibility service
    const { accessibilityService } = await import('@/database/services')
    
    // Convert reportDate to Date object if it's a timestamp
    const dateToUse = reportDate.value instanceof Date 
      ? reportDate.value 
      : new Date(reportDate.value)
    
    // Upload files
    const report = await accessibilityService.uploadReport(selectedFiles.value, dateToUse)
    
    const totalIssues = report.summary.total_issues
    const rowCount = report.file_info.row_count
    
    let message = `Successfully imported ${rowCount} valid rows from ${selectedFiles.value.length} file(s)`
    
    // Show deduplication info if there's a difference
    if (totalIssues < rowCount) {
      const duplicates = rowCount - totalIssues
      message += `. Found ${totalIssues} unique issues (${duplicates} duplicates removed)`
    }
    
    showSuccess(message)
    
    // Close modal and reload data
    closeAccessibilityModal()
    await loadAccessibilityReports()
    
  } catch (error) {
    console.error('Failed to upload report:', error)
    showError(error.message || 'Failed to upload accessibility report')
  } finally {
    isUploading.value = false
  }
}

// Load accessibility reports
const loadAccessibilityReports = async () => {
  try {
    accessibilityLoading.value = true
    const { accessibilityService } = await import('@/database/services')
    accessibilityReports.value = await accessibilityService.getAllReports()
    
    // Load filter options, issues, and test summary (statistics computed client-side)
    if (accessibilityReports.value.length > 0) {
      await Promise.all([
        loadAccessibilityFilterOptions(),
        loadAccessibilityIssues(),
        loadAccessibilityTestSummary(),
        loadAccessibilityAllIssuesForCharts() // Load ALL issues for client-side filtering
      ])
      
      // Load trends after DOM is updated
      await nextTick()
      setTimeout(() => {
        loadAccessibilityTrends()
      }, 150)
    }
  } catch (error) {
    console.error('Failed to load accessibility reports:', error)
    showError('Failed to load accessibility reports')
  } finally {
    accessibilityLoading.value = false
  }
}

// Load accessibility statistics (for charts)
const loadAccessibilityStatistics = async (useSummaryFilters = false) => {
  if (!accessibilityReports.value || accessibilityReports.value.length === 0) {
    return
  }
  
  try {
    const { accessibilityService } = await import('@/database/services')
    
    let reportDates = []
    
    // If using summary filters and in overview/summary view, apply date range filter
    if (useSummaryFilters && (accessibilityView.value === ACCESSIBILITY_TABS.OVERVIEW || accessibilityView.value === ACCESSIBILITY_TABS.SUMMARY)) {
      const dateRange = accessibilitySummaryFilters.value.dateRange
      const now = new Date()
      
      if (dateRange === 'all') {
        // Use all report dates
        reportDates = accessibilityReports.value.map(r => new Date(r.report_date))
      } else if (dateRange === 'latest') {
        // Use only the latest report
        reportDates = [new Date(accessibilityReports.value[0].report_date)]
      } else if (dateRange === 'last_7_days') {
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        reportDates = accessibilityReports.value
          .filter(r => new Date(r.report_date) >= sevenDaysAgo)
          .map(r => new Date(r.report_date))
      } else if (dateRange === 'last_30_days') {
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        reportDates = accessibilityReports.value
          .filter(r => new Date(r.report_date) >= thirtyDaysAgo)
          .map(r => new Date(r.report_date))
      } else if (dateRange === 'custom' && accessibilitySummaryFilters.value.customStartDate && accessibilitySummaryFilters.value.customEndDate) {
        const startDate = new Date(accessibilitySummaryFilters.value.customStartDate)
        const endDate = new Date(accessibilitySummaryFilters.value.customEndDate)
        reportDates = accessibilityReports.value
          .filter(r => {
            const reportDate = new Date(r.report_date)
            return reportDate >= startDate && reportDate <= endDate
          })
          .map(r => new Date(r.report_date))
      } else {
        // Fallback to latest report
        reportDates = [new Date(accessibilityReports.value[0].report_date)]
      }
    } else {
      // Default behavior: use latest report
      reportDates = [new Date(accessibilityReports.value[0].report_date)]
    }
    
    if (reportDates.length === 0) {
      console.warn('No report dates available after filtering')
      return
    }
    
    // Build filters object (without report_dates - those are passed separately)
    const filters = {}
    
    // Apply Overview/Summary view filters
    if (useSummaryFilters && (accessibilityView.value === ACCESSIBILITY_TABS.OVERVIEW || accessibilityView.value === ACCESSIBILITY_TABS.SUMMARY)) {
      if (accessibilitySummaryFilters.value.impact.length > 0) {
        filters.impacts = accessibilitySummaryFilters.value.impact
      }
      if (accessibilitySummaryFilters.value.status.length > 0) {
        filters.statuses = accessibilitySummaryFilters.value.status
      }
      if (accessibilitySummaryFilters.value.assignedTo.length > 0) {
        filters.assigned_to = accessibilitySummaryFilters.value.assignedTo
      }
    }
    
    // Apply Detailed view filters (affects charts AND table)
    if (accessibilityView.value === ACCESSIBILITY_TABS.DETAILED) {
      if (accessibilityIssuesFilters.value.impact.length > 0) {
        filters.impacts = accessibilityIssuesFilters.value.impact
      }
      if (accessibilityIssuesFilters.value.status.length > 0) {
        filters.statuses = accessibilityIssuesFilters.value.status
      }
      if (accessibilityIssuesFilters.value.assignedTo.length > 0) {
        filters.assigned_to = accessibilityIssuesFilters.value.assignedTo
      }
      if (accessibilityIssuesFilters.value.pageUrl.length > 0) {
        filters.page_urls = accessibilityIssuesFilters.value.pageUrl
      }
      if (accessibilityIssuesFilters.value.testName.length > 0) {
        filters.test_names = accessibilityIssuesFilters.value.testName
      }
      if (accessibilityIssuesFilters.value.issueId.length > 0) {
        filters.issue_ids = accessibilityIssuesFilters.value.issueId
      }
      if (accessibilityIssuesFilters.value.node.length > 0) {
        filters.nodes = accessibilityIssuesFilters.value.node
      }
      if (accessibilityIssuesFilters.value.search) {
        filters.search = accessibilityIssuesFilters.value.search
      }
    }
    
    // Pass filters only if there are any, otherwise pass undefined to get all data
    const filtersToPass = Object.keys(filters).length > 0 ? filters : undefined
    
    console.log('[Statistics] Loading with filters:', filtersToPass)
    console.log('[Statistics] Date range:', reportDates[0].toISOString(), 'to', reportDates[reportDates.length - 1].toISOString())
    
    accessibilityStatistics.value = await accessibilityService.getStatistics(reportDates[0], reportDates[reportDates.length - 1], filtersToPass)
    
    console.log('[Statistics] Received:', accessibilityStatistics.value)
  } catch (error) {
    console.error('Failed to load accessibility statistics:', error)
  }
}

// Load all issues for charts (without pagination, no filters - filtering done client-side)
const loadAccessibilityAllIssuesForCharts = async () => {
  if (!accessibilityReports.value || accessibilityReports.value.length === 0) {
    return
  }
  
  try {
    const { accessibilityService } = await import('@/database/services')
    const reportDate = new Date(accessibilityReports.value[0].report_date)
    
    console.log('[Chart Data] Loading ALL issues for client-side filtering, report date:', reportDate.toISOString())
    
    // Fetch ALL issues without any filters (filtering will be done client-side)
    const result = await accessibilityService.queryAllIssues({
      report_date: reportDate.toISOString()
    })
    
    console.log('[Chart Data] Received result type:', Array.isArray(result) ? 'array' : typeof result)
    
    // queryAllIssues can return array directly or { issues: [] }
    if (Array.isArray(result)) {
      accessibilityAllIssuesForCharts.value = result
    } else if (result && result.issues) {
      accessibilityAllIssuesForCharts.value = result.issues
    } else {
      accessibilityAllIssuesForCharts.value = []
    }
    
    console.log('[Chart Data] Loaded', accessibilityAllIssuesForCharts.value.length, 'issues for client-side filtering')
  } catch (error) {
    console.error('[Chart Data] Failed to load all issues for charts:', error)
    accessibilityAllIssuesForCharts.value = []
  }
}

// Load accessibility test summary (for summary table)
const loadAccessibilityTestSummary = async () => {
  if (!accessibilityReports.value || accessibilityReports.value.length === 0) {
    return
  }
  
  try {
    const { accessibilityService } = await import('@/database/services')
    const reportDate = new Date(accessibilityReports.value[0].report_date)
    const result = await accessibilityService.getTestSummary(reportDate)
    console.log('Test summary loaded:', result)
    accessibilityTestSummaryData.value = result || []
  } catch (error) {
    console.error('Failed to load accessibility test summary:', error)
    accessibilityTestSummaryData.value = []
  }
}

// Load accessibility trends
const loadAccessibilityTrends = async () => {
  try {
    console.log('Loading accessibility trends...')
    const { accessibilityService } = await import('@/database/services')
    
    let startDate, endDate
    
    // Calculate date range based on selection
    if (accessibilityTrendDateRange.value === 'custom') {
      // Check if both dates are selected
      if (!accessibilityTrendCustomStartDate.value || !accessibilityTrendCustomEndDate.value) {
        console.log('Custom date range incomplete')
        return
      }
      
      // Convert to Date objects if they're timestamps
      startDate = accessibilityTrendCustomStartDate.value instanceof Date 
        ? accessibilityTrendCustomStartDate.value 
        : new Date(accessibilityTrendCustomStartDate.value)
      
      endDate = accessibilityTrendCustomEndDate.value instanceof Date 
        ? accessibilityTrendCustomEndDate.value 
        : new Date(accessibilityTrendCustomEndDate.value)
      
      // Validate dates
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        console.error('Invalid custom dates')
        return
      }
      
      // Ensure start date is before end date
      if (startDate > endDate) {
        console.error('Start date must be before end date')
        return
      }
    } else {
      endDate = new Date()
      startDate = new Date()
      
      switch (accessibilityTrendDateRange.value) {
        case '7_days':
          startDate.setDate(startDate.getDate() - 7)
          break
        case '30_days':
          startDate.setDate(startDate.getDate() - 30)
          break
        case '12_weeks':
        default:
          startDate.setDate(startDate.getDate() - 84) // 12 weeks
          break
      }
    }
    
    console.log('Fetching trends from', startDate.toISOString(), 'to', endDate.toISOString())
    const result = await accessibilityService.getTrends(startDate, endDate)
    accessibilityTrendData.value = result
    console.log('Trends loaded:', result)
    
    // Charts will be created by the watcher on accessibilityTrendData
  } catch (error) {
    console.error('Failed to load accessibility trends:', error)
    accessibilityTrendData.value = null
  }
}

// Create Impact Trend Chart (Multi-line)
const createImpactTrendChart = () => {
  // Use the canvas from Overview tab (only location for trend charts now)
  const canvas = impactTrendCanvas.value
    
  if (!canvas) {
    console.log('Impact trend canvas ref not available yet for view:', accessibilityView.value)
    return
  }
  if (!accessibilityImpactTrendData.value || !accessibilityImpactTrendData.value.length) {
    console.log('Impact trend data not available yet')
    return
  }
  
  console.log('Creating impact trend chart with', accessibilityImpactTrendData.value.length, 'data points')
  const ctx = canvas.getContext('2d')
  
  if (impactTrendChartInstance) {
    impactTrendChartInstance.destroy()
  }
  
  impactTrendChartInstance = new Chart(ctx, {
    type: 'line',
    data: {
      labels: accessibilityImpactTrendData.value.map(d => d.date),
      datasets: [
        {
          label: 'Critical',
          data: accessibilityImpactTrendData.value.map(d => d.critical),
          borderColor: 'rgba(239, 68, 68, 0.8)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.4
        },
        {
          label: 'Serious',
          data: accessibilityImpactTrendData.value.map(d => d.serious),
          borderColor: 'rgba(249, 115, 22, 0.8)',
          backgroundColor: 'rgba(249, 115, 22, 0.1)',
          tension: 0.4
        },
        {
          label: 'Moderate',
          data: accessibilityImpactTrendData.value.map(d => d.moderate),
          borderColor: 'rgba(234, 179, 8, 0.8)',
          backgroundColor: 'rgba(234, 179, 8, 0.1)',
          tension: 0.4
        },
        {
          label: 'Minor',
          data: accessibilityImpactTrendData.value.map(d => d.minor),
          borderColor: 'rgba(59, 130, 246, 0.8)',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.4
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom'
        }
      },
      scales: {
        y: {
          beginAtZero: true
        }
      }
    }
  })
}

// Create New vs Fixed Trend Chart (Multi-line)
const createNewVsFixedTrendChart = () => {
  // Use the canvas from Overview tab (only location for trend charts now)
  const canvas = newVsFixedTrendCanvas.value
    
  if (!canvas) {
    console.log('New vs Fixed trend canvas ref not available yet for view:', accessibilityView.value)
    return
  }
  if (!accessibilityNewVsFixedTrendData.value || !accessibilityNewVsFixedTrendData.value.length) {
    console.log('New vs Fixed trend data not available yet')
    return
  }
  
  console.log('Creating new vs fixed trend chart with', accessibilityNewVsFixedTrendData.value.length, 'data points')
  const ctx = canvas.getContext('2d')
  
  if (newVsFixedTrendChartInstance) {
    newVsFixedTrendChartInstance.destroy()
  }
  
  newVsFixedTrendChartInstance = new Chart(ctx, {
    type: 'line',
    data: {
      labels: accessibilityNewVsFixedTrendData.value.map(d => d.date),
      datasets: [
        {
          label: 'New Issues',
          data: accessibilityNewVsFixedTrendData.value.map(d => d.new_issues),
          borderColor: 'rgba(239, 68, 68, 0.8)',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.4
        },
        {
          label: 'Fixed Issues',
          data: accessibilityNewVsFixedTrendData.value.map(d => d.fixed_issues),
          borderColor: 'rgba(16, 185, 129, 0.8)',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          tension: 0.4
        },
        {
          label: 'Net Change',
          data: accessibilityNewVsFixedTrendData.value.map(d => d.net_change),
          borderColor: 'rgba(156, 163, 175, 0.8)',
          backgroundColor: 'rgba(156, 163, 175, 0.1)',
          tension: 0.4,
          borderDash: [5, 5]
        }
      ]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom'
        }
      },
      scales: {
        y: {
          beginAtZero: true
        }
      }
    }
  })
}

// Load all unique Issue IDs and Nodes for filter dropdowns
const loadAccessibilityFilterOptions = async () => {
  if (!accessibilityReports.value || accessibilityReports.value.length === 0) {
    return
  }
  
  try {
    const { accessibilityService } = await import('@/database/services')
    
    // Fetch ALL issues without pagination to get ALL unique values for filters
    const response = await accessibilityService.queryAllIssues({
      report_date: new Date(accessibilityReports.value[0].report_date).toISOString()
    })
    
    const issues = response || []
    
    console.log(`Loaded ${issues.length} issues for filter options`)
    
    // Extract ALL unique values for filters
    accessibilityAllIssueIds.value = [...new Set(issues.map(issue => issue.issue_id))].filter(Boolean)
    accessibilityAllNodes.value = [...new Set(issues.map(issue => issue.node))].filter(Boolean)
    accessibilityAllPageUrls.value = [...new Set(issues.map(issue => issue.page_url))].filter(Boolean)
    accessibilityAllTestNames.value = [...new Set(issues.map(issue => issue.test_name))].filter(Boolean)
    
    console.log('Filter options loaded:', {
      issueIds: accessibilityAllIssueIds.value.length,
      nodes: accessibilityAllNodes.value.length,
      pageUrls: accessibilityAllPageUrls.value.length,
      testNames: accessibilityAllTestNames.value.length
    })
  } catch (error) {
    console.error('Failed to load filter options:', error)
  }
}

const loadAccessibilityIssues = async () => {
  if (!accessibilityReports.value || accessibilityReports.value.length === 0) {
    return
  }
  
  try {
    accessibilityIssuesLoading.value = true
    const { accessibilityService } = await import('@/database/services')
    
    const query = {
      report_date: new Date(accessibilityReports.value[0].report_date).toISOString(),
      page: accessibilityIssuesPagination.value.currentPage,
      limit: accessibilityIssuesPagination.value.pageSize,
      impacts: accessibilityIssuesFilters.value.impact.length > 0 ? accessibilityIssuesFilters.value.impact : undefined,
      statuses: accessibilityIssuesFilters.value.status.length > 0 ? accessibilityIssuesFilters.value.status : undefined,
      page_urls: accessibilityIssuesFilters.value.pageUrl.length > 0 ? accessibilityIssuesFilters.value.pageUrl : undefined,
      test_names: accessibilityIssuesFilters.value.testName.length > 0 ? accessibilityIssuesFilters.value.testName : undefined,
      issue_ids: accessibilityIssuesFilters.value.issueId.length > 0 ? accessibilityIssuesFilters.value.issueId : undefined,
      nodes: accessibilityIssuesFilters.value.node.length > 0 ? accessibilityIssuesFilters.value.node : undefined,
      assigned_to: accessibilityIssuesFilters.value.assignedTo.length > 0 ? accessibilityIssuesFilters.value.assignedTo : undefined,
      search: accessibilityIssuesFilters.value.search || undefined
    }
    
    const response = await accessibilityService.queryIssues(query)
    accessibilityIssues.value = response.data || []
    accessibilityIssuesPagination.value.total = response.pagination?.total || 0
  } catch (error) {
    console.error('Failed to load accessibility issues:', error)
    showError('Failed to load accessibility issues')
  } finally {
    accessibilityIssuesLoading.value = false
  }
}

// Pagination handlers for accessibility issues
const handleAccessibilityPageChange = (page) => {
  accessibilityIssuesPagination.value.currentPage = page
  loadAccessibilityIssues()
}

const handleAccessibilityPerPageChange = (perPage) => {
  const newPageSize = Number(perPage)
  accessibilityIssuesPagination.value.pageSize = newPageSize
  accessibilityIssuesPagination.value.currentPage = 1
  loadAccessibilityIssues()
}

// Pagination handlers for accessibility summary
const handleAccessibilitySummaryPageChange = (page) => {
  accessibilitySummaryPagination.value.currentPage = page
}

const handleAccessibilitySummaryPerPageChange = (perPage) => {
  const newPageSize = Number(perPage)
  accessibilitySummaryPagination.value.pageSize = newPageSize
  accessibilitySummaryPagination.value.currentPage = 1
}

// Export accessibility issues to CSV
const handleAccessibilityExport = async () => {
  try {
    const { accessibilityService } = await import('@/database/services')
    
    // Fetch ALL issues with current filters (no pagination)
    const query = {
      report_date: new Date(accessibilityReports.value[0].report_date).toISOString(),
      page: 1,
      limit: 999999, // Get all results
      impacts: accessibilityIssuesFilters.value.impact.length > 0 ? accessibilityIssuesFilters.value.impact : undefined,
      statuses: accessibilityIssuesFilters.value.status.length > 0 ? accessibilityIssuesFilters.value.status : undefined,
      page_urls: accessibilityIssuesFilters.value.pageUrl.length > 0 ? accessibilityIssuesFilters.value.pageUrl : undefined,
      test_names: accessibilityIssuesFilters.value.testName.length > 0 ? accessibilityIssuesFilters.value.testName : undefined,
      issue_ids: accessibilityIssuesFilters.value.issueId.length > 0 ? accessibilityIssuesFilters.value.issueId : undefined,
      nodes: accessibilityIssuesFilters.value.node.length > 0 ? accessibilityIssuesFilters.value.node : undefined,
      assigned_to: accessibilityIssuesFilters.value.assignedTo.length > 0 ? accessibilityIssuesFilters.value.assignedTo : undefined,
      search: accessibilityIssuesFilters.value.search || undefined
    }
    
    const response = await accessibilityService.queryIssues(query)
    const allIssues = response.data || []
    
    if (allIssues.length === 0) {
      showError('No issues to export')
      return
    }
    
    // Convert to CSV
    const headers = ['Impact', 'Test Name', 'Page URL', 'Issue ID', 'Node', 'Description', 'Status', 'Assigned To', 'Help URL']
    const csvRows = [headers.join(',')]
    
    allIssues.forEach(issue => {
      const row = [
        `"${issue.impact || ''}"`,
        `"${(issue.test_name || '').replace(/"/g, '""')}"`,
        `"${(issue.page_url || '').replace(/"/g, '""')}"`,
        `"${issue.issue_id || ''}"`,
        `"${(issue.node || '').replace(/"/g, '""')}"`,
        `"${(issue.description || '').replace(/"/g, '""')}"`,
        `"${issue.status || ''}"`,
        `"${issue.assigned_to_name || 'Unassigned'}"`,
        `"${issue.help_url || ''}"`
      ]
      csvRows.push(row.join(','))
    })
    
    const csvContent = csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    
    const reportDate = new Date(accessibilityReports.value[0].report_date).toISOString().split('T')[0]
    link.setAttribute('href', url)
    link.setAttribute('download', `accessibility-issues-${reportDate}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    showSuccess(`Exported ${allIssues.length} issues to CSV`)
  } catch (error) {
    console.error('Failed to export accessibility issues:', error)
    showError('Failed to export accessibility issues')
  }
}

// Export Overview Statistics to CSV
const handleOverviewExport = () => {
  try {
    const csvRows = []
    
    // Section 1: Summary Statistics
    csvRows.push('=== SUMMARY STATISTICS ===')
    csvRows.push('')
    csvRows.push('Metric,Value')
    accessibilityStats.value.forEach(stat => {
      csvRows.push(`"${stat.label}","${stat.value}"`)
    })
    csvRows.push('')
    
    // Section 2: Issues by Impact (filtered)
    csvRows.push('=== ISSUES BY IMPACT ===')
    csvRows.push('')
    csvRows.push('Impact,Count')
    const impactData = accessibilityFilteredStats.value?.by_impact || {}
    csvRows.push(`"Critical","${impactData.critical || 0}"`)
    csvRows.push(`"Serious","${impactData.serious || 0}"`)
    csvRows.push(`"Moderate","${impactData.moderate || 0}"`)
    csvRows.push(`"Minor","${impactData.minor || 0}"`)
    csvRows.push('')
    
    // Section 3: Issues by Status (filtered)
    csvRows.push('=== ISSUES BY STATUS ===')
    csvRows.push('')
    csvRows.push('Status,Count')
    const statusData = accessibilityFilteredStats.value?.by_status || {}
    csvRows.push(`"Open","${statusData.open || 0}"`)
    csvRows.push(`"Reopened","${statusData.reopened || 0}"`)
    csvRows.push(`"In Progress","${statusData.in_progress || 0}"`)
    csvRows.push(`"Blocked","${statusData.blocked || 0}"`)
    csvRows.push(`"Closed","${statusData.closed || 0}"`)
    csvRows.push('')
    
    // Section 4: Issues by Assignee (filtered)
    csvRows.push('=== ISSUES BY ASSIGNEE ===')
    csvRows.push('')
    csvRows.push('Assignee,Count')
    const assigneeData = accessibilityAssigneeChartData.value || []
    assigneeData.forEach(item => {
      csvRows.push(`"${item.label}","${item.value}"`)
    })
    csvRows.push('')
    
    // Section 5: Issues by Type (filtered)
    csvRows.push('=== ISSUES BY TYPE ===')
    csvRows.push('')
    csvRows.push('Issue Type,Count')
    const typeData = accessibilityIssueIdChartData.value || []
    typeData.forEach(item => {
      csvRows.push(`"${item.label}","${item.value}"`)
    })
    
    // Create and download CSV
    const csvContent = csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    
    const reportDate = new Date(accessibilityReports.value[0].report_date).toISOString().split('T')[0]
    link.setAttribute('href', url)
    link.setAttribute('download', `accessibility-overview-statistics-${reportDate}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    showSuccess('Exported overview statistics to CSV')
  } catch (error) {
    console.error('Failed to export overview statistics:', error)
    showError('Failed to export overview statistics')
  }
}

// Export Summary Table to CSV (filtered data)
const handleSummaryExport = () => {
  try {
    const data = accessibilityTestSummary.value || []
    
    if (data.length === 0) {
      showError('No data to export')
      return
    }
    
    // CSV Headers
    const headers = ['Test Name', 'Total', 'Open', 'Reopened', 'In Progress', 'Blocked', 'Closed']
    const csvRows = [headers.join(',')]
    
    // Data rows
    data.forEach(item => {
      const row = [
        `"${(item.test_name || '').replace(/"/g, '""')}"`,
        item.total || 0,
        item.open || 0,
        item.reopened || 0,
        item.in_progress || 0,
        item.blocked || 0,
        item.closed || 0
      ]
      csvRows.push(row.join(','))
    })
    
    // Add totals row
    const totals = data.reduce((acc, item) => ({
      total: acc.total + (item.total || 0),
      open: acc.open + (item.open || 0),
      reopened: acc.reopened + (item.reopened || 0),
      in_progress: acc.in_progress + (item.in_progress || 0),
      blocked: acc.blocked + (item.blocked || 0),
      closed: acc.closed + (item.closed || 0)
    }), { total: 0, open: 0, reopened: 0, in_progress: 0, blocked: 0, closed: 0 })
    
    csvRows.push('')
    csvRows.push(`"TOTAL",${totals.total},${totals.open},${totals.reopened},${totals.in_progress},${totals.blocked},${totals.closed}`)
    
    // Create and download CSV
    const csvContent = csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    
    const reportDate = new Date(accessibilityReports.value[0].report_date).toISOString().split('T')[0]
    link.setAttribute('href', url)
    link.setAttribute('download', `accessibility-test-summary-${reportDate}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    showSuccess(`Exported ${data.length} test summaries to CSV`)
  } catch (error) {
    console.error('Failed to export summary:', error)
    showError('Failed to export summary')
  }
}

// Load all users for assignment
const loadAccessibilityUsers = async () => {
  try {
    const { http } = await import('@/utils/httpClient')
    const users = await http.get('/users')
    accessibilityAllUsersForAssignment.value = users || []
  } catch (error) {
    console.error('Failed to load users:', error)
  }
}

// Handle inline assignment change
const handleInlineAssignment = async (issueId, userId) => {
  try {
    const { accessibilityService } = await import('@/database/services')
    const userName = userId ? accessibilityAllUsersForAssignment.value.find(u => u.id === userId)?.name || null : null
    const currentUserId = authStore.userProfile?.id || authStore.userProfile?.uid || 'unknown'
    const currentUserName = authStore.userProfile?.name || authStore.userProfile?.fullName || 'Unknown User'
    
    // Optimistically update the UI immediately
    const issueIndex = accessibilityIssues.value.findIndex(issue => issue.id === issueId)
    if (issueIndex !== -1) {
      accessibilityIssues.value[issueIndex].assigned_to = userId
      accessibilityIssues.value[issueIndex].assigned_to_name = userName
    }
    
    // Update in background
    await accessibilityService.assignIssues(
      [issueId],
      userId,
      userName,
      currentUserId,
      currentUserName
    )
    
    // Reload all issues for charts (statistics are computed automatically from filtered issues)
    await loadAccessibilityAllIssuesForCharts()
    
    showSuccess('Assignment updated successfully')
  } catch (error) {
    console.error('Failed to update assignment:', error)
    showError('Failed to update assignment')
    // Reload on error to revert optimistic update
    await loadAccessibilityIssues()
  }
}

// Handle inline status change
const handleInlineStatusChange = async (issueId, status) => {
  try {
    const { accessibilityService } = await import('@/database/services')
    const userId = authStore.userProfile?.id || authStore.userProfile?.uid
    const userName = authStore.userProfile?.name || authStore.userProfile?.fullName || 'Unknown'
    
    // Optimistically update the UI immediately
    const issueIndex = accessibilityIssues.value.findIndex(issue => issue.id === issueId)
    if (issueIndex !== -1) {
      accessibilityIssues.value[issueIndex].status = status
    }
    
    // Update in background
    await accessibilityService.updateStatus([issueId], status, userId, userName)
    
    // Reload reports (to get updated summary counts) and issues for charts
    await Promise.all([
      loadAccessibilityReports(),
      loadAccessibilityAllIssuesForCharts()
    ])
    
    showSuccess('Status updated successfully')
  } catch (error) {
    console.error('Failed to update status:', error)
    showError('Failed to update status')
    // Reload on error to revert optimistic update
    await loadAccessibilityIssues()
  }
}

// Toggle select all issues
const toggleSelectAllAccessibilityIssues = () => {
  if (accessibilitySelectedIssues.value.length === accessibilityIssues.value.length) {
    accessibilitySelectedIssues.value = []
  } else {
    accessibilitySelectedIssues.value = accessibilityIssues.value.map(issue => issue.id)
  }
}

// Open bulk assign modal
const openBulkAssignModal = () => {
  if (accessibilitySelectedIssues.value.length === 0) {
    showError('Please select at least one issue')
    return
  }
  bulkAssignUser.value = null
  showBulkAssignModal.value = true
}

// Open delete report modal
const openDeleteReportModal = (report) => {
  reportToDelete.value = report
  showDeleteReportModal.value = true
}

// Confirm and delete report
const confirmDeleteReport = async () => {
  if (!reportToDelete.value) return
  
  try {
    isDeletingReport.value = true
    
    const { accessibilityService } = await import('@/database/services')
    
    await accessibilityService.deleteReport(reportToDelete.value.id)
    
    showSuccess(`Report deleted successfully`)
    
    // Close modal
    showDeleteReportModal.value = false
    reportToDelete.value = null
    
    // Reload all data including trends
    await loadAccessibilityReports()
    await loadAccessibilityTrends()
    
  } catch (error) {
    console.error('Failed to delete report:', error)
    showError(error.message || 'Failed to delete report')
  } finally {
    isDeletingReport.value = false
  }
}

// Open bulk status modal
const openBulkStatusModal = () => {
  if (accessibilitySelectedIssues.value.length === 0) {
    showError('Please select at least one issue')
    return
  }
  bulkStatus.value = 'open'
  showBulkStatusModal.value = true
}

// Confirm bulk assignment
const confirmBulkAssignment = async () => {
  try {
    const { accessibilityService } = await import('@/database/services')
    const userName = bulkAssignUser.value ? accessibilityAllUsersForAssignment.value.find(u => u.id === bulkAssignUser.value)?.name || null : null
    const currentUserId = authStore.userProfile?.id || authStore.userProfile?.uid || 'unknown'
    const currentUserName = authStore.userProfile?.name || authStore.userProfile?.fullName || 'Unknown User'
    
    const selectedCount = accessibilitySelectedIssues.value.length
    
    await accessibilityService.assignIssues(
      accessibilitySelectedIssues.value,
      bulkAssignUser.value,
      userName,
      currentUserId,
      currentUserName
    )
    
    showBulkAssignModal.value = false
    accessibilitySelectedIssues.value = []
    await Promise.all([
      loadAccessibilityIssues(),
      loadAccessibilityAllIssuesForCharts() // Statistics computed automatically
    ])
    showSuccess(`Successfully assigned ${selectedCount} issue(s)`)
  } catch (error) {
    console.error('Failed to assign issues:', error)
    showError('Failed to assign issues')
  }
}

// Confirm bulk status update
const confirmBulkStatusUpdate = async () => {
  try {
    const { accessibilityService } = await import('@/database/services')
    const selectedCount = accessibilitySelectedIssues.value.length
    const userId = authStore.userProfile?.id || authStore.userProfile?.uid
    const userName = authStore.userProfile?.name || authStore.userProfile?.fullName || 'Unknown'
    
    await accessibilityService.updateStatus(accessibilitySelectedIssues.value, bulkStatus.value, userId, userName)
    
    showBulkStatusModal.value = false
    accessibilitySelectedIssues.value = []
    await Promise.all([
      loadAccessibilityReports(), // Reload reports to get updated summary counts
      loadAccessibilityIssues(),
      loadAccessibilityAllIssuesForCharts() // Statistics computed automatically
    ])
    showSuccess(`Successfully updated status of ${selectedCount} issue(s)`)
  } catch (error) {
    console.error('Failed to update status:', error)
    showError('Failed to update status')
  }
}

// View assignment history
const viewAssignmentHistory = async (issueId) => {
  try {
    const { accessibilityService } = await import('@/database/services')
    const history = await accessibilityService.getAssignmentHistory(issueId)
    currentAssignmentHistory.value = history
    assignmentHistoryIssueId.value = issueId
    showAssignmentHistoryDrawer.value = true
  } catch (error) {
    console.error('Failed to load assignment history:', error)
    showError('Failed to load assignment history')
  }
}

const exportAllData = async () => {
  
  try {
    // Dynamically import XLSX
    const XLSX = await import('xlsx')
    
    const timestamp = new Date().toISOString().split('T')[0]
    const dateTime = new Date().toLocaleString()
    
    // Create a new workbook
    const workbook = XLSX.utils.book_new()
    
    // 1. Teams Sheet
    const teamsData = [
      [t('reports.export.teamsReportTitle')],
      [t('reports.export.generatedOn', { date: dateTime })],
      [t('reports.export.totalTeams', { count: allTeams.value.length })],
      [],
      EXPORT_HEADERS.TEAMS
    ]
    allTeams.value.forEach(team => {
      const teamData = teamsTableData.value.find(t => t.id === team.id)
      teamsData.push([
        team.name,
        teamData?.projectsCount || 0,
        teamData?.featuresCount || 0,
        teamData?.testCasesCount || 0,
        teamData?.bugsCount || 0,
        formatDate(team.created_at)
      ])
    })
    const teamsSheet = XLSX.utils.aoa_to_sheet(teamsData)
    XLSX.utils.book_append_sheet(workbook, teamsSheet, EXPORT_SHEET_NAMES.TEAMS)
    
    // 2. Projects Sheet
    const projectsData = [
      [t('reports.export.projectsReportTitle')],
      [t('reports.export.generatedOn', { date: dateTime })],
      [t('reports.export.totalProjects', { count: allProjects.value.length })],
      [],
      EXPORT_HEADERS.PROJECTS
    ]
    allProjects.value.forEach(project => {
      const features = allFeatures.value.filter(f => 
        f.project_id === project.id && 
        (!f.parent_id || f.parent_id === null || f.parent_id === '')
      ).length
      const testCases = allTestCases.value.filter(tc => tc.project_id === project.id).length
      const testRuns = allTestRuns.value.filter(tr => tr.project_id === project.id).length
      const bugs = allBugs.value.filter(b => b.project_id === project.id).length
      projectsData.push([
        project.name,
        getTeamName(project.teamId),
        features,
        testCases,
        testRuns,
        bugs,
        formatDate(project.created_at)
      ])
    })
    const projectsSheet = XLSX.utils.aoa_to_sheet(projectsData)
    XLSX.utils.book_append_sheet(workbook, projectsSheet, EXPORT_SHEET_NAMES.PROJECTS)
    
    // 3. Features Sheet
    const featuresData = [
      [t('reports.export.featuresReportTitle')],
      [t('reports.export.generatedOn', { date: dateTime })],
      [t('reports.export.totalFeatures', { count: allFeatures.value.filter(f => !f.parent_id || f.parent_id === null || f.parent_id === '').length })],
      [],
      EXPORT_HEADERS.FEATURES
    ]
    allFeatures.value
      .filter(f => !f.parent_id || f.parent_id === null || f.parent_id === '')
      .forEach(feature => {
        featuresData.push([
          feature.featureId,
          feature.title,
          getProjectName(feature.project_id),
          feature.status,
          feature.priority,
          getUserName(feature.dev_assignee),
          getUserName(feature.qa_assignee),
          formatDate(feature.start_date),
          formatDate(feature.due_date),
          formatDate(feature.created_at)
        ])
      })
    const featuresSheet = XLSX.utils.aoa_to_sheet(featuresData)
    XLSX.utils.book_append_sheet(workbook, featuresSheet, EXPORT_SHEET_NAMES.FEATURES)
    
    // 4. Test Cases Sheet
    const testCasesData = [
      [t('reports.export.testCasesReportTitle')],
      [t('reports.export.generatedOn', { date: dateTime })],
      [t('reports.export.totalTestCases', { count: allTestCases.value.length })],
      [],
      EXPORT_HEADERS.TEST_CASES
    ]
    allTestCases.value.forEach(tc => {
      testCasesData.push([
        tc.testCaseId,
        tc.title || tc.name,
        getProjectName(tc.project_id),
        tc.type || tc.test_type,
        tc.case_type,
        tc.priority,
        tc.status,
        tc.automation_status,
        formatDate(tc.created_at)
      ])
    })
    const testCasesSheet = XLSX.utils.aoa_to_sheet(testCasesData)
    XLSX.utils.book_append_sheet(workbook, testCasesSheet, EXPORT_SHEET_NAMES.TEST_CASES)
    
    // 5. Test Runs Sheet
    const testRunsData = [
      [t('reports.export.testRunsReportTitle')],
      [t('reports.export.generatedOn', { date: dateTime })],
      [t('reports.export.totalTestRuns', { count: allTestRuns.value.length })],
      [],
      EXPORT_HEADERS.TEST_RUNS
    ]
    allTestRuns.value.forEach(tr => {
      testRunsData.push([
        tr.id,
        tr.name,
        getProjectName(tr.project_id),
        tr.status || t('reports.export.noStatus'),
        tr.environment || '-',
        `${tr.progress || 0}%`,
        formatDate(tr.created_at)
      ])
    })
    const testRunsSheet = XLSX.utils.aoa_to_sheet(testRunsData)
    XLSX.utils.book_append_sheet(workbook, testRunsSheet, EXPORT_SHEET_NAMES.TEST_RUNS)
    
    // 6. Bugs Sheet
    const bugsData = [
      [t('reports.export.bugsReportTitle')],
      [t('reports.export.generatedOn', { date: dateTime })],
      [t('reports.export.totalBugs', { count: allBugs.value.length })],
      [],
      EXPORT_HEADERS.BUGS
    ]
    allBugs.value.forEach(bug => {
      bugsData.push([
        bug.bugId,
        bug.title,
        getProjectName(bug.project_id),
        bug.type,
        bug.status,
        bug.severity,
        bug.priority,
        bug.environment,
        formatDate(bug.created_at)
      ])
    })
    const bugsSheet = XLSX.utils.aoa_to_sheet(bugsData)
    XLSX.utils.book_append_sheet(workbook, bugsSheet, EXPORT_SHEET_NAMES.BUGS)
    
    // 7. Accessibility Sheet
    try {
      const { accessibilityService } = await import('@/database/services')
      
      // Load accessibility reports
      const allAccessibilityReports = await accessibilityService.getAllReports()
      
      if (allAccessibilityReports && allAccessibilityReports.length > 0) {
        // Fetch all accessibility issues without pagination
        const allAccessibilityIssues = []
        for (const report of allAccessibilityReports) {
          const response = await accessibilityService.queryAllIssues({
            report_date: new Date(report.report_date).toISOString()
          })
          if (response?.data) {
            allAccessibilityIssues.push(...response.data)
          }
        }
        
        const accessibilityData = [
          ['Accessibility Report'],
          [`Generated on: ${dateTime}`],
          [`Total Issues: ${allAccessibilityIssues.length}`],
          [],
          EXPORT_HEADERS.ACCESSIBILITY
        ]
        
        allAccessibilityIssues.forEach(issue => {
          const reportDates = issue.report_dates && Array.isArray(issue.report_dates) 
            ? issue.report_dates.map(d => formatDate(d)).join(', ')
            : '-'
          
          accessibilityData.push([
            issue.test_name || '-',
            issue.page_url || '-',
            issue.issue_id || '-',
            issue.impact || '-',
            issue.description || '-',
            issue.help_url || '-',
            issue.node || '-',
            issue.status || 'open',
            issue.assigned_to_name || 'Unassigned',
            formatDate(issue.first_detected),
            formatDate(issue.last_detected),
            reportDates
          ])
        })
        
        const accessibilitySheet = XLSX.utils.aoa_to_sheet(accessibilityData)
        XLSX.utils.book_append_sheet(workbook, accessibilitySheet, EXPORT_SHEET_NAMES.ACCESSIBILITY)
      }
    } catch (error) {
      console.error('Failed to export accessibility data:', error)
      // Continue with export even if accessibility fails
    }
    
    // Write the workbook to file
    const filename = t('reports.export.filename.completeAnalytics', { timestamp })
    XLSX.writeFile(workbook, filename)
  } catch (error) {
    showError(t('reports.export.exportFailed'))
  }
}

const getTeamName = (teamId) => {
  const team = allTeams.value.find(t => t.id === teamId)
  return team ? team.name : t('reports.export.unknown')
}

const getProjectName = (projectId) => {
  const project = allProjects.value.find(p => p.id === projectId)
  return project ? project.name : t('reports.export.unknown')
}

// Get project name or show as "Deleted" if project is deleted
// This function checks both visible and deleted projects
const getProjectNameOrDeleted = (projectId) => {
  // First check in visible projects
  const visibleProject = allProjects.value.find(p => p.id === projectId)
  if (visibleProject) return visibleProject.name
  
  // If not found in visible projects, check in the including deleted list
  const deletedProject = allProjectsIncludingDeleted.value.find(p => p.id === projectId)
  if (deletedProject) {
    if (deletedProject.is_deleted === true || deletedProject.deleted_at) {
      return `${deletedProject.name} (${t('common.deleted')})`
    }
    return deletedProject.name
  }
  
  return t('common.unknown')
}

const handleFeatureStatusClick = (segment) => {
  // Could apply status filter
}

const handleBugStatusClick = (segment) => {
  // Could apply status filter
}

// Toggle section filter expand/collapse
const toggleSectionFilter = (section) => {
  sectionFilters.value[section].expanded = !sectionFilters.value[section].expanded
}

// Clear section filters
const clearSectionFilter = (section) => {
  if (sectionFilters.value[section]) {
    Object.keys(sectionFilters.value[section]).forEach(key => {
      if (key !== 'expanded' && key !== 'includeDeleted') {
        if (Array.isArray(sectionFilters.value[section][key])) {
          sectionFilters.value[section][key] = []
        } else if (typeof sectionFilters.value[section][key] === 'object' && sectionFilters.value[section][key] !== null) {
          // Handle date range objects and estimation range
          if (key === 'estimationRange') {
            sectionFilters.value[section][key] = { min: null, max: null }
          } else {
            // Default to start/end for date ranges
            sectionFilters.value[section][key] = { start: null, end: null }
          }
        } else {
          sectionFilters.value[section][key] = ''
        }
      }
    })
  }
}

// Helper to format dates
const formatDate = (dateString) => {
  if (!dateString) return '-'
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  })
}

// Export filtered data
const exportFilteredData = (type) => {
  let data = []
  let filename = ''
  
  switch(type) {
    case 'features':
      data = filteredFeatures.value
      filename = 'filtered-features.csv'
      break
    case 'testCases':
      data = filteredTestCases.value
      filename = 'filtered-test-cases.csv'
      break
    case 'testRuns':
      data = filteredTestRuns.value
      filename = 'filtered-test-runs.csv'
      break
    case 'bugs':
      data = filteredBugs.value
      filename = 'filtered-bugs.csv'
      break
  }
  
  if (data.length === 0) {
    return
  }

  // Convert to CSV
  const headers = Object.keys(data[0])
  const csvContent = [
    headers.join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header]
        return typeof value === 'string' && value.includes(',') 
          ? `"${value}"` 
          : value
      }).join(',')
    )
  ].join('\n')
  
  // Download
  const blob = new Blob([csvContent], { type: 'text/csv' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  window.URL.revokeObjectURL(url)
}

// Feature project options (filtered by selected teams)
const featureProjectOptions = computed(() => {
  let projects = allProjects.value
  
  // If teams are selected in features filter, only show projects from those teams
  if (sectionFilters.value.features.teamIds.length > 0) {
    projects = projects.filter(p => sectionFilters.value.features.teamIds.includes(p.teamId))
  }
  
  return projects.map(p => ({
    value: p.id,
    label: p.name
  }))
})

// Use constants to show all possible status and priority values, not just existing ones
const featureStatusOptions = computed(() => FEATURE_STATUS_OPTIONS)
const featurePriorityOptions = computed(() => FEATURE_PRIORITY_OPTIONS)

// Developer assignee options (from users)
const featureDevOptions = computed(() => {
  // Get unique dev assignee IDs from features
  const devIds = [...new Set(allFeatures.value.map(f => f.dev_assignee).filter(Boolean))]
  
  // Map to user objects with names
  const userOptions = devIds.map(userId => {
    const user = allUsers.value.find(u => u.id === userId)
    return {
      value: userId,
      label: user ? user.name : userId
    }
  }).filter(opt => opt.label) // Only show if we found the user
  
  // Add "Unassigned" option at the beginning
  return [
    { value: FILTER_VALUES.UNASSIGNED, label: t('reports.filter.options.unassigned') },
    ...userOptions
  ]
})

// QA assignee options (from users)
const featureQAOptions = computed(() => {
  // Get unique QA assignee IDs from features
  const qaIds = [...new Set(allFeatures.value.map(f => f.qa_assignee).filter(Boolean))]
  
  // Map to user objects with names
  const userOptions = qaIds.map(userId => {
    const user = allUsers.value.find(u => u.id === userId)
    return {
      value: userId,
      label: user ? user.name : userId
    }
  }).filter(opt => opt.label) // Only show if we found the user
  
  // Add "Unassigned" option at the beginning
  return [
    { value: FILTER_VALUES.UNASSIGNED, label: t('reports.filter.options.unassigned') },
    ...userOptions
  ]
})
// Use constants to show all possible options for test cases
const testCaseTypeOptions = computed(() => TEST_CASE_TYPES)
const testCaseCaseTypeOptions = computed(() => CASE_TYPES)
const testCasePriorityOptions = computed(() => PRIORITY_LEVELS)
const testCaseStatusOptions = computed(() => TEST_CASE_STATUS)

// Test case assignee options (from users)
const testCaseAssigneeOptions = computed(() => {
  // Get unique assignee IDs from test cases
  const assigneeIds = [...new Set(allTestCases.value.map(tc => tc.assignee || tc.assigned_to).filter(Boolean))]
  
  // Map to user objects with names
  const userOptions = assigneeIds.map(userId => {
    const user = allUsers.value.find(u => u.id === userId)
    return {
      value: userId,
      label: user ? user.name : userId
    }
  }).filter(opt => opt.label)
  
  // Add "Unassigned" option at the beginning
  return [
    { value: FILTER_VALUES.UNASSIGNED, label: t('reports.filter.options.unassigned') },
    ...userOptions
  ]
})
const testCaseAutomationOptions = computed(() => AUTOMATION_STATUS)

// Test case project options (filtered by selected teams)
const testCaseProjectOptions = computed(() => {
  let projects = allProjects.value
  
  // If teams are selected in test cases filter, only show projects from those teams
  if (sectionFilters.value.testCases.teamIds.length > 0) {
    projects = projects.filter(p => sectionFilters.value.testCases.teamIds.includes(p.teamId))
  }
  
  return projects.map(p => ({
    value: p.id,
    label: p.name
  }))
})
// Use constants for test runs
const testRunStatusOptions = computed(() => TEST_RUN_STATUS)
const testRunEnvironmentOptions = computed(() => TEST_RUN_ENVIRONMENTS)

// Test run created by options (from users)
const testRunCreatedByOptions = computed(() => {
  // Get unique created_by user IDs from test runs
  const creatorIds = [...new Set(allTestRuns.value.map(tr => tr.created_by).filter(Boolean))]
  
  // Map to user objects with names
  const userOptions = creatorIds.map(userId => {
    const user = allUsers.value.find(u => u.id === userId)
    return {
      value: userId,
      label: user ? user.name : userId
    }
  }).filter(opt => opt.label)
  
  // Add "Unassigned" option at the beginning
  return [
    { value: FILTER_VALUES.UNASSIGNED, label: t('reports.filter.options.unassigned') },
    ...userOptions
  ]
})

// Test run project options (filtered by selected teams)
const testRunProjectOptions = computed(() => {
  let projects = allProjects.value
  
  if (sectionFilters.value.testRuns.teamIds.length > 0) {
    projects = projects.filter(p => sectionFilters.value.testRuns.teamIds.includes(p.teamId))
  }
  
  return projects.map(p => ({
    value: p.id,
    label: p.name
  }))
})

// Use constants for bugs
const bugStatusOptions = computed(() => BUG_STATUS_OPTIONS)
const bugSeverityOptions = computed(() => BUG_SEVERITY_OPTIONS)
const bugPriorityOptions = computed(() => BUG_PRIORITY_OPTIONS)
const bugTypeOptions = computed(() => BUG_TYPE_OPTIONS)
const bugEnvironmentOptions = computed(() => ENVIRONMENT_OPTIONS)

// Bug assigned to options (from users)
const bugAssignedToOptions = computed(() => {
  // Get unique assigned_to user IDs from bugs
  const assigneeIds = [...new Set(allBugs.value.map(b => b.assigned_to).filter(Boolean))]
  
  // Map to user objects with names
  const userOptions = assigneeIds.map(userId => {
    const user = allUsers.value.find(u => u.id === userId)
    return {
      value: userId,
      label: user ? user.name : userId
    }
  }).filter(opt => opt.label)
  
  // Add "Unassigned" option at the beginning
  return [
    { value: FILTER_VALUES.UNASSIGNED, label: t('reports.filter.options.unassigned') },
    ...userOptions
  ]
})

// Bug project options (filtered by selected teams)
const bugProjectOptions = computed(() => {
  let projects = allProjects.value
  
  if (sectionFilters.value.bugs.teamIds.length > 0) {
    projects = projects.filter(p => sectionFilters.value.bugs.teamIds.includes(p.teamId))
  }
  
  return projects.map(p => ({
    value: p.id,
    label: p.name
  }))
})

// Helper to create option with checkbox rendering
const createOptionWithCheckbox = (opt, getSelectedValues) => {
  return {
    label: opt.label,
    value: opt.value,
    renderOption: () => {
      const selectedValues = getSelectedValues()
      const isSelected = selectedValues.includes(opt.value)
      return h('div', { 
        class: 'flex items-center gap-1.5 w-full cursor-pointer px-2 py-0.5 hover:bg-gray-50 dark:hover:bg-gray-700'
      }, [
        h('input', {
          type: 'checkbox',
          checked: isSelected,
          class: 'rounded border-gray-300 text-blue-600 focus:ring-blue-500 w-4 h-4 cursor-pointer flex-shrink-0',
          style: 'pointer-events: none;',
          readonly: true
        }),
        h('span', { 
          class: 'text-sm text-gray-900 dark:text-white select-none flex-1'
        }, opt.label)
      ])
    }
  }
}

// Option renderer function for HLSelect
const optionRenderer = (option) => {
  if (option.renderOption) {
    return option.renderOption()
  }
  return h('span', option.label)
}

// Generate unique filter ID
const uniqueFilterId = ref(Date.now().toString())

// Computed properties for HLSelect with checkbox rendering
// Teams & Projects tab
const teamSelectOptions = computed(() => {
  return teamOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.teamsAndProjects.selectedTeams))
})

const teamsAndProjectsProjectSelectOptionsComputed = computed(() => {
  const baseOptions = teamsAndProjectsProjectOptions.value
  return baseOptions.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.teamsAndProjects.selectedProjects))
})

// Features tab
const featureTeamSelectOptions = computed(() => {
  return teamOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.features.teamIds))
})

const featureProjectSelectOptions = computed(() => {
  return featureProjectOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.features.projectIds))
})

const featureStatusSelectOptions = computed(() => {
  return featureStatusOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.features.status))
})

const featurePrioritySelectOptions = computed(() => {
  return featurePriorityOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.features.priority))
})

const featureDevSelectOptions = computed(() => {
  return featureDevOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.features.devAssignees))
})

const featureQASelectOptions = computed(() => {
  return featureQAOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.features.qaAssignees))
})

// Computed properties for date range values (for HLDatePicker which expects array format)
const featuresStartDateRangeValue = computed({
  get: () => {
    if (sectionFilters.value.features.startDateRange.start && sectionFilters.value.features.startDateRange.end) {
      return [sectionFilters.value.features.startDateRange.start, sectionFilters.value.features.startDateRange.end]
    }
    return null
  },
  set: (value) => {
    if (value && Array.isArray(value) && value.length === 2) {
      sectionFilters.value.features.startDateRange.start = value[0]
      sectionFilters.value.features.startDateRange.end = value[1]
    } else {
      sectionFilters.value.features.startDateRange.start = null
      sectionFilters.value.features.startDateRange.end = null
    }
  }
})

const featuresDueDateRangeValue = computed({
  get: () => {
    if (sectionFilters.value.features.dueDateRange.start && sectionFilters.value.features.dueDateRange.end) {
      return [sectionFilters.value.features.dueDateRange.start, sectionFilters.value.features.dueDateRange.end]
    }
    return null
  },
  set: (value) => {
    if (value && Array.isArray(value) && value.length === 2) {
      sectionFilters.value.features.dueDateRange.start = value[0]
      sectionFilters.value.features.dueDateRange.end = value[1]
    } else {
      sectionFilters.value.features.dueDateRange.start = null
      sectionFilters.value.features.dueDateRange.end = null
    }
  }
})

// Test Cases tab
const testCaseTeamSelectOptions = computed(() => {
  return teamOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.testCases.teamIds))
})

const testCaseProjectSelectOptions = computed(() => {
  return testCaseProjectOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.testCases.projectIds))
})

const testCaseTypeSelectOptions = computed(() => {
  return testCaseTypeOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.testCases.testType))
})

const testCaseCaseTypeSelectOptions = computed(() => {
  return testCaseCaseTypeOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.testCases.caseType))
})

const testCasePrioritySelectOptions = computed(() => {
  return testCasePriorityOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.testCases.priority))
})

const testCaseStatusSelectOptions = computed(() => {
  return testCaseStatusOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.testCases.status))
})

const testCaseAutomationSelectOptions = computed(() => {
  return testCaseAutomationOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.testCases.automationStatus))
})

const testCaseAssigneeSelectOptions = computed(() => {
  return testCaseAssigneeOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.testCases.assignees))
})

// Test Runs tab
const testRunTeamSelectOptions = computed(() => {
  return teamOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.testRuns.teamIds))
})

const testRunProjectSelectOptions = computed(() => {
  return testRunProjectOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.testRuns.projectIds))
})

const testRunStatusSelectOptions = computed(() => {
  return testRunStatusOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.testRuns.status))
})

const testRunEnvironmentSelectOptions = computed(() => {
  return testRunEnvironmentOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.testRuns.environment))
})

const testRunCreatedBySelectOptions = computed(() => {
  return testRunCreatedByOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.testRuns.createdBy))
})

// Bugs tab
const bugTeamSelectOptions = computed(() => {
  return teamOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.bugs.teamIds))
})

const bugProjectSelectOptions = computed(() => {
  return bugProjectOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.bugs.projectIds))
})

const bugFeatureSelectOptions = computed(() => {
  return bugFeatureOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.bugs.featureIds))
})

const bugStatusSelectOptions = computed(() => {
  return bugStatusOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.bugs.status))
})

const bugSeveritySelectOptions = computed(() => {
  return bugSeverityOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.bugs.severity))
})

const bugPrioritySelectOptions = computed(() => {
  return bugPriorityOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.bugs.priority))
})

const bugTypeSelectOptions = computed(() => {
  return bugTypeOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.bugs.type))
})

const bugEnvironmentSelectOptions = computed(() => {
  return bugEnvironmentOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.bugs.environment))
})

const bugAssignedToSelectOptions = computed(() => {
  return bugAssignedToOptions.value.map(opt => createOptionWithCheckbox(opt, () => sectionFilters.value.bugs.assignedTo))
})

const bugFeatureOptions = computed(() => {
  let features = allFeatures.value
  
  // Filter by selected teams (through projects)
  if (sectionFilters.value.bugs.teamIds.length > 0) {
    const teamProjectIds = allProjects.value
      .filter(p => sectionFilters.value.bugs.teamIds.includes(p.teamId))
      .map(p => p.id)
    features = features.filter(f => teamProjectIds.includes(f.project_id))
  }
  
  // Filter by selected projects
  if (sectionFilters.value.bugs.projectIds.length > 0) {
    features = features.filter(f => sectionFilters.value.bugs.projectIds.includes(f.project_id))
  }
  
  return features.map(f => ({
    value: f.id,
    label: f.title
  }))
})

// Count active filters for each section
const countActiveFilters = (section) => {
  let count = 0
  const filters = sectionFilters.value[section]
  
  Object.keys(filters).forEach(key => {
    if (key !== 'expanded' && key !== 'includeDeleted') {
      if (Array.isArray(filters[key]) && filters[key].length > 0) {
        count++
      } else if (typeof filters[key] === 'string' && filters[key]) {
        count++
      } else if (typeof filters[key] === 'object' && filters[key] !== null) {
        // Handle date range objects and estimation range
        if (key === 'estimationRange') {
          if (filters[key].min !== null || filters[key].max !== null) {
        count++
          }
        } else if (filters[key].start || filters[key].end) {
          count++
        }
      }
    }
  })
  
  return count
}

const teamsAndProjectsActiveFilters = computed(() => countActiveFilters('teamsAndProjects'))

// Custom result message for Teams & Projects tab
const teamsAndProjectsResultMessage = computed(() => {
  const filteredTeams = filteredTeamsAndProjects.value.teams.length
  const filteredProjects = filteredTeamsAndProjects.value.projects.length
  const totalTeams = sectionFilters.value.teamsAndProjects.includeDeleted 
    ? allTeamsIncludingDeleted.value.length 
    : allTeams.value.length
  const totalProjects = sectionFilters.value.teamsAndProjects.includeDeleted 
    ? allProjectsIncludingDeleted.value.length 
    : allProjects.value.length
  
  const key = sectionFilters.value.teamsAndProjects.includeDeleted 
    ? 'reports.filter.showingTeamsAndProjectsWithDeleted'
    : 'reports.filter.showingTeamsAndProjects'
  
  return t(key, {
    filteredTeams,
    totalTeams,
    filteredProjects,
    totalProjects
  })
})

const teamsActiveFilters = computed(() => countActiveFilters('teams'))
const projectsActiveFilters = computed(() => countActiveFilters('projects'))
const featuresActiveFilters = computed(() => countActiveFilters('features'))
const testCasesActiveFilters = computed(() => countActiveFilters('testCases'))
const testRunsActiveFilters = computed(() => countActiveFilters('testRuns'))
const bugsActiveFilters = computed(() => countActiveFilters('bugs'))

// Project options for Teams & Projects tab (filtered by selected teams)
const teamsAndProjectsProjectOptions = computed(() => {
  let projects = sectionFilters.value.teamsAndProjects.includeDeleted 
    ? allProjectsIncludingDeleted.value 
    : allProjects.value
  
  // If teams are selected, only show projects from those teams
  if (sectionFilters.value.teamsAndProjects.selectedTeams.length > 0) {
    projects = projects.filter(p => sectionFilters.value.teamsAndProjects.selectedTeams.includes(p.teamId))
  }
  
  return projects.map(p => ({
    value: p.id,
    label: p.name
  }))
})

// Tab configuration
const reportTabs = computed(() => [
  {
    id: REPORT_TABS.OVERVIEW,
    label: t('reports.tabs.overview'),
    svgPath: getIconPath('report'),
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    iconColorHex: TAB_ICON_COLORS.REPORTS_OVERVIEW,
    badge: '6'
  },
  {
    id: REPORT_TABS.TEAMS,
    label: t('reports.tabs.teams'),
    svgPath: getIconPath('teamsProjects'),
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconColorHex: TAB_ICON_COLORS.OVERVIEW, // Using blue for teams
    badge: `${filteredTeams.value?.length ?? 0}/${filteredProjects.value?.length ?? 0}`
  },
  {
    id: REPORT_TABS.FEATURES,
    label: t('reports.tabs.features'),
    svgPath: getIconPath('feature'),
    iconColor: 'text-purple-600 dark:text-purple-400',
    iconColorHex: TAB_ICON_COLORS.FEATURES,
    badge: String(filteredFeatures.value?.length ?? 0)
  },
  {
    id: REPORT_TABS.TEST_CASES,
    label: t('reports.tabs.testCases'),
    svgPath: getIconPath('testCase'),
    iconColor: 'text-green-600 dark:text-green-400',
    iconColorHex: TAB_ICON_COLORS.TEST_CASES,
    badge: String(filteredTestCases.value?.length ?? 0)
  },
  {
    id: REPORT_TABS.TEST_RUNS,
    label: t('reports.tabs.testRuns'),
    svgPath: getIconPath('testRun'),
    iconColor: 'text-amber-600 dark:text-amber-400',
    iconColorHex: TAB_ICON_COLORS.TEST_RUNS,
    badge: String(filteredTestRuns.value?.length ?? 0)
  },
  {
    id: REPORT_TABS.BUGS,
    label: t('reports.tabs.bugs'),
    svgPath: getIconPath('bug'),
    iconColor: 'text-red-600 dark:text-red-400',
    iconColorHex: TAB_ICON_COLORS.BUGS,
    badge: String(filteredBugs.value?.length ?? 0)
  },
  {
    id: REPORT_TABS.ACCESSIBILITY,
    label: t('reports.tabs.accessibility'),
    svgPath: getIconPath('shield'),
    iconColor: 'text-teal-600 dark:text-teal-400',
    iconColorHex: TAB_ICON_COLORS.ACCESSIBILITY,
    badge: '0'
  }
])

onMounted(() => {
  loadAllData()
  // Load accessibility reports if on that tab
  if (activeTab.value === REPORT_TABS.ACCESSIBILITY) {
    loadAccessibilityReports()
    loadAccessibilityUsers()
  }
})

// Watch for tab changes to load accessibility reports
watch(activeTab, (newTab, oldTab) => {
  if (newTab === REPORT_TABS.ACCESSIBILITY && oldTab !== REPORT_TABS.ACCESSIBILITY) {
    loadAccessibilityReports()
    loadAccessibilityUsers()
  }
})

// Watch for accessibility trend date range changes
watch(accessibilityTrendDateRange, () => {
  if (activeTab.value === REPORT_TABS.ACCESSIBILITY) {
    loadAccessibilityTrends()
  }
})

// Watch for custom date changes (debounced)
let customDateDebounceTimer = null
watch([accessibilityTrendCustomStartDate, accessibilityTrendCustomEndDate], () => {
  if (activeTab.value === REPORT_TABS.ACCESSIBILITY && accessibilityTrendDateRange.value === 'custom') {
    // Debounce to prevent multiple API calls while user is selecting dates
    if (customDateDebounceTimer) {
      clearTimeout(customDateDebounceTimer)
    }
    customDateDebounceTimer = setTimeout(() => {
      loadAccessibilityTrends()
    }, 500)
  }
})

// Watch for view changes to recreate trend charts
watch(() => route.query.view, async (newView) => {
  if (activeTab.value === REPORT_TABS.ACCESSIBILITY) {
    // Recreate trend charts when switching views (data is already filtered via computed properties)
    console.log('View changed to:', newView || 'summary', '- recreating charts')
    await nextTick()
    setTimeout(() => {
      createImpactTrendChart()
      createNewVsFixedTrendChart()
    }, 150)
  }
})

// Watch for accessibility reports to be loaded and load trends
watch(() => accessibilityReports.value.length, async (newLength) => {
  if (activeTab.value === REPORT_TABS.ACCESSIBILITY && newLength > 1) {
    console.log('Accessibility reports loaded, loading trends...')
    await nextTick()
    setTimeout(() => {
      loadAccessibilityTrends()
    }, 200)
  }
})

// No need to watch filters and reload - computed properties handle filtering reactively (like other sections)
// The charts will automatically update when filters change because they use computed properties

// Watch for trend data changes and recreate charts
watch(() => accessibilityTrendData.value, async (newData) => {
  if (activeTab.value === REPORT_TABS.ACCESSIBILITY && newData) {
    console.log('Accessibility trend data changed, recreating charts...')
    await nextTick()
    setTimeout(() => {
      createImpactTrendChart()
      createNewVsFixedTrendChart()
    }, 150)
  }
}, { deep: true })

// Export section-specific report
const exportSectionReport = async (section) => {
  
  // Special handling for Teams & Projects tab - export to Excel with multiple sheets
  if (section === 'teams') {
    try {
      // Dynamically import XLSX
      const XLSX = await import('xlsx')
      
      const timestamp = new Date().toISOString().split('T')[0]
      const dateTime = new Date().toLocaleString()
      
      // Create a new workbook
      const workbook = XLSX.utils.book_new()
      
      // 1. Teams Sheet
      const teamsData = [
        [t('reports.export.teamsReportTitle')],
        [t('reports.export.generatedOn', { date: dateTime })],
        [t('reports.export.totalTeams', { count: teamsTableData.value.length })],
        [],
        [
          t('reports.export.headers.teamName'),
          t('reports.export.headers.totalProjects'),
          t('reports.export.headers.totalFeatures'),
          t('reports.export.headers.totalTestCases'),
          t('reports.export.headers.totalTestRuns'),
          t('reports.export.headers.totalBugs'),
          t('reports.export.headers.createdDate')
        ]
      ]
      
      teamsTableData.value.forEach(team => {
        const teamInfo = filteredTeamsAndProjects.value.teams.find(t => t.id === team.id)
        teamsData.push([
          team.name,
          team.projectsCount,
          team.featuresCount,
          team.testCasesCount,
          team.testRunsCount,
          team.bugsCount,
          formatDate(teamInfo?.created_at)
        ])
      })
      
      const teamsSheet = XLSX.utils.aoa_to_sheet(teamsData)
      XLSX.utils.book_append_sheet(workbook, teamsSheet, EXPORT_SHEET_NAMES.TEAMS)
      
      // 2. Projects Sheet
      const projectsData = [
        [t('reports.export.projectsReportTitle')],
        [t('reports.export.generatedOn', { date: dateTime })],
        [t('reports.export.totalProjects', { count: projectsTableData.value.length })],
        [],
        [
          t('reports.export.headers.projectName'),
          t('reports.export.headers.team'),
          t('reports.export.headers.features'),
          t('reports.export.headers.testCases'),
          t('reports.export.headers.testRuns'),
          t('reports.export.headers.bugs'),
          t('reports.export.headers.createdDate')
        ]
      ]
      
      projectsTableData.value.forEach(project => {
        const projectInfo = filteredTeamsAndProjects.value.projects.find(p => p.id === project.id)
        projectsData.push([
          project.name,
          getTeamName(project.teamId),
          project.featuresCount,
          project.testCasesCount,
          project.testRunsCount,
          project.bugsCount,
          formatDate(projectInfo?.created_at)
        ])
      })
      
      const projectsSheet = XLSX.utils.aoa_to_sheet(projectsData)
      XLSX.utils.book_append_sheet(workbook, projectsSheet, EXPORT_SHEET_NAMES.PROJECTS)
      
      // Write the workbook to file
      const filename = t('reports.export.filename.teamsAndProjects', { timestamp })
      XLSX.writeFile(workbook, filename)
    } catch (error) {
      showError(t('reports.export.exportFailed'))
    }
    return
  }
  
  // Original CSV export for other sections
  let data = []
  let filename = ''
  let headers = []
  
  switch(section) {
    case 'projects':
      data = filteredProjects.value.map(project => ({
        [t('reports.export.headers.projectName')]: project.name,
        [t('reports.export.headers.team')]: getTeamName(project.teamId),
        [t('reports.export.headers.features')]: allFeatures.value.filter(f => 
          f.project_id === project.id && 
          (!f.parent_id || f.parent_id === null || f.parent_id === '')
        ).length,
        [t('reports.export.headers.testCases')]: allTestCases.value.filter(tc => tc.project_id === project.id).length,
        [t('reports.export.headers.testRuns')]: allTestRuns.value.filter(tr => tr.project_id === project.id).length,
        [t('reports.export.headers.bugs')]: allBugs.value.filter(b => b.project_id === project.id).length,
        [t('reports.export.headers.createdDate')]: formatDate(project.created_at)
      }))
      filename = t('reports.export.filename.projects', { timestamp: new Date().toISOString().split('T')[0] })
      break
      
    case 'projects':
      data = filteredProjects.value.map(project => ({
        [t('reports.export.headers.projectName')]: project.name,
        [t('reports.export.headers.team')]: getTeamName(project.teamId),
        [t('reports.export.headers.features')]: allFeatures.value.filter(f => 
          f.project_id === project.id && 
          (!f.parent_id || f.parent_id === null || f.parent_id === '')
        ).length,
        [t('reports.export.headers.testCases')]: allTestCases.value.filter(tc => tc.project_id === project.id).length,
        [t('reports.export.headers.testRuns')]: allTestRuns.value.filter(tr => tr.project_id === project.id).length,
        [t('reports.export.headers.bugs')]: allBugs.value.filter(b => b.project_id === project.id).length,
        [t('reports.export.headers.createdDate')]: formatDate(project.created_at)
      }))
      filename = t('reports.export.filename.projects', { timestamp: new Date().toISOString().split('T')[0] })
      break
      
    case 'features':
      data = filteredFeatures.value.map(feature => ({
        [t('reports.export.headers.featureId')]: feature.featureId,
        [t('reports.export.headers.title')]: feature.title,
        [t('reports.export.headers.project')]: getProjectName(feature.project_id),
        [t('reports.export.headers.status')]: feature.status,
        [t('reports.export.headers.priority')]: feature.priority,
        [t('reports.export.headers.developer')]: getUserName(feature.dev_assignee),
        [t('reports.export.headers.qa')]: getUserName(feature.qa_assignee),
        [t('reports.export.headers.startDate')]: formatDate(feature.start_date),
        [t('reports.export.headers.dueDate')]: formatDate(feature.due_date),
        [t('reports.export.headers.createdDate')]: formatDate(feature.created_at)
      }))
      filename = t('reports.export.filename.features', { timestamp: new Date().toISOString().split('T')[0] })
      break
      
    case 'testCases':
      data = filteredTestCases.value.map(tc => ({
        [t('reports.export.headers.testCaseId')]: tc.testCaseId,
        [t('reports.export.headers.title')]: tc.title || tc.name,
        [t('reports.export.headers.project')]: getProjectName(tc.project_id),
        [t('reports.export.headers.type')]: tc.type || tc.test_type,
        [t('reports.export.headers.caseType')]: tc.case_type,
        [t('reports.export.headers.priority')]: tc.priority,
        [t('reports.export.headers.status')]: tc.status,
        [t('reports.export.headers.automationStatus')]: tc.automation_status,
        [t('reports.export.headers.createdDate')]: formatDate(tc.created_at)
      }))
      filename = t('reports.export.filename.testCases', { timestamp: new Date().toISOString().split('T')[0] })
      break
      
    case 'testRuns':
      data = filteredTestRuns.value.map(tr => ({
        [t('reports.export.headers.testRunId')]: tr.id,
        [t('reports.export.headers.title')]: tr.name,
        [t('reports.export.headers.project')]: getProjectName(tr.project_id),
        [t('reports.export.headers.status')]: tr.status || t('reports.export.noStatus'),
        [t('reports.export.headers.environment')]: tr.environment || '-',
        [t('reports.export.headers.progress')]: `${tr.progress || 0}%`,
        [t('reports.export.headers.createdDate')]: formatDate(tr.created_at)
      }))
      filename = t('reports.export.filename.testRuns', { timestamp: new Date().toISOString().split('T')[0] })
      break
      
    case 'bugs':
      data = filteredBugs.value.map(bug => ({
        [t('reports.export.headers.bugId')]: bug.bugId,
        [t('reports.export.headers.title')]: bug.title,
        [t('reports.export.headers.project')]: getProjectName(bug.project_id),
        [t('reports.export.headers.type')]: bug.type,
        [t('reports.export.headers.status')]: bug.status,
        [t('reports.export.headers.severity')]: bug.severity,
        [t('reports.export.headers.priority')]: bug.priority,
        [t('reports.export.headers.environment')]: bug.environment,
        [t('reports.export.headers.createdDate')]: formatDate(bug.created_at)
      }))
      filename = t('reports.export.filename.bugs', { timestamp: new Date().toISOString().split('T')[0] })
      break
      
    default:
      showError(t('reports.invalidSection'))
      return
  }
  
  if (data.length === 0) {
    showError(t('reports.noDataForSection', { section }))
    return
  }
  
  // Convert to CSV
  headers = Object.keys(data[0])
  const csvContent = [
    `"${t('reports.export.sectionReportTitle', { section: section.toUpperCase(), date: new Date().toLocaleString() })}"`,
    `"${t('reports.export.totalRecords', { count: data.length })}"`,
    '', // Empty line
    headers.map(h => `"${h}"`).join(','),
    ...data.map(row => 
      headers.map(header => {
        const value = row[header] || ''
        return typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n')) 
          ? `"${value.replace(/"/g, '""')}"` 
          : `"${value}"`
      }).join(',')
    )
  ].join('\n')
  
  // Download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  window.URL.revokeObjectURL(url)
}

// Helper to get user name
const getUserName = (userId) => {
  if (!userId) return '-'
  const user = allUsers.value.find(u => u.id === userId)
  return user ? user.name : userId
}

// Watch for filter changes and reset pagination to page 1
watch(() => sectionFilters.value.features, () => {
  pagination.value.features.currentPage = 1
}, { deep: true })

watch(() => sectionFilters.value.testCases, () => {
  pagination.value.testCases.currentPage = 1
}, { deep: true })

watch(() => sectionFilters.value.testRuns, () => {
  pagination.value.testRuns.currentPage = 1
}, { deep: true })

watch(() => sectionFilters.value.bugs, () => {
  pagination.value.bugs.currentPage = 1
}, { deep: true })

// Watch for tab changes and update URL query parameter
watch(activeTab, (newTab) => {
  // Only update URL if the tab is different from current query param
  if (route.query.tab !== newTab) {
    const query = { ...route.query, tab: newTab }
    
    // Remove 'view' parameter when switching away from accessibility tab
    if (newTab !== REPORT_TABS.ACCESSIBILITY && query.view) {
      delete query.view
    }
    
    router.replace({ query })
  }
})

// Watch for URL query changes and update activeTab
watch(
  () => route.query.tab,
  (newTabParam) => {
    // Validate and update activeTab if URL changes (e.g., browser back/forward)
    const validTabs = Object.values(REPORT_TABS)
    if (newTabParam && validTabs.includes(newTabParam)) {
      if (activeTab.value !== newTabParam) {
        activeTab.value = newTabParam
      }
    } else if (!newTabParam) {
      // If tab param is removed from URL, go to overview
      if (activeTab.value !== REPORT_TABS.OVERVIEW) {
        activeTab.value = REPORT_TABS.OVERVIEW
      }
    }
  }
)

// Paginated filtered features data
const paginatedFilteredFeatures = computed(() => {
  const start = (pagination.value.features.currentPage - 1) * pagination.value.features.pageSize
  const end = start + pagination.value.features.pageSize
  return filteredFeatures.value.slice(start, end)
})

// Features Filtered Table Columns (HLDataTable format)
const featuresFilteredTableColumns = computed(() => [
  {
    id: 'featureId',
    accessorKey: 'featureId',
    header: t('reports.table.id'),
    size: 100,
    enableResizing: true,
    enableSorting: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const row = props.row.original
      return h('a', { 
        href: `/projects/${row.project_id}/features/${row.id}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer'
      }, row.featureId)
    }
  },
  {
    id: 'title',
    accessorKey: 'title',
    header: t('reports.table.title'),
    size: 180,
    enableResizing: true,
    enableSorting: true,
    meta: { align: 'left', headerAlign: 'left' },
    cellFormatter: (props) => {
      const row = props.row.original
      const title = row.title || ''
      const link = h('a', { 
        href: `/projects/${row.project_id}/features/${row.id}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer line-clamp-2'
      }, title)
      
      // Check if text would be truncated (use CSS to detect)
      if (title.length > 40) {
        return h(HLPopover, {
          trigger: 'hover',
          placement: 'top',
          contentClass: '!p-3 !max-w-xs'
        }, {
          trigger: () => link,
          default: () => h('div', { class: 'text-sm text-gray-900 dark:text-gray-100' }, title)
        })
      }
      return link
    }
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: t('reports.table.status'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const status = props.row.original.status || 'draft'
      const statusColors = STATUS_COLORS[status.toUpperCase()] || STATUS_COLORS.DRAFT
      return h('span', { 
        class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors.badge}`
      }, formatDisplayText(status))
    }
  },
  {
    id: 'priority',
    accessorKey: 'priority',
    header: t('reports.table.priority'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const priority = props.row.original.priority || 'medium'
      const priorityColors = {
        'critical': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
        'high': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
        'medium': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
        'low': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
      }
      return h('span', { 
        class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[priority] || priorityColors.medium}`
      }, formatDisplayText(priority))
    }
  },
  {
    id: 'project_id',
    accessorKey: 'project_id',
    header: t('reports.table.project'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const projectId = props.row.original.project_id
      const projectName = getProjectName(projectId)
      return h('a', { 
        href: `/projects/${projectId}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer'
      }, projectName || 'N/A')
    }
  },
  {
    id: 'dev_assignee',
    accessorKey: 'dev_assignee',
    header: t('reports.table.developer'),
    size: 120,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const devId = props.row.original.dev_assignee
      const devName = devId ? getUserName(devId) : t('reports.filter.options.unassigned')
      return h('div', { class: 'text-sm text-gray-900 dark:text-gray-100' }, devName)
    }
  },
  {
    id: 'qa_assignee',
    accessorKey: 'qa_assignee',
    header: t('reports.table.qa'),
    size: 120,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const qaId = props.row.original.qa_assignee
      const qaName = qaId ? getUserName(qaId) : t('reports.filter.options.unassigned')
      return h('div', { class: 'text-sm text-gray-900 dark:text-gray-100' }, qaName)
    }
  }
])

// Paginated filtered test cases data
const paginatedFilteredTestCases = computed(() => {
  const start = (pagination.value.testCases.currentPage - 1) * pagination.value.testCases.pageSize
  const end = start + pagination.value.testCases.pageSize
  return filteredTestCases.value.slice(start, end)
})

// Test Cases Filtered Table Columns (HLDataTable format)
const testCasesFilteredTableColumns = computed(() => [
  {
    id: 'testCaseId',
    accessorKey: 'testCaseId',
    header: t('reports.table.id'),
    size: 100,
    enableResizing: true,
    enableSorting: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const row = props.row.original
      return h('a', { 
        href: `/projects/${row.project_id}/test-cases/${row.id}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer'
      }, row.testCaseId)
    }
  },
  {
    id: 'title',
    accessorKey: 'title',
    header: t('reports.table.title'),
    size: 200,
    enableResizing: true,
    enableSorting: true,
    meta: { align: 'left', headerAlign: 'left' },
    cellFormatter: (props) => {
      const row = props.row.original
      const title = row.title || row.name || ''
      const link = h('a', { 
        href: `/projects/${row.project_id}/test-cases/${row.id}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer line-clamp-2'
      }, title)
      
      // Check if text would be truncated (use CSS to detect)
      if (title.length > 50) {
        return h(HLPopover, {
          trigger: 'hover',
          placement: 'top',
          contentClass: '!p-3 !max-w-xs'
        }, {
          trigger: () => link,
          default: () => h('div', { class: 'text-sm text-gray-900 dark:text-gray-100' }, title)
        })
      }
      return link
    }
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: t('reports.table.status'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const status = props.row.original.status || 'draft'
      const statusColors = STATUS_COLORS[status.toUpperCase()] || STATUS_COLORS.DRAFT
      return h('span', { 
        class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors.badge}`
      }, formatDisplayText(status))
    }
  },
  {
    id: 'priority',
    accessorKey: 'priority',
    header: t('reports.table.priority'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const priority = props.row.original.priority || 'medium'
      const priorityColors = {
        'critical': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
        'high': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
        'medium': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
        'low': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
      }
      return h('span', { 
        class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[priority] || priorityColors.medium}`
      }, formatDisplayText(priority))
    }
  },
  {
    id: 'type',
    accessorKey: 'type',
    header: t('reports.table.type'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const type = props.row.original.type || 'functional'
      const typeColors = {
        'functional': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        'regression': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
        'smoke': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
        'integration': 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300'
      }
      return h('span', { 
        class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeColors[type] || typeColors.functional}`
      }, formatDisplayText(type))
    }
  },
  {
    id: 'automation_status',
    accessorKey: 'automation_status',
    header: t('reports.table.automationStatus'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const status = props.row.original.automation_status || 'manual'
      const automationColors = {
        'manual': 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-300',
        'automated': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
        'in_progress': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300'
      }
      return h('span', { 
        class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${automationColors[status] || automationColors.manual}`
      }, formatDisplayText(status))
    }
  },
  {
    id: 'project_id',
    accessorKey: 'project_id',
    header: t('reports.table.project'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const projectId = props.row.original.project_id
      const projectName = getProjectName(projectId)
      return h('a', { 
        href: `/projects/${projectId}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer'
      }, projectName || 'N/A')
    }
  }
])

// Paginated filtered test runs data
const paginatedFilteredTestRuns = computed(() => {
  const start = (pagination.value.testRuns.currentPage - 1) * pagination.value.testRuns.pageSize
  const end = start + pagination.value.testRuns.pageSize
  return filteredTestRuns.value.slice(start, end)
})

// Test Runs Filtered Table Columns (HLDataTable format)
const testRunsFilteredTableColumns = computed(() => [
  {
    id: 'testRunId',
    accessorKey: 'testrun_id',
    header: t('reports.table.id'),
    size: 100,
    enableResizing: true,
    enableSorting: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const row = props.row.original
      return h('a', { 
        href: `/projects/${row.project_id}/test-runs/${row.id}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer'
      }, row.testrun_id)
    }
  },
  {
    id: 'title',
    accessorKey: 'title',
    header: t('reports.table.title'),
    size: 180,
    enableResizing: true,
    enableSorting: true,
    meta: { align: 'left', headerAlign: 'left' },
    cellFormatter: (props) => {
      const row = props.row.original
      const title = row.title || row.name || ''
      return h('a', { 
        href: `/projects/${row.project_id}/test-runs/${row.id}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer line-clamp-2'
      }, title)
    }
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: t('reports.table.status'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const status = props.row.original.status || 'draft'
      const statusColors = STATUS_COLORS[status.toUpperCase()] || STATUS_COLORS.DRAFT
      return h('span', { 
        class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors.badge}`
      }, formatDisplayText(status))
    }
  },
  {
    id: 'environment',
    accessorKey: 'environment',
    header: t('reports.table.environment'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const environment = props.row.original.environment || 'staging'
      const envColors = {
        'production': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
        'staging': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        'qa': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
        'development': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
        'uat': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
      }
      return h('span', { 
        class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${envColors[environment] || envColors.staging}`
      }, formatDisplayText(environment))
    }
  },
  {
    id: 'project_id',
    accessorKey: 'project_id',
    header: t('reports.table.project'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const projectId = props.row.original.project_id
      const projectName = getProjectName(projectId)
      return h('a', { 
        href: `/projects/${projectId}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer'
      }, projectName || 'N/A')
    }
  }
])

// Accessibility Stats - All metrics in one grid
const accessibilityStats = computed(() => {
  // Use filtered statistics (client-side filtering like other sections)
  const stats = accessibilityFilteredStats.value
  const impact = stats.by_impact || { critical: 0, serious: 0, moderate: 0, minor: 0 }
  
  return [
    {
      label: 'Total Issues',
      value: stats.total_issues || 0,
      subtext: 'All impacts',
      iconPath: getIconPath('shield'),
      colors: STATUS_COLORS.TOTAL
    },
    {
      label: 'Unique Pages',
      value: stats.unique_pages || 0,
      subtext: 'Tested',
      iconPath: getIconPath('document'),
      colors: STAT_CARD_COLORS.TEST_RUNS
    },
    {
      label: 'Test Types',
      value: stats.unique_tests || 0,
      subtext: 'Unique',
      iconPath: getIconPath('clipboardList'),
      colors: STAT_CARD_COLORS.TEST_CASES
    },
    {
      label: 'Critical',
      value: impact.critical || 0,
      subtext: 'High priority',
      iconPath: STATUS_COLORS.CRITICAL.icon,
      colors: STATUS_COLORS.CRITICAL
    },
    {
      label: 'Serious',
      value: impact.serious || 0,
      subtext: 'Important',
      iconPath: STATUS_COLORS.HIGH.icon,
      colors: STATUS_COLORS.HIGH
    },
    {
      label: 'Moderate',
      value: impact.moderate || 0,
      subtext: 'Medium',
      iconPath: STATUS_COLORS.MEDIUM.icon,
      colors: STATUS_COLORS.MEDIUM
    },
    {
      label: 'Minor',
      value: impact.minor || 0,
      subtext: 'Low priority',
      iconPath: STATUS_COLORS.LOW.icon,
      colors: STATUS_COLORS.LOW
    }
  ]
})

// Accessibility Impact Distribution Chart Data
const accessibilityImpactChartData = computed(() => {
  // Use filtered statistics (client-side filtering)
  const impact = accessibilityFilteredStats.value.by_impact
  
  return [
    { label: 'Critical', value: impact.critical || 0, color: 'rgba(239, 68, 68, 0.8)' },
    { label: 'Serious', value: impact.serious || 0, color: 'rgba(249, 115, 22, 0.8)' },
    { label: 'Moderate', value: impact.moderate || 0, color: 'rgba(234, 179, 8, 0.8)' },
    { label: 'Minor', value: impact.minor || 0, color: 'rgba(34, 197, 94, 0.8)' }
  ]
})

// Accessibility Top Pages Chart Data (calculated from filtered issues)
const accessibilityTopPagesData = computed(() => {
  const issues = accessibilityFilteredIssues.value
  if (!issues || issues.length === 0) {
    return []
  }
  
  // Count issues by page_url from filtered issues
  const pageCounts = {}
  issues.forEach(issue => {
    const page = issue.page_url
    if (page) {
      pageCounts[page] = (pageCounts[page] || 0) + 1
    }
  })
  
  // Sort by count and get top 10
  const sortedPages = Object.entries(pageCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
  
  return sortedPages.map(([page, count]) => ({
    label: page.length > 50 ? page.substring(0, 47) + '...' : page,
    value: count,
    fullLabel: page
  }))
})

// Accessibility Top Tests Chart Data (calculated from filtered issues)
const accessibilityTopTestsData = computed(() => {
  const issues = accessibilityFilteredIssues.value
  if (!issues || issues.length === 0) {
    return []
  }
  
  // Count issues by test_name from filtered issues
  const testCounts = {}
  issues.forEach(issue => {
    const test = issue.test_name
    if (test) {
      testCounts[test] = (testCounts[test] || 0) + 1
    }
  })
  
  // Sort by count and get top 10
  const sortedTests = Object.entries(testCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
  
  return sortedTests.map(([test, count]) => ({
    label: test.length > 40 ? test.substring(0, 37) + '...' : test,
    value: count,
    fullLabel: test
  }))
})

const accessibilityTopNodesData = computed(() => {
  const issues = accessibilityFilteredIssues.value
  if (!issues || issues.length === 0) {
    return []
  }
  
  // Count issues by node from filtered issues
  const nodeCounts = {}
  issues.forEach(issue => {
    const node = issue.node
    if (node) {
      nodeCounts[node] = (nodeCounts[node] || 0) + 1
    }
  })
  
  // Sort by count and get top 10
  const sortedNodes = Object.entries(nodeCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
  
  return sortedNodes.map(([node, count]) => ({
    label: node.length > 50 ? node.substring(0, 47) + '...' : node,
    value: count,
    fullLabel: node
  }))
})

// Accessibility Assignee Chart Data (from filtered statistics)
const accessibilityAssigneeChartData = computed(() => {
  const assigneeData = accessibilityFilteredStats.value.by_assignee
  
  // Define color palette for assignees
  const colorPalette = [
    'rgba(59, 130, 246, 0.8)',   // Blue
    'rgba(16, 185, 129, 0.8)',   // Green
    'rgba(245, 158, 11, 0.8)',   // Amber
    'rgba(239, 68, 68, 0.8)',    // Red
    'rgba(168, 85, 247, 0.8)',   // Purple
    'rgba(236, 72, 153, 0.8)',   // Pink
    'rgba(14, 165, 233, 0.8)',   // Sky
    'rgba(234, 179, 8, 0.8)',    // Yellow
    'rgba(20, 184, 166, 0.8)',   // Teal
    'rgba(249, 115, 22, 0.8)',   // Orange
  ]
  
  // Convert to array format for chart
  const chartData = Object.entries(assigneeData).map(([name, count]) => ({
    label: name === 'unassigned' ? 'Unassigned' : name,
    value: count,
    name: name
  }))
  
  // Sort by count descending (show ALL - legend will handle Top 5 + more)
  const sortedData = chartData.sort((a, b) => b.value - a.value)
  
  // Assign colors to all assignees
  return sortedData.map((item, index) => ({
    ...item,
    color: item.name === 'unassigned' ? 'rgba(156, 163, 175, 0.8)' : colorPalette[index % colorPalette.length]
  }))
})

// Accessibility Status Distribution Chart Data
const accessibilityStatusChartData = computed(() => {
  const statusData = accessibilityFilteredStats.value.by_status
  
  // Define status colors matching the ACCESSIBILITY_STATUS_COLORS
  const statusColors = {
    open: 'rgba(59, 130, 246, 0.8)',        // Blue
    reopened: 'rgba(249, 115, 22, 0.8)',    // Orange
    in_progress: 'rgba(234, 179, 8, 0.8)',  // Yellow
    blocked: 'rgba(239, 68, 68, 0.8)',      // Red
    closed: 'rgba(16, 185, 129, 0.8)',      // Green
  }
  
  const statusLabels = {
    open: 'Open',
    reopened: 'Reopened',
    in_progress: 'In Progress',
    blocked: 'Blocked',
    closed: 'Closed'
  }
  
  // Convert to array format for chart
  return Object.entries(statusData)
    .filter(([_, count]) => count > 0) // Only show statuses with issues
    .map(([status, count]) => ({
      label: statusLabels[status] || status,
      value: count,
      color: statusColors[status] || 'rgba(156, 163, 175, 0.8)'
    }))
    .sort((a, b) => b.value - a.value) // Sort by count descending
})

// Accessibility Issue ID Distribution Chart Data (All Issue IDs)
const accessibilityIssueIdChartData = computed(() => {
  const issues = accessibilityFilteredIssues.value
  console.log('[Issue Types Chart] Computing, filtered data length:', issues?.length)
  
  if (!issues || issues.length === 0) {
    console.log('[Issue Types Chart] No filtered data available')
    return []
  }
  
  // Count issues by issue_id from filtered issues
  const issueIdCounts = {}
  issues.forEach(issue => {
    const issueId = issue.issue_id
    if (issueId) {
      issueIdCounts[issueId] = (issueIdCounts[issueId] || 0) + 1
    }
  })
  
  console.log('[Issue Types Chart] Issue ID counts:', Object.keys(issueIdCounts).length, 'types')
  
  const totalIssues = issues.length
  
  // Sort by count descending (show ALL - legend will handle Top 5 + more)
  const sortedIssueIds = Object.entries(issueIdCounts)
    .sort((a, b) => b[1] - a[1])
  
  // Generate distinct colors for each issue type (extended palette)
  const generateColor = (index) => {
    const hue = (index * 137.5) % 360 // Golden angle for better distribution
    return `hsla(${hue}, 65%, 55%, 0.8)`
  }
  
  return sortedIssueIds.map(([issueId, count], index) => {
    const percentage = ((count / totalIssues) * 100).toFixed(1)
    return {
      label: issueId,
      value: count,
      percentage: percentage,
      color: generateColor(index),
      // Custom tooltip text
      tooltip: `${issueId}\n${count} issues (${percentage}%)`
    }
  })
})

// Accessibility Trend Charts Data
const accessibilityTotalIssuesTrendData = computed(() => {
  if (!accessibilityTrendData.value || !accessibilityTrendData.value.total_issues_trend) {
    return []
  }
  
  return accessibilityTrendData.value.total_issues_trend.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    value: item.value
  }))
})

const accessibilityImpactTrendData = computed(() => {
  if (!accessibilityTrendData.value || !accessibilityTrendData.value.impact_trends) {
    return []
  }
  
  return accessibilityTrendData.value.impact_trends.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    critical: item.critical,
    serious: item.serious,
    moderate: item.moderate,
    minor: item.minor
  }))
})

const accessibilityNewVsFixedTrendData = computed(() => {
  if (!accessibilityTrendData.value || !accessibilityTrendData.value.new_vs_fixed_trend) {
    return []
  }
  
  return accessibilityTrendData.value.new_vs_fixed_trend.map(item => ({
    date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    new_issues: item.new_issues,
    fixed_issues: item.fixed_issues,
    net_change: item.net_change
  }))
})

// Accessibility Filter Options
const accessibilityPageUrlOptions = computed(() => {
  if (!accessibilityAllPageUrls.value || accessibilityAllPageUrls.value.length === 0) {
    return []
  }
  
  return accessibilityAllPageUrls.value.sort().map(pageUrl => ({
    value: pageUrl,
    label: pageUrl.length > 60 ? pageUrl.substring(0, 57) + '...' : pageUrl
  }))
})

const accessibilityTestNameOptions = computed(() => {
  if (!accessibilityAllTestNames.value || accessibilityAllTestNames.value.length === 0) {
    return []
  }
  
  return accessibilityAllTestNames.value.sort().map(testName => ({
    value: testName,
    label: testName
  }))
})

const accessibilityIssueIdOptions = computed(() => {
  if (!accessibilityAllIssueIds.value || accessibilityAllIssueIds.value.length === 0) {
    return []
  }
  
  return accessibilityAllIssueIds.value.sort().map(issueId => ({
    value: issueId,
    label: issueId
  }))
})

const accessibilityNodeOptions = computed(() => {
  if (!accessibilityAllNodes.value || accessibilityAllNodes.value.length === 0) {
    return []
  }
  
  return accessibilityAllNodes.value.sort().map(node => ({
    value: node,
    label: node.length > 100 ? node.substring(0, 97) + '...' : node
  }))
})

const accessibilityAssigneeOptions = computed(() => {
  const options = [
    { value: 'unassigned', label: 'Unassigned' },
    ...accessibilityAllUsersForAssignment.value.map(u => ({ value: u.id, label: u.name }))
  ]
  return options.sort((a, b) => a.label.localeCompare(b.label))
})

// Filtered issues based on current view's filters (client-side filtering like other sections)
const accessibilityFilteredIssues = computed(() => {
  let issues = accessibilityAllIssuesForCharts.value || []
  
  // Determine which filters to use based on current view
  // Overview and Summary tabs share the same filters, Detailed has its own
  const filters = (accessibilityView.value === ACCESSIBILITY_TABS.OVERVIEW || accessibilityView.value === ACCESSIBILITY_TABS.SUMMARY)
    ? accessibilitySummaryFilters.value 
    : accessibilityIssuesFilters.value
  
  // Apply Impact filter
  if (filters.impact && filters.impact.length > 0) {
    issues = issues.filter(i => filters.impact.includes(i.impact))
  }
  
  // Apply Status filter
  if (filters.status && filters.status.length > 0) {
    issues = issues.filter(i => filters.status.includes(i.status))
  }
  
  // Apply Assigned To filter
  if (filters.assignedTo && filters.assignedTo.length > 0) {
    issues = issues.filter(i => {
      if (filters.assignedTo.includes('unassigned')) {
        return !i.assigned_to || i.assigned_to === null || i.assigned_to === ''
      }
      return filters.assignedTo.includes(i.assigned_to)
    })
  }
  
  // Apply Issue Type filter (for Overview tab)
  if (filters.issueId && filters.issueId.length > 0) {
    issues = issues.filter(i => filters.issueId.includes(i.issue_id))
  }
  
  // Apply Test Name filter (for Overview tab)
  if (filters.testName && filters.testName.length > 0) {
    issues = issues.filter(i => filters.testName.includes(i.test_name))
  }
  
  // Apply Detailed view specific filters
  if (accessibilityView.value === ACCESSIBILITY_TABS.DETAILED) {
    // Page URL filter
    if (filters.pageUrl && filters.pageUrl.length > 0) {
      issues = issues.filter(i => filters.pageUrl.includes(i.page_url))
    }
    
    // Node filter
    if (filters.node && filters.node.length > 0) {
      issues = issues.filter(i => filters.node.includes(i.node))
    }
    
    // Search filter
    if (filters.search && filters.search.trim()) {
      const searchTerm = filters.search.toLowerCase()
      issues = issues.filter(i => 
        i.test_name?.toLowerCase().includes(searchTerm) ||
        i.page_url?.toLowerCase().includes(searchTerm) ||
        i.issue_id?.toLowerCase().includes(searchTerm) ||
        i.description?.toLowerCase().includes(searchTerm) ||
        i.node?.toLowerCase().includes(searchTerm)
      )
    }
  }
  
  return issues
})

// Computed statistics from filtered issues (like other sections)
const accessibilityFilteredStats = computed(() => {
  const issues = accessibilityFilteredIssues.value
  
  // Calculate impact breakdown
  const impactBreakdown = {
    critical: issues.filter(i => i.impact === 'critical').length,
    serious: issues.filter(i => i.impact === 'serious').length,
    moderate: issues.filter(i => i.impact === 'moderate').length,
    minor: issues.filter(i => i.impact === 'minor').length,
  }
  
  // Calculate status breakdown
  const statusBreakdown = {
    open: issues.filter(i => i.status === 'open').length,
    reopened: issues.filter(i => i.status === 'reopened').length,
    in_progress: issues.filter(i => i.status === 'in_progress').length,
    blocked: issues.filter(i => i.status === 'blocked').length,
    closed: issues.filter(i => i.status === 'closed').length,
  }
  
  // Calculate assignee breakdown
  const assigneeBreakdown = {}
  let unassignedCount = 0
  
  issues.forEach(issue => {
    if (!issue.assigned_to || issue.assigned_to === null) {
      unassignedCount++
    } else {
      const key = issue.assigned_to_name || issue.assigned_to
      assigneeBreakdown[key] = (assigneeBreakdown[key] || 0) + 1
    }
  })
  
  if (unassignedCount > 0) {
    assigneeBreakdown['unassigned'] = unassignedCount
  }
  
  return {
    total_issues: issues.length,
    by_impact: impactBreakdown,
    by_status: statusBreakdown,
    by_assignee: assigneeBreakdown,
    unique_pages: new Set(issues.map(i => i.page_url)).size,
    unique_tests: new Set(issues.map(i => i.test_name)).size,
  }
})

// Check if summary view has active filters
const hasSummaryFiltersActive = computed(() => {
  return (
    accessibilitySummaryFilters.value.impact.length > 0 ||
    accessibilitySummaryFilters.value.status.length > 0 ||
    accessibilitySummaryFilters.value.assignedTo.length > 0 ||
    accessibilitySummaryFilters.value.dateRange !== 'all'
  )
})

// Count of active filters for Overview tab SectionFilter
const accessibilityOverviewActiveFilters = computed(() => {
  let count = 0
  if (accessibilitySummaryFilters.value.impact.length > 0) count++
  if (accessibilitySummaryFilters.value.status.length > 0) count++
  if (accessibilitySummaryFilters.value.assignedTo.length > 0) count++
  if (accessibilitySummaryFilters.value.issueId.length > 0) count++
  if (accessibilitySummaryFilters.value.testName.length > 0) count++
  if (accessibilitySummaryFilters.value.dateRange !== 'all') count++
  return count
})

// Clear all summary view filters (Overview tab)
const clearSummaryFilters = () => {
  accessibilitySummaryFilters.value.impact = []
  accessibilitySummaryFilters.value.status = []
  accessibilitySummaryFilters.value.assignedTo = []
  accessibilitySummaryFilters.value.issueId = []
  accessibilitySummaryFilters.value.testName = []
  accessibilitySummaryFilters.value.dateRange = 'all'
  accessibilitySummaryFilters.value.customStartDate = null
  accessibilitySummaryFilters.value.customEndDate = null
}

// Count of active filters for Summary table SectionFilter
const accessibilitySummaryTableActiveFilters = computed(() => {
  let count = 0
  if (accessibilitySummarySearch.value) count++
  if (accessibilitySummaryTableFilters.value.impact.length > 0) count++
  return count
})

// Total test summary count (before filtering)
const accessibilityTestSummaryTotal = computed(() => {
  return accessibilityTestSummaryData.value?.length || 0
})

// Clear Summary table filters
const clearSummaryTableFilters = () => {
  accessibilitySummarySearch.value = ''
  accessibilitySummaryTableFilters.value.impact = []
}

// Check if all issues view has active filters
const hasAllIssuesFiltersActive = computed(() => {
  return (
    accessibilityIssuesFilters.value.search.length > 0 ||
    accessibilityIssuesFilters.value.impact.length > 0 ||
    accessibilityIssuesFilters.value.status.length > 0 ||
    accessibilityIssuesFilters.value.pageUrl.length > 0 ||
    accessibilityIssuesFilters.value.testName.length > 0 ||
    accessibilityIssuesFilters.value.issueId.length > 0 ||
    accessibilityIssuesFilters.value.node.length > 0 ||
    accessibilityIssuesFilters.value.assignedTo.length > 0
  )
})

// Clear all issues view filters
const clearAllIssuesFilters = () => {
  accessibilityIssuesFilters.value.search = ''
  accessibilityIssuesFilters.value.impact = []
  accessibilityIssuesFilters.value.status = []
  accessibilityIssuesFilters.value.pageUrl = []
  accessibilityIssuesFilters.value.testName = []
  accessibilityIssuesFilters.value.issueId = []
  accessibilityIssuesFilters.value.node = []
  accessibilityIssuesFilters.value.assignedTo = []
  
  // Reset pagination and reload issues with cleared filters
  accessibilityIssuesPagination.value.currentPage = 1
  loadAccessibilityIssues()
}

// Accessibility Test Summary (grouped by test_name with status counts)
const accessibilityTestSummary = computed(() => {
  // Get all issues for charts (contains all issues without pagination)
  const allIssues = accessibilityAllIssuesForCharts.value || []
  
  // If no issues, fall back to backend data
  if (allIssues.length === 0) {
    const data = accessibilityTestSummaryData.value || []
    if (!accessibilitySummarySearch.value) {
      return data
    }
    const search = accessibilitySummarySearch.value.toLowerCase()
    return data.filter(item => item.test_name.toLowerCase().includes(search))
  }
  
  // Apply impact filter
  let filteredIssues = allIssues
  if (accessibilitySummaryTableFilters.value.impact && accessibilitySummaryTableFilters.value.impact.length > 0) {
    filteredIssues = filteredIssues.filter(issue => 
      accessibilitySummaryTableFilters.value.impact.includes(issue.impact)
    )
  }
  
  // Aggregate by test_name
  const summaryMap = new Map()
  filteredIssues.forEach(issue => {
    const testName = issue.test_name || 'Unknown'
    if (!summaryMap.has(testName)) {
      summaryMap.set(testName, {
        test_name: testName,
        total: 0,
        open: 0,
        reopened: 0,
        in_progress: 0,
        blocked: 0,
        closed: 0
      })
    }
    const summary = summaryMap.get(testName)
    summary.total++
    const status = issue.status || 'open'
    if (status === 'open') summary.open++
    else if (status === 'reopened') summary.reopened++
    else if (status === 'in_progress') summary.in_progress++
    else if (status === 'blocked') summary.blocked++
    else if (status === 'closed') summary.closed++
  })
  
  // Convert to array and sort by total descending
  let result = Array.from(summaryMap.values()).sort((a, b) => b.total - a.total)
  
  // Apply search filter
  if (accessibilitySummarySearch.value) {
    const search = accessibilitySummarySearch.value.toLowerCase()
    result = result.filter(item => item.test_name.toLowerCase().includes(search))
  }
  
  return result
})

// Paginated summary data
const accessibilitySummaryPaginatedData = computed(() => {
  const start = (accessibilitySummaryPagination.value.currentPage - 1) * accessibilitySummaryPagination.value.pageSize
  const end = start + accessibilitySummaryPagination.value.pageSize
  return accessibilityTestSummary.value.slice(start, end)
})

// Navigate to Detailed View with pre-filled filters
const navigateToDetailedViewWithFilters = async (testName, status = null) => {
  // Clear existing filters first
  accessibilityIssuesFilters.value.search = ''
  accessibilityIssuesFilters.value.impact = []
  accessibilityIssuesFilters.value.status = status ? [status] : []
  accessibilityIssuesFilters.value.pageUrl = []
  accessibilityIssuesFilters.value.testName = [testName]
  accessibilityIssuesFilters.value.issueId = []
  accessibilityIssuesFilters.value.node = []
  accessibilityIssuesFilters.value.assignedTo = []
  
  // Reset pagination
  accessibilityIssuesPagination.value.currentPage = 1
  
  // Navigate to detailed view (uses router.push which is async)
  accessibilityView.value = ACCESSIBILITY_TABS.DETAILED
  
  // Wait for the next tick to ensure the view has changed
  await nextTick()
  
  // Reload issues with new filters
  await loadAccessibilityIssues()
}

// Accessibility Summary Table Columns
const accessibilitySummaryColumns = computed(() => [
  {
    id: 'test_name',
    accessorKey: 'test_name',
    header: 'Test Name',
    size: 400,
    enableSorting: true,
    meta: { align: 'left', headerAlign: 'left' },
    cellFormatter: (props) => {
      return h('div', {
        class: 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 font-medium',
        onClick: () => navigateToDetailedViewWithFilters(props.row.original.test_name)
      }, props.row.original.test_name)
    }
  },
  {
    id: 'total',
    accessorKey: 'total',
    header: 'Total',
    size: 100,
    enableSorting: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      return h('div', {
        class: 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 text-center',
        onClick: () => navigateToDetailedViewWithFilters(props.row.original.test_name)
      }, props.row.original.total)
    }
  },
  {
    id: 'open',
    accessorKey: 'open',
    header: 'Open',
    size: 100,
    enableSorting: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const count = props.row.original.open
      return h('div', {
        class: count > 0 ? 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 text-center' : 'text-center text-gray-400',
        onClick: () => count > 0 && navigateToDetailedViewWithFilters(props.row.original.test_name, 'open')
      }, count)
    }
  },
  {
    id: 'reopened',
    accessorKey: 'reopened',
    header: 'Reopened',
    size: 110,
    enableSorting: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const count = props.row.original.reopened || 0
      return h('div', {
        class: count > 0 ? 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 text-center' : 'text-center text-gray-400',
        onClick: () => count > 0 && navigateToDetailedViewWithFilters(props.row.original.test_name, 'reopened')
      }, count)
    }
  },
  {
    id: 'in_progress',
    accessorKey: 'in_progress',
    header: 'In Progress',
    size: 120,
    enableSorting: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const count = props.row.original.in_progress
      return h('div', {
        class: count > 0 ? 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 text-center' : 'text-center text-gray-400',
        onClick: () => count > 0 && navigateToDetailedViewWithFilters(props.row.original.test_name, 'in_progress')
      }, count)
    }
  },
  {
    id: 'blocked',
    accessorKey: 'blocked',
    header: 'Blocked',
    size: 100,
    enableSorting: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const count = props.row.original.blocked
      return h('div', {
        class: count > 0 ? 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 text-center' : 'text-center text-gray-400',
        onClick: () => count > 0 && navigateToDetailedViewWithFilters(props.row.original.test_name, 'blocked')
      }, count)
    }
  },
  {
    id: 'closed',
    accessorKey: 'closed',
    header: 'Closed',
    size: 100,
    enableSorting: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const count = props.row.original.closed
      return h('div', {
        class: count > 0 ? 'cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 text-center' : 'text-center text-gray-400',
        onClick: () => count > 0 && navigateToDetailedViewWithFilters(props.row.original.test_name, 'closed')
      }, count)
    }
  }
])

// Accessibility Issues Table Columns
const accessibilityIssuesColumns = computed(() => [
  {
    id: 'select',
    header: () => h(HLCheckbox, {
      checked: accessibilitySelectedIssues.value.length === accessibilityIssues.value.length && accessibilityIssues.value.length > 0,
      indeterminate: accessibilitySelectedIssues.value.length > 0 && accessibilitySelectedIssues.value.length < accessibilityIssues.value.length,
      onChange: toggleSelectAllAccessibilityIssues
    }),
    size: 50,
    enableResizing: false,
    enableSorting: false,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const issueId = props.row.original.id
      return h(HLCheckbox, {
        checked: accessibilitySelectedIssues.value.includes(issueId),
        onChange: (event) => {
          const checked = event?.target?.checked ?? event
          if (checked) {
            if (!accessibilitySelectedIssues.value.includes(issueId)) {
              accessibilitySelectedIssues.value.push(issueId)
            }
          } else {
            accessibilitySelectedIssues.value = accessibilitySelectedIssues.value.filter(id => id !== issueId)
          }
        }
      })
    }
  },
  {
    id: 'impact',
    accessorKey: 'impact',
    header: 'Impact',
    size: 110,
    enableResizing: true,
    enableSorting: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const impact = props.row.original.impact
      const impactLower = impact?.toLowerCase() || ''
      const colorMap = {
        critical: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
        serious: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
        moderate: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300',
        minor: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
      }
      return h('div', { class: 'flex items-center justify-center' }, [
        h('span', { 
          class: `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${colorMap[impactLower] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300'}`
        }, ACCESSIBILITY_IMPACT_LABELS[impactLower] || impact)
      ])
    }
  },
  {
    id: 'test_name',
    accessorKey: 'test_name',
    header: 'Test Name',
    size: 200,
    enableResizing: true,
    enableSorting: true,
    meta: { align: 'left', headerAlign: 'left' },
    cellFormatter: (props) => {
      const testName = props.row.original.test_name
      const isLong = testName.length > 50
      return h(HLTooltip, {
        trigger: 'hover',
        placement: 'top',
        disabled: !isLong
      }, {
        trigger: () => h('div', {
          class: 'text-sm text-gray-700 dark:text-gray-300 line-clamp-2',
          style: 'word-wrap: break-word; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.5;'
        }, testName),
        default: () => h('div', { class: 'max-w-md' }, testName)
      })
    }
  },
  {
    id: 'page_url',
    accessorKey: 'page_url',
    header: 'Page URL',
    size: 280,
    enableResizing: true,
    enableSorting: true,
    meta: { align: 'left', headerAlign: 'left' },
    cellFormatter: (props) => {
      const url = props.row.original.page_url
      const isLong = url.length > 80
      return h(HLTooltip, {
        trigger: 'hover',
        placement: 'top',
        disabled: !isLong
      }, {
        trigger: () => h('a', {
          href: url,
          target: '_blank',
          rel: 'noopener noreferrer',
          class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm hover:underline line-clamp-2',
          style: 'word-break: break-all; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; line-height: 1.5;'
        }, url),
        default: () => h('div', { class: 'max-w-md break-all' }, url)
      })
    }
  },
  {
    id: 'issue_id',
    accessorKey: 'issue_id',
    header: 'Issue ID',
    size: 200,
    enableResizing: true,
    enableSorting: true,
    meta: { align: 'left', headerAlign: 'left' },
    cellFormatter: (props) => {
      const issueId = props.row.original.issue_id
      const color = getIssueIdColor(issueId)
      const colorMap = {
        purple: 'bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-300',
        blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
        cyan: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/20 dark:text-cyan-300',
        teal: 'bg-teal-100 text-teal-800 dark:bg-teal-900/20 dark:text-teal-300',
        green: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
        yellow: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300',
        orange: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
        red: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
        pink: 'bg-pink-100 text-pink-800 dark:bg-pink-900/20 dark:text-pink-300',
        indigo: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/20 dark:text-indigo-300'
      }
      return h('span', { 
        class: `inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${colorMap[color] || 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-300'}`
      }, issueId)
    }
  },
  {
    id: 'node',
    accessorKey: 'node',
    header: 'Node',
    size: 300,
    enableResizing: true,
    enableSorting: false,
    meta: { align: 'left', headerAlign: 'left' },
    cellFormatter: (props) => {
      const node = props.row.original.node || ''
      const displayNode = node.length > 80 ? node.substring(0, 77) + '...' : node
      return h(HLTooltip, {
        placement: 'top',
        trigger: 'hover',
        disabled: node.length <= 80,
        'max-width': '600px',
        raw: true
      }, {
        trigger: () => h('div', {
          class: 'text-xs text-gray-600 dark:text-gray-400 font-mono leading-relaxed line-clamp-2',
          style: 'white-space: normal; word-wrap: break-word; line-height: 1.5;'
        }, displayNode),
        default: () => h('div', { 
          class: 'p-2 text-xs font-mono max-h-96 overflow-y-auto',
          style: 'word-break: break-all; white-space: pre-wrap;'
        }, node)
      })
    }
  },
  {
    id: 'description',
    accessorKey: 'description',
    header: 'Description',
    size: 350,
    enableResizing: true,
    enableSorting: false,
    meta: { align: 'left', headerAlign: 'left' },
    cellFormatter: (props) => {
      const description = props.row.original.description
      return h('div', { 
        class: 'text-sm text-gray-700 dark:text-gray-300 leading-relaxed',
        style: 'white-space: normal; word-wrap: break-word; line-height: 1.6;'
      }, 
        description.length > 150 ? description.substring(0, 147) + '...' : description
      )
    }
  },
  {
    id: 'assigned_to',
    accessorKey: 'assigned_to',
    header: 'Assigned To',
    size: 180,
    minSize: 180,
    maxSize: 180,
    enableResizing: false,
    enableSorting: true,
    meta: { align: 'left', headerAlign: 'left' },
    cellFormatter: (props) => {
      const issue = props.row.original
      const assignedUserId = issue.assigned_to || null
      const assignedUserName = issue.assigned_to_name || 'Unassigned'
      const userOptions = [
        { label: 'Unassigned', value: null },
        ...accessibilityAllUsersForAssignment.value.map(u => ({ label: u.name, value: u.id }))
      ]
      
      return h('div', { 
        style: { 
          display: 'flex',
          alignItems: 'center', 
          minHeight: '40px'
        } 
      }, [
        h(HLSelect, {
          value: assignedUserId,
          'onUpdate:value': (userId) => handleInlineAssignment(issue.id, userId),
          options: userOptions,
          placeholder: assignedUserName,
          filterable: true,
          clearable: true,
          style: { width: '100%' }
        })
      ])
    }
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: 'Status',
    size: 140,
    minSize: 140,
    maxSize: 140,
    enableResizing: false,
    enableSorting: true,
    meta: { align: 'left', headerAlign: 'left' },
    cellFormatter: (props) => {
      const issue = props.row.original
      // Map old status values to new ones for backward compatibility
      let status = issue.status || 'open'
      if (status === 'active') status = 'open'
      if (status === 'fixed') status = 'closed'
      if (status === 'ignored') status = 'closed'
      
      return h('div', { 
        style: { 
          width: '100%', 
          minHeight: '40px', 
          display: 'flex', 
          alignItems: 'center',
          paddingRight: '16px'
        } 
      }, [
        h(HLSelect, {
          value: status,
          'onUpdate:value': (newStatus) => handleInlineStatusChange(issue.id, newStatus),
          options: ACCESSIBILITY_STATUS_OPTIONS,
          placeholder: 'Select status',
          style: { width: '120px', minWidth: '120px', maxWidth: '120px' }
        })
      ])
    }
  },
  {
    id: 'history',
    accessorKey: 'id',
    header: 'History',
    size: 80,
    minSize: 80,
    maxSize: 80,
    enableResizing: false,
    enableSorting: false,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const issue = props.row.original
      return h('div', { 
        class: 'history-icon-cell',
        style: { 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '56px'
        } 
      }, [
        h(Icon, {
          name: 'clipboardList',
          size: 'sm',
          class: 'cursor-pointer text-primary-600 hover:text-primary-700',
          onClick: () => viewAssignmentHistory(issue.id)
        })
      ])
    }
  }
])

// Paginated filtered bugs data
const paginatedFilteredBugs = computed(() => {
  const start = (pagination.value.bugs.currentPage - 1) * pagination.value.bugs.pageSize
  const end = start + pagination.value.bugs.pageSize
  return filteredBugs.value.slice(start, end)
})

// Bugs Filtered Table Columns (HLDataTable format)
const bugsFilteredTableColumns = computed(() => [
  {
    id: 'bugId',
    accessorKey: 'bugId',
    header: t('reports.table.id'),
    size: 100,
    enableResizing: true,
    enableSorting: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const row = props.row.original
      return h('a', { 
        href: `/projects/${row.project_id}/bugs/${row.id}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer'
      }, row.bugId)
    }
  },
  {
    id: 'title',
    accessorKey: 'title',
    header: t('reports.table.title'),
    size: 180,
    enableResizing: true,
    enableSorting: true,
    meta: { align: 'left', headerAlign: 'left' },
    cellFormatter: (props) => {
      const row = props.row.original
      const title = row.title || ''
      const link = h('a', { 
        href: `/projects/${row.project_id}/bugs/${row.id}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer line-clamp-2'
      }, title)
      
      // Check if text would be truncated
      if (title.length > 50) {
        return h(HLPopover, {
          trigger: 'hover',
          placement: 'top',
          contentClass: '!p-3 !max-w-xs'
        }, {
          trigger: () => link,
          default: () => h('div', { class: 'text-sm text-gray-900 dark:text-gray-100' }, title)
        })
      }
      return link
    }
  },
  {
    id: 'status',
    accessorKey: 'status',
    header: t('reports.table.status'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const status = props.row.original.status || 'open'
      const statusColors = STATUS_COLORS[status.toUpperCase()] || STATUS_COLORS.OPEN
      return h('span', { 
        class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusColors.badge}`
      }, formatDisplayText(status))
    }
  },
  {
    id: 'type',
    accessorKey: 'type',
    header: t('reports.table.type'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const type = props.row.original.type || 'functional'
      const typeColors = {
        'functional': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        'ui': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
        'performance': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
        'regression': 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300'
      }
      return h('span', { 
        class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeColors[type] || typeColors.functional}`
      }, formatDisplayText(type))
    }
  },
  {
    id: 'severity',
    accessorKey: 'severity',
    header: t('reports.table.severity'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const severity = props.row.original.severity || 'medium'
      const severityColors = {
        'critical': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
        'high': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
        'medium': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
        'low': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
      }
      return h('span', { 
        class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${severityColors[severity] || severityColors.medium}`
      }, formatDisplayText(severity))
    }
  },
  {
    id: 'priority',
    accessorKey: 'priority',
    header: t('reports.table.priority'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const priority = props.row.original.priority || 'medium'
      const priorityColors = {
        'critical': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
        'high': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
        'medium': 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
        'low': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
      }
      return h('span', { 
        class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${priorityColors[priority] || priorityColors.medium}`
      }, formatDisplayText(priority))
    }
  },
  {
    id: 'environment',
    accessorKey: 'environment',
    header: t('reports.table.environment'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const environment = props.row.original.environment || 'staging'
      const envColors = {
        'production': 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
        'staging': 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
        'qa': 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
        'development': 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
        'uat': 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300'
      }
      return h('span', { 
        class: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${envColors[environment] || envColors.staging}`
      }, formatDisplayText(environment))
    }
  },
  {
    id: 'project_id',
    accessorKey: 'project_id',
    header: t('reports.table.project'),
    size: 100,
    enableResizing: true,
    meta: { align: 'center', headerAlign: 'center' },
    cellFormatter: (props) => {
      const projectId = props.row.original.project_id
      const projectName = getProjectName(projectId)
      return h('a', { 
        href: `/projects/${projectId}`,
        target: '_blank',
        rel: 'noopener noreferrer',
        class: 'text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 text-sm font-medium cursor-pointer'
      }, projectName || 'N/A')
    }
  }
])

// Projects table columns configuration
const projectsTableColumns = computed(() => [
  {
    ...TABLE_COLUMN_CONFIGS.PROJECTS.NAME,
    label: t('reports.table.project')
  },
  {
    ...TABLE_COLUMN_CONFIGS.PROJECTS.TEAM,
    label: t('reports.table.team')
  },
  {
    ...TABLE_COLUMN_CONFIGS.PROJECTS.FEATURES_COUNT,
    label: t('reports.table.featuresCount')
  },
  {
    ...TABLE_COLUMN_CONFIGS.PROJECTS.TEST_CASES_COUNT,
    label: t('reports.table.testCasesCount')
  },
  {
    ...TABLE_COLUMN_CONFIGS.PROJECTS.TEST_RUNS_COUNT,
    label: t('reports.table.testRunsCount')
  },
  {
    ...TABLE_COLUMN_CONFIGS.PROJECTS.BUGS_COUNT,
    label: t('reports.table.bugsCount')
  }
])
</script> 

<style scoped>
/* Hide any default popover/tooltip indicator icons */
:deep(.hr-popover-trigger::before),
:deep(.hr-popover-trigger::after),
:deep(.hr-popover .hr-popover-icon),
:deep(.n-popover-trigger::before),
:deep(.n-popover-trigger::after),
:deep(.n-popover__content::before),
:deep(.n-popover__content .n-icon),
:deep(.hr-popover-content::before),
:deep(.hr-popover-content .hr-icon) {
  display: none !important;
}

/* Ensure our custom icon shows */
:deep(.hr-popover-trigger),
:deep(.n-popover-trigger) {
  display: inline-flex;
  align-items: center;
}

/* Hide any icons in popover content */
:deep(.n-popover__content) {
  position: relative;
}

:deep(.n-popover__content > *:first-child) {
  display: block;
}

/* HLDataTable Styling for Reports */
:deep(.reports-data-table) {
  width: 100% !important;
  max-width: none !important;
}

:deep(.reports-data-table .hr-data-table-wrapper) {
  width: 100% !important;
  max-width: none !important;
  margin: 0 !important;
  padding: 0 !important;
  height: auto !important;
  overflow: visible !important;
}

:deep(.reports-data-table .hr-data-table) {
  border: 1px solid rgb(229 231 235) !important;
  border-collapse: collapse;
  table-layout: auto;
  width: 100% !important;
  max-width: none !important;
  min-width: 100%;
  height: auto !important;
  max-height: none !important;
  border-radius: 0.5rem;
  margin: 0 !important;
}

:deep(.dark .reports-data-table .hr-data-table) {
  border: 1px solid rgb(75 85 99) !important;
}

:deep(.reports-data-table .hr-data-table th) {
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.05em;
  color: rgb(75 85 99) !important;
  background-color: rgb(249 250 251) !important;
  padding: 0.875rem 0.75rem;
  white-space: nowrap;
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
  border-bottom: 1px solid rgb(229 231 235) !important;
}

:deep(.dark .reports-data-table .hr-data-table th) {
  color: rgb(209 213 219) !important;
  background-color: rgb(31 41 55) !important;
  border-bottom: 1px solid rgb(55 65 81) !important;
}

:deep(.reports-data-table .hr-data-table td) {
  padding: 0.875rem 0.75rem;
  vertical-align: middle;
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
  border-bottom: 1px solid rgb(243 244 246) !important;
}

:deep(.dark .reports-data-table .hr-data-table td) {
  border-bottom: 1px solid rgb(55 65 81) !important;
}

/* Add extra padding to first column for better spacing */
:deep(.reports-data-table .hr-data-table th:first-child) {
  padding-left: 1.5rem !important;
}

:deep(.reports-data-table .hr-data-table td:first-child) {
  padding-left: 1.5rem !important;
}

/* Add extra padding to last column for symmetry */
:deep(.reports-data-table .hr-data-table th:last-child) {
  padding-right: 1.5rem !important;
}

:deep(.reports-data-table .hr-data-table td:last-child) {
  padding-right: 1.5rem !important;
}

:deep(.reports-data-table .hr-data-table tbody tr) {
  height: auto;
  line-height: 1.4;
}

/* Remove border from last row */
:deep(.reports-data-table .hr-data-table tbody tr:last-child td) {
  border-bottom: none !important;
}

/* Row hover effects */
:deep(.reports-data-table .hr-data-table tbody tr:hover) {
  background-color: rgb(249 250 251) !important;
}

:deep(.dark .reports-data-table .hr-data-table tbody tr:hover) {
  background-color: rgb(31 41 55) !important;
}

/* Reduce heading margin before tables */
:deep(.reports-data-table) + * > h5,
h5 + .border {
  margin-top: 0.5rem !important;
}

/* Remove underlines from all links in report tables */
:deep(.reports-data-table a) {
  text-decoration: none !important;
}

:deep(.reports-data-table a:hover) {
  text-decoration: none !important;
}

/* Increase row height for filtered result tables (Features, Test Cases, Test Runs, Bugs) */
:deep(#features-filtered-table tbody tr),
:deep(#testruns-filtered-table tbody tr),
:deep(#bugs-filtered-table tbody tr) {
  min-height: 60px;
}

/* Increase row height for Teams and Projects Breakdown tables */
:deep(#teams-breakdown-table tbody tr),
:deep(#projects-breakdown-table tbody tr) {
  min-height: 55px;
}

/* Test cases table row height */
:deep(#testcases-filtered-table tbody tr) {
  min-height: 60px;
}

/* Accessibility issues table - increased row height for 2 lines of content */
:deep(.accessibility-issues-table tbody tr) {
  min-height: 80px !important;
  height: auto !important;
}

:deep(.accessibility-issues-table tbody td) {
  padding: 12px 8px !important;
  vertical-align: top !important;
  line-height: 1.5 !important;
  white-space: normal !important;
  word-wrap: break-word !important;
  max-width: 400px;
}

/* Center align history icon cell */
:deep(.accessibility-issues-table td:has(.history-icon-cell)) {
  vertical-align: middle !important;
  padding: 0 !important;
}

:deep(.accessibility-issues-table .history-icon-cell) {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  height: 100% !important;
  min-height: 80px !important;
}

/* Make sure cells can accommodate 2 lines */
:deep(.accessibility-issues-table tbody td > div) {
  max-height: none !important;
  overflow: visible !important;
  display: block !important;
  line-height: 1.5 !important;
}

/* Support for line-clamp-2 utility */
:deep(.line-clamp-2) {
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  word-wrap: break-word;
  line-height: 1.5;
}

/* Tooltip max width */
:deep(.hr-tooltip-content),
:deep(.n-tooltip-content) {
  max-width: 500px !important;
  word-wrap: break-word;
  white-space: normal;
}

/* Ensure badges and tags have proper filled colors */
:deep(.accessibility-issues-table .hr-badge),
:deep(.accessibility-issues-table .n-badge) {
  border-radius: 9999px !important;
  font-weight: 600 !important;
  padding: 6px 14px !important;
  border: none !important;
  opacity: 1 !important;
}

:deep(.accessibility-issues-table .hr-tag),
:deep(.accessibility-issues-table .n-tag) {
  border-radius: 9999px !important;
  font-weight: 600 !important;
  padding: 6px 14px !important;
  border: none !important;
  opacity: 1 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  text-align: center !important;
}

/* Ensure tag content is centered and visible */
:deep(.accessibility-issues-table .hr-tag .n-tag__content),
:deep(.accessibility-issues-table .n-tag .n-tag__content) {
  font-weight: 600 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  text-align: center !important;
}

/* Impact Tag Filled Colors */
:deep(.accessibility-issues-table .impact-tag.n-tag--red-type),
:deep(.accessibility-issues-table .impact-tag.n-tag--red) {
  background-color: #ef4444 !important;
  color: white !important;
}

:deep(.accessibility-issues-table .impact-tag.n-tag--orange-type),
:deep(.accessibility-issues-table .impact-tag.n-tag--orange) {
  background-color: #f97316 !important;
  color: white !important;
}

:deep(.accessibility-issues-table .impact-tag.n-tag--yellow-type),
:deep(.accessibility-issues-table .impact-tag.n-tag--yellow) {
  background-color: #eab308 !important;
  color: white !important;
}

:deep(.accessibility-issues-table .impact-tag.n-tag--blue-type),
:deep(.accessibility-issues-table .impact-tag.n-tag--blue) {
  background-color: #3b82f6 !important;
  color: white !important;
}

/* Status Tag Filled Colors */
:deep(.accessibility-issues-table .status-tag.n-tag--red-type),
:deep(.accessibility-issues-table .status-tag.n-tag--red) {
  background-color: #ef4444 !important;
  color: white !important;
}

:deep(.accessibility-issues-table .status-tag.n-tag--green-type),
:deep(.accessibility-issues-table .status-tag.n-tag--green) {
  background-color: #22c55e !important;
  color: white !important;
}

:deep(.accessibility-issues-table .status-tag.n-tag--gray-type),
:deep(.accessibility-issues-table .status-tag.n-tag--gray) {
  background-color: #6b7280 !important;
  color: white !important;
}

/* Issue ID Tag Filled Colors */
:deep(.accessibility-issues-table .issue-id-tag.n-tag--purple-type),
:deep(.accessibility-issues-table .issue-id-tag.n-tag--purple) {
  background-color: #a855f7 !important;
  color: white !important;
}

:deep(.accessibility-issues-table .issue-id-tag.n-tag--blue-type),
:deep(.accessibility-issues-table .issue-id-tag.n-tag--blue) {
  background-color: #3b82f6 !important;
  color: white !important;
}

:deep(.accessibility-issues-table .issue-id-tag.n-tag--cyan-type),
:deep(.accessibility-issues-table .issue-id-tag.n-tag--cyan) {
  background-color: #06b6d4 !important;
  color: white !important;
}

:deep(.accessibility-issues-table .issue-id-tag.n-tag--teal-type),
:deep(.accessibility-issues-table .issue-id-tag.n-tag--teal) {
  background-color: #14b8a6 !important;
  color: white !important;
}

:deep(.accessibility-issues-table .issue-id-tag.n-tag--green-type),
:deep(.accessibility-issues-table .issue-id-tag.n-tag--green) {
  background-color: #22c55e !important;
  color: white !important;
}

:deep(.accessibility-issues-table .issue-id-tag.n-tag--yellow-type),
:deep(.accessibility-issues-table .issue-id-tag.n-tag--yellow) {
  background-color: #eab308 !important;
  color: white !important;
}

:deep(.accessibility-issues-table .issue-id-tag.n-tag--orange-type),
:deep(.accessibility-issues-table .issue-id-tag.n-tag--orange) {
  background-color: #f97316 !important;
  color: white !important;
}

:deep(.accessibility-issues-table .issue-id-tag.n-tag--red-type),
:deep(.accessibility-issues-table .issue-id-tag.n-tag--red) {
  background-color: #ef4444 !important;
  color: white !important;
}

:deep(.accessibility-issues-table .issue-id-tag.n-tag--pink-type),
:deep(.accessibility-issues-table .issue-id-tag.n-tag--pink) {
  background-color: #ec4899 !important;
  color: white !important;
}

:deep(.accessibility-issues-table .issue-id-tag.n-tag--indigo-type),
:deep(.accessibility-issues-table .issue-id-tag.n-tag--indigo) {
  background-color: #6366f1 !important;
  color: white !important;
}

</style>
